'use client'

import { ChangeEvent, FormEvent, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useSession } from 'next-auth/react'
import { useQueryClient } from '@tanstack/react-query'
import { LazyMarkdownMessage as MarkdownMessage } from '@/components/ai-chat/LazyMarkdownMessage'
import { AppIcon } from '@/components/ui/icons'
import { apiFetch } from '@/lib/api-fetch'
import { getPageLocale } from '@/lib/api-fetch'
import {
  AI_CHAT_COMPRESS_STREAM_CONTENT_TYPE,
  isAiChatCompressionFailureContent,
  sanitizeAiChatCompressedSummary,
} from '@/lib/ai-chat/compression'
import { clearAuthAndOpenSignIn } from '@/lib/auth/client-logout'
import { toDisplayImageUrl } from '@/lib/media/image-url'
import { queryKeys } from '@/lib/query/keys'
import { useImageTextSettings } from '@/lib/query/hooks/useImageText'
import { LITE_PPT_MAX_PAGE_COUNT, LITE_PPT_MIN_PAGE_COUNT } from '@/lib/lite-ppt/constants'
import { useLitePptSettings } from '@/lib/query/hooks/useLitePpt'
import { useQuickVideoSettings } from '@/lib/query/hooks/useQuickVideoSettings'
import { useUserModels, type UserModelsPayload } from '@/lib/query/hooks/useUserModels'
import type { LitePptTextBoxDTO } from '@/types/lite-ppt'

const ImagePreviewModal = dynamic(() => import('@/components/ui/ImagePreviewModal'), { ssr: false })

type ChatRole = 'user' | 'assistant' | 'system'
type SkillMode = 'general' | 'quick-video' | 'image-text' | 'lite-ppt'
type CompressionLimitMode = 'start' | 'recent' | 'unlimited'

type CompressionOptions = {
  limitMode: CompressionLimitMode
  maxChars?: number
}

type AssistantMessagePlacement =
  | { type: 'append' }
  | { type: 'replace'; messageId: string }

interface ChatMessage {
  id: string
  role: ChatRole
  content: string
  createdAt?: string
  workflow?: AiChatWorkflow
  weatherCard?: AiChatWeatherCard
  sources?: AiChatTextSource[]
  translation?: AiChatMessageTranslation
  versions?: AiChatMessageVersion[]
  activeVersionIndex?: number
  steps?: AiChatProcessStep[]
}

interface AiChatTextSource {
  title: string
  url: string
  snippet?: string
}

interface AiChatMessageTranslation {
  language: string
  content: string
}

interface AiChatMessageVersion {
  id: string
  content: string
  createdAt?: string
  sources?: AiChatTextSource[]
  translation?: AiChatMessageTranslation
  weatherCard?: AiChatWeatherCard
  steps?: AiChatProcessStep[]
}

type AiChatProcessStepKind = 'tool' | 'reasoning' | 'status'
type AiChatProcessStepStatus = 'running' | 'completed' | 'failed'

interface AiChatProcessStep {
  id: string
  kind: AiChatProcessStepKind
  title: string
  status: AiChatProcessStepStatus
  detail?: string
  durationMs?: number
}

interface AiChatWeatherCard {
  city: string
  location: string
  condition: string
  temperature: string
  feelsLike: string
  humidity: string
  wind: string
  precipitation: string
  updatedAt: string
  source: string
  forecast: Array<{
    day: string
    condition: string
    range: string
    rainProbability: string
  }>
}

type AiChatWorkflow =
  | {
    type: 'image-text'
    projectId: string
    href: string
    status?: 'running' | 'completed' | 'failed'
    images?: string[]
    pageImages?: string[]
    targetPageCount?: number
    pageStatuses?: Array<'pending' | 'running' | 'completed' | 'failed'>
    failureReason?: string
  }
  | {
    type: 'quick-video'
    projectId: string
    episodeId: string
    resultId?: string
    taskId?: string
    href: string
    status?: 'running' | 'completed' | 'failed'
    videoUrl?: string
    posterUrl?: string
    posterLookupCompleted?: boolean
    ratio?: string
    progress?: number
    message?: string
  }
  | {
    type: 'lite-ppt'
    projectId: string
    href: string
    taskId?: string
    status?: 'running' | 'completed' | 'failed'
    targetPageCount?: number
    progress?: number
    message?: string
    pageImages?: string[]
    pageTextBoxes?: LitePptTextBoxDTO[][]
    previewLookupCompleted?: boolean
  }

const INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: 'welcome',
    role: 'assistant',
    content: '我会像智能助手一样工作。你可以直接对话，也可以先选择 AI短视频 或 AI图文 技能卡片，再把主题、素材和约束交给我。',
    createdAt: new Date().toISOString(),
  },
]

const SKILL_CARDS: Array<{
  id: SkillMode
  title: string
  icon: 'messageCircle' | 'video' | 'imageEdit' | 'monitor'
}> = [
  {
    id: 'general',
    title: 'AI对话',
    icon: 'messageCircle',
  },
  {
    id: 'quick-video',
    title: 'AI短视频',
    icon: 'video',
  },
  {
    id: 'image-text',
    title: 'AI图文',
    icon: 'imageEdit',
  },
  {
    id: 'lite-ppt',
    title: 'AI PPT',
    icon: 'monitor',
  },
]

const QUICK_VIDEO_RATIOS = ['9:16', '16:9', '1:1'] as const
const IMAGE_TEXT_RATIOS = ['9:16', '1:1', '16:9'] as const
const IMAGE_TEXT_PAGE_COUNTS = [1, 2, 3, 6, 9, 12] as const
const LITE_PPT_PAGE_COUNTS = Array.from(
  { length: LITE_PPT_MAX_PAGE_COUNT - LITE_PPT_MIN_PAGE_COUNT + 1 },
  (_item, index) => LITE_PPT_MIN_PAGE_COUNT + index,
)
const IMAGE_TEXT_MODES = [
  { value: 'basic', label: '小红书模式' },
  { value: 'none', label: '直接生图模式' },
] as const
const QUICK_VIDEO_USER_FIRST_FRAME_MAX_BYTES = 3 * 1024 * 1024
const QUICK_VIDEO_USER_FIRST_FRAME_MIN_EDGE = 512
const QUICK_VIDEO_USER_FIRST_FRAME_MAX_EDGE = 2048
const QUICK_VIDEO_USER_FIRST_FRAME_RATIO_TOLERANCE = 0.04
const QUICK_VIDEO_USER_FIRST_FRAME_MISSING_MESSAGE = '用户首帧文件不存在或已被清理，请重新上传首帧后再生成'
const QUICK_VIDEO_DURATION_OPTION_COUNT = 7
const IMAGE_TEXT_REFERENCE_LIMIT = 5
const DEFAULT_MANUAL_COMPRESS_CHAR_COUNT = 6_000
const MAX_MANUAL_COMPRESS_CHAR_COUNT = 60_000
const AI_CHAT_AUTO_COMPRESSION_NOTICE_PREFIX = '之前的对话已自动压缩'
const AI_CHAT_MANUAL_COMPRESSION_NOTICE_PREFIX = '之前的对话已手动压缩'
const CHAT_STORAGE_KEY_PREFIX = 'aimanju:ai-chat:draft:v2'
const CHAT_SESSIONS_STORAGE_KEY_PREFIX = 'aimanju:ai-chat:sessions:v2'
const CHAT_BOOTSTRAP_STORAGE_KEY_PREFIX = 'aimanju:ai-chat:bootstrap:v1'
const CHAT_INPUT_LINE_HEIGHT = 20
const CHAT_INPUT_VERTICAL_PADDING = 4
const CHAT_INPUT_MIN_HEIGHT = CHAT_INPUT_LINE_HEIGHT + CHAT_INPUT_VERTICAL_PADDING
const CHAT_INPUT_MAX_HEIGHT = CHAT_INPUT_LINE_HEIGHT * 5 + CHAT_INPUT_VERTICAL_PADDING
const LITE_PPT_SLIDE_WIDTH = 1440
const LITE_PPT_SLIDE_HEIGHT = 810
const AI_CHAT_LOCAL_PERSIST_IDLE_TIMEOUT_MS = 700
const AI_CHAT_SERVER_SAVE_DELAY_MS = 2000
const AI_CHAT_STREAM_RENDER_THROTTLE_MS = 80
const AI_CHAT_AUTO_SCROLL_BOTTOM_THRESHOLD_PX = 96
const AI_CHAT_AUTO_SCROLL_UNLOCK_THRESHOLD_PX = 8
const AI_CHAT_RECENT_MESSAGE_RENDER_LIMIT = 20
const AI_CHAT_HISTORY_COLLAPSE_BUFFER = 4
const AI_CHAT_SETTINGS_MEMORY_CACHE_TTL_MS = 10 * 60_000
const AI_CHAT_SESSIONS_MEMORY_CACHE_TTL_MS = 10 * 60_000
const AI_CHAT_MODELS_MEMORY_CACHE_TTL_MS = 10 * 60_000
const AI_CHAT_BROWSER_BOOTSTRAP_CACHE_TTL_MS = 30 * 60_000
const AI_CHAT_HEAVY_PREVIEW_IDLE_DELAY_MS = 360
const AI_CHAT_HEAVY_PREVIEW_IDLE_TIMEOUT_MS = 900
const AI_CHAT_COPY_FEEDBACK_DURATION_MS = 1_600
const DEFAULT_AI_CHAT_AVATAR = '/xinbao-ai-logo'
const DEFAULT_USER_AVATAR = '😀'
const AI_CHAT_ASSISTANT_TITLE = '讯宝'
const AI_CHAT_TRANSLATION_LANGUAGES = ['简体中文', 'English', '繁體中文', '日本語', '한국어', 'Français', 'Deutsch'] as const
const AI_CHAT_TEXT_FILE_ACCEPT = '.txt,.md,.markdown,.mdx,.json,.csv,.tsv,.log,.yaml,.yml,.html,.htm,.xml,.js,.jsx,.ts,.tsx,.py,.java,.kt,.dart,.docx,.pdf'
const AI_CHAT_TEXT_FILE_MAX_BYTES = 5 * 1024 * 1024
const AI_CHAT_TEXT_FILE_MAX_CHARS = 12_000
const AI_CHAT_PROCESS_STEP_DETAIL_MAX_CHARS = 280
const API_SESSION_EXPIRED_REASON = 'SESSION_EXPIRED'
const API_SESSION_EXPIRED_MESSAGE = 'Session expired, please sign in again'
const AI_CHAT_SESSION_EXPIRED_MESSAGE = '登录状态已失效，请重新登录。'
const AI_CHAT_NETWORK_FAILURE_HINT = '本机服务连接中断，可能是开发服务正在重启、接口编译中或请求被浏览器取消。请等页面稳定后重试。'
const DELETED_RESOURCE_IMAGE_SRC = '/resource-deleted.webp'
const IMAGE_TEXT_GENERIC_FAILURE_MESSAGE = '图片生成失败，请进入项目查看原因。'

function normalizeImageTextFailureReasonForChat(value: string) {
  const reason = value.trim()
  if (!reason) return ''

  const normalizedReason = reason.toLowerCase()
  if (
    normalizedReason.includes('model_not_found')
    || normalizedReason.includes('model not found')
    || normalizedReason.includes('model_not_registered')
    || normalizedReason.includes('channel not found')
    || normalizedReason.includes('distributor')
    || reason.includes('无可用渠道')
  ) {
    return '当前模型无可用渠道'
  }

  return reason
}

function getChatStorageKey(userId: string) {
  return `${CHAT_STORAGE_KEY_PREFIX}:${userId}`
}

function getChatSessionsStorageKey(userId: string) {
  return `${CHAT_SESSIONS_STORAGE_KEY_PREFIX}:${userId}`
}

function getChatBootstrapStorageKey(userId: string) {
  return `${CHAT_BOOTSTRAP_STORAGE_KEY_PREFIX}:${userId}`
}

function isAiChatSkillWorkflowMessage(message: ChatMessage) {
  return !!message.workflow || message.content.includes('\n\n[技能卡片：')
}

type PersistedChatState = {
  id?: string
  title?: string
  customTitle?: boolean
  updatedAt?: string
  skill?: SkillMode
  messages?: ChatMessage[]
  forkedFrom?: AiChatForkSource
  compressedSummary?: string
  quickVideoRatio?: (typeof QUICK_VIDEO_RATIOS)[number]
  quickVideoDuration?: number
  quickVideoWatermarkEnabled?: boolean
  quickVideoFirstFrameUrl?: string
  quickVideoFirstFrameStorageKey?: string
  quickVideoFirstFrameName?: string
  imageTextRatio?: (typeof IMAGE_TEXT_RATIOS)[number]
  imageTextPageCount?: number
  imageTextMode?: (typeof IMAGE_TEXT_MODES)[number]['value']
  imageTextWatermarkEnabled?: boolean
  imageTextReferenceUrls?: string[]
  imageTextReferenceNames?: string[]
  litePptPageCount?: number
  litePptVisualPrompt?: string
}

type ChatSession = Required<Pick<PersistedChatState, 'id' | 'title' | 'updatedAt'>> & PersistedChatState

type AiChatSettingsSnapshot = {
  model: string
  compressionModel: string
  assistantAvatarUrl: string
  regenerateDeleteTrailingMessages: boolean
  showRegenerateConfirmDialog: boolean
}

type AiChatSettingsMemoryCacheEntry = {
  loadedAt: number
  settings: AiChatSettingsSnapshot
}

type AiChatSessionsMemoryCacheEntry = {
  loadedAt: number
  sessions: ChatSession[]
  activeSessionId: string
  serializedPayload: string
}

type AiChatModelsMemoryCacheEntry = {
  loadedAt: number
  models: UserModelsPayload
}

type AiChatLocalSessionsCacheEntry = {
  sessions: ChatSession[]
  activeSessionId: string
  serializedPayload: string
  activeSessionPayload: string
}

type AiChatBrowserBootstrapCacheEntry = {
  settings: AiChatSettingsSnapshot
  models: UserModelsPayload
  sessions: ChatSession[]
  activeSessionId: string
  serializedPayload: string
}

type AiChatBootstrapMode = 'pending' | 'ready' | 'fallback'

const aiChatSettingsMemoryCache = new Map<string, AiChatSettingsMemoryCacheEntry>()
const aiChatSessionsMemoryCache = new Map<string, AiChatSessionsMemoryCacheEntry>()
const aiChatModelsMemoryCache = new Map<string, AiChatModelsMemoryCacheEntry>()

type AiChatForkSource = {
  sessionId?: string
  title?: string
  messageId?: string
  createdAt?: string
}

type ChatAvatarMeta = {
  avatar?: string | null
  backgroundColor?: string | null
  title?: string | null
}

function isImageAvatarValue(value: string) {
  return value.startsWith('/')
    || value.startsWith('blob:')
    || value.startsWith('data:image/')
    || /^https?:\/\//i.test(value)
}

function getAvatarFallback(meta: ChatAvatarMeta) {
  const source = (meta.title || meta.avatar || 'AI').trim()
  return Array.from(source)[0] || 'AI'
}

type PreferredFixedImageProps = {
  basePath: string
  alt: string
  className?: string
  imageClassName?: string
  loading?: 'eager' | 'lazy'
  decoding?: 'async' | 'auto' | 'sync'
  ariaHidden?: boolean
}

function PreferredFixedImage({
  basePath,
  alt,
  className,
  imageClassName = 'h-full w-full object-cover object-center',
  loading = 'lazy',
  decoding = 'async',
  ariaHidden = false,
}: PreferredFixedImageProps) {
  return (
    <picture className={className}>
      <source srcSet={`${basePath}.avif`} type="image/avif" />
      <source srcSet={`${basePath}.webp`} type="image/webp" />
      <img
        src={`${basePath}.jpeg`}
        alt={alt}
        loading={loading}
        decoding={decoding}
        aria-hidden={ariaHidden || undefined}
        className={imageClassName}
      />
    </picture>
  )
}

function DeletedWorkflowMediaPlaceholder({
  className = '',
  framed = true,
}: {
  className?: string
  framed?: boolean
}) {
  return (
    <div
      className={`flex aspect-square w-36 max-w-full items-center justify-center overflow-hidden rounded-2xl ${
        framed
          ? 'border border-white/80 bg-white/70 p-1 shadow-[0_10px_22px_rgba(32,46,88,0.10)]'
          : 'bg-transparent'
      } ${className}`}
    >
      <img
        src={DELETED_RESOURCE_IMAGE_SRC}
        alt="资源已删除"
        loading="lazy"
        decoding="async"
        className={`h-full w-full object-cover ${framed ? 'rounded-xl' : 'rounded-2xl'}`}
      />
    </div>
  )
}

type WorkflowImageWithDeletedFallbackProps = {
  src: string
  alt: string
  className: string
  placeholderClassName?: string
  placeholderFramed?: boolean
  onLoad?: () => void
  onMissingChange?: (missing: boolean) => void
}

function WorkflowImageWithDeletedFallback({
  src,
  alt,
  className,
  placeholderClassName,
  placeholderFramed,
  onLoad,
  onMissingChange,
}: WorkflowImageWithDeletedFallbackProps) {
  const [missing, setMissing] = useState(false)
  const displaySrc = toDisplayImageUrl(src) || src

  useEffect(() => {
    setMissing(false)
    onMissingChange?.(false)
  }, [src, onMissingChange])

  if (missing) {
    return <DeletedWorkflowMediaPlaceholder className={placeholderClassName} framed={placeholderFramed} />
  }

  return (
    <img
      src={displaySrc}
      alt={alt}
      onLoad={onLoad}
      onError={() => {
        setMissing(true)
        onMissingChange?.(true)
      }}
      loading="lazy"
      decoding="async"
      className={className}
    />
  )
}

type ImageTextPreviewButtonProps = {
  messageId: string
  src: string
  index: number
  onPreview: (src: string) => void
  onLoad: () => void
}

function ImageTextPreviewButton({
  messageId,
  src,
  index,
  onPreview,
  onLoad,
}: ImageTextPreviewButtonProps) {
  const [missing, setMissing] = useState(false)
  const previewLabel = missing ? `AI图文生成图 ${index + 1} 已删除` : `预览 AI图文生成图 ${index + 1}`

  return (
    <button
      key={`${messageId}-${src}-${index}`}
      type="button"
      onClick={() => {
        if (!missing) onPreview(src)
      }}
      aria-label={previewLabel}
      title={previewLabel}
	      className={`group relative block max-w-full overflow-hidden rounded-2xl border border-white/70 bg-white/65 p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
	        missing
	          ? 'w-36 cursor-default border-white/80 bg-transparent shadow-none'
	          : 'cursor-zoom-in'
	      }`}
    >
      <WorkflowImageWithDeletedFallback
        src={src}
        alt={`AI图文生成图 ${index + 1}`}
        onLoad={onLoad}
        onMissingChange={setMissing}
	        className="max-h-[420px] w-full object-contain"
        placeholderClassName="rounded-2xl"
      />
      {!missing ? (
	        <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full border border-white/40 bg-slate-950/62 px-2.5 py-1 text-[11px] font-black text-white opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100">
          <AppIcon name="eye" className="h-3.5 w-3.5" />
          预览
        </span>
      ) : null}
    </button>
  )
}

type LitePptPreviewCardProps = {
  messageId: string
  src: string
  index: number
  boxes: LitePptTextBoxDTO[]
  onLoad: () => void
}

function LitePptPreviewCard({
  messageId,
  src,
  index,
  boxes,
  onLoad,
}: LitePptPreviewCardProps) {
  const [missing, setMissing] = useState(false)

  return (
    <div
      key={`${messageId}-lite-ppt-preview-${src}-${index}`}
      className={`relative max-w-full overflow-hidden ${
        missing
          ? 'w-36 rounded-2xl bg-transparent shadow-none'
          : 'workspace-performance-container-outline aspect-video rounded-xl bg-slate-950 shadow-[0_10px_22px_rgba(32,46,88,0.10)]'
      }`}
    >
      <WorkflowImageWithDeletedFallback
        src={src}
        alt={`AI PPT 预览 ${index + 1}`}
        onLoad={onLoad}
        onMissingChange={setMissing}
        className="absolute inset-0 h-full w-full object-cover"
        placeholderClassName="rounded-2xl"
        placeholderFramed={false}
      />
      {!missing ? boxes.slice(0, 12).map((box) => (
        <span
          key={box.id}
          className="absolute block overflow-hidden whitespace-pre-wrap break-words leading-tight"
          style={{
            left: `${(box.x / LITE_PPT_SLIDE_WIDTH) * 100}%`,
            top: `${(box.y / LITE_PPT_SLIDE_HEIGHT) * 100}%`,
            width: `${(box.width / LITE_PPT_SLIDE_WIDTH) * 100}%`,
            height: `${(box.height / LITE_PPT_SLIDE_HEIGHT) * 100}%`,
            color: box.color,
            fontSize: Math.max(3, box.fontSize / 10),
            fontWeight: box.bold ? 900 : 700,
            textAlign: box.align === 'center' || box.align === 'right' ? box.align : 'left',
            lineHeight: box.lineHeight || 1.15,
            textShadow: box.textShadow,
          }}
        >
          {box.text}
        </span>
      )) : null}
    </div>
  )
}

type QuickVideoPreviewCardProps = {
  videoUrl: string
  posterUrl: string | undefined
  ratio: string
  onLoadedMetadata: () => void
}

function QuickVideoPreviewCard({
  videoUrl,
  posterUrl,
  ratio,
  onLoadedMetadata,
}: QuickVideoPreviewCardProps) {
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    setMissing(false)
  }, [videoUrl])

  if (missing) {
    return (
      <div className="mt-3">
        <DeletedWorkflowMediaPlaceholder />
      </div>
    )
  }

  return (
    <div
      className={`mt-3 w-full overflow-hidden rounded-2xl border border-white/70 bg-black shadow-[0_12px_28px_rgba(32,46,88,0.10)] ${getQuickVideoPreviewMaxWidthClass(ratio)}`}
      style={{ aspectRatio: getQuickVideoPreviewAspectRatio(ratio) }}
    >
      <video
        src={videoUrl}
        poster={posterUrl}
        controls
        playsInline
        preload="metadata"
        onError={() => setMissing(true)}
        onLoadedMetadata={onLoadedMetadata}
        className="h-full w-full object-contain"
      />
    </div>
  )
}

function getPreferredFixedImageBasePath(avatar: string) {
  return avatar === '/xinbao-ai-logo'
    || avatar === '/xinbao-ai-logo.png'
    || avatar === '/xinbao-ai-logo.jpeg'
    || avatar === '/xinbao-ai-logo.jpg'
    || avatar === '/xinbao-ai-logo.webp'
    || avatar === '/xinbao-ai-logo.avif'
    ? '/xinbao-ai-logo'
    : avatar === '/xinbao-chat-mascot'
    || avatar === '/xinbao-chat-mascot.png'
    || avatar === '/xinbao-chat-mascot.jpeg'
    || avatar === '/xinbao-chat-mascot.jpg'
    || avatar === '/xinbao-chat-mascot.webp'
    || avatar === '/xinbao-chat-mascot.avif'
    ? '/xinbao-chat-mascot'
    : ''
}

function AiChatAvatar({ meta, loading = false }: { meta: ChatAvatarMeta; loading?: boolean }) {
  const avatar = (meta.avatar || '').trim()
  const title = (meta.title || 'avatar').trim()
  const backgroundColor = meta.backgroundColor || 'linear-gradient(135deg,#ecf4ff 0%,#f7edff 100%)'
  const preferredFixedImageBasePath = getPreferredFixedImageBasePath(avatar)
  const displayAvatar = avatar && !preferredFixedImageBasePath && isImageAvatarValue(avatar)
    ? toDisplayImageUrl(avatar) || avatar
    : avatar

  return (
    <span
      data-ai-chat-avatar="true"
      aria-label={title}
      title={title}
      className={`grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-xl border border-white/70 text-base font-black text-blue-700 shadow-[0_10px_24px_rgba(32,46,88,0.14)] ${loading ? 'animate-pulse ring-2 ring-blue-200' : ''}`}
      style={{ background: backgroundColor }}
    >
      {preferredFixedImageBasePath ? (
        <PreferredFixedImage
          basePath={preferredFixedImageBasePath}
          alt=""
          ariaHidden
          className="block h-full w-full"
          imageClassName="h-full w-full object-cover"
        />
      ) : displayAvatar && isImageAvatarValue(displayAvatar) ? (
        <span
          aria-hidden="true"
          className="h-full w-full bg-cover bg-center"
          style={{ backgroundImage: `url(${JSON.stringify(displayAvatar)})` }}
        />
      ) : (
        <span className="leading-none">{avatar || getAvatarFallback(meta)}</span>
      )}
    </span>
  )
}

function AiChatProcessSteps({ steps }: { steps: AiChatProcessStep[] }) {
  if (!steps.length) return null
  const visibleSteps = steps.slice(-4)
  return (
    <div className="mb-3 w-full max-w-[min(32rem,calc(100vw-4rem))] rounded-2xl border border-emerald-100 bg-emerald-50/85 p-2.5 text-emerald-950 shadow-[0_10px_24px_rgba(16,185,129,0.08)]">
      {steps.length > visibleSteps.length ? (
        <div className="mb-1.5 flex items-center gap-2 px-1 text-[11px] font-black text-emerald-800">
          <AppIcon name="chevronDown" className="h-3.5 w-3.5" />
          <span>展开更多 {steps.length} 步</span>
        </div>
      ) : null}
      <div className="space-y-1">
        {visibleSteps.map((step) => {
          const duration = formatAiChatProcessStepDuration(step.durationMs)
          const detail = getAiChatProcessStepDetail(step)
          return (
            <div key={step.id} className="flex min-w-0 items-center gap-2 rounded-xl px-1.5 py-1.5">
              <span className="grid h-5 w-5 shrink-0 place-items-center text-emerald-700">
                <AppIcon name={step.kind === 'reasoning' ? 'idea' : step.kind === 'tool' ? 'package' : 'info'} className="h-4 w-4" />
              </span>
              <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                <span className="shrink-0 truncate text-xs font-black">
                  {step.title}{duration ? ` (${duration})` : ''}
                </span>
                {detail ? (
                  <span className="min-w-0 truncate text-[11px] font-semibold leading-4 text-emerald-900/70">
                    <span aria-hidden="true">· </span>{detail}
                  </span>
                ) : null}
              </span>
              {step.status === 'running' ? (
                <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-emerald-500" />
              ) : (
                <AppIcon name={step.status === 'failed' ? 'alert' : 'checkTiny'} className={`h-3.5 w-3.5 shrink-0 ${step.status === 'failed' ? 'text-red-500' : 'text-emerald-700'}`} />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function createMessage(role: ChatRole, content: string, workflow?: AiChatWorkflow, weatherCard?: AiChatWeatherCard): ChatMessage {
  return {
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    role,
    content,
    createdAt: new Date().toISOString(),
    workflow,
    weatherCard,
  }
}

function createAiChatMessageVersion(message: ChatMessage): AiChatMessageVersion {
  return {
    id: `version-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    content: message.content,
    createdAt: message.createdAt,
	    ...(message.sources?.length ? { sources: message.sources } : {}),
	    ...(message.translation ? { translation: message.translation } : {}),
	    ...(message.weatherCard ? { weatherCard: message.weatherCard } : {}),
	    ...(message.steps?.length ? { steps: message.steps } : {}),
	  }
	}

function getAiChatMessageVersions(message: ChatMessage): AiChatMessageVersion[] {
  return message.versions?.length ? message.versions : [createAiChatMessageVersion(message)]
}

function normalizeAiChatVersionIndex(index: number | undefined, length: number) {
  if (length <= 0) return 0
  if (typeof index !== 'number' || !Number.isFinite(index)) return length - 1
  return Math.max(0, Math.min(length - 1, Math.floor(index)))
}

function applyAiChatMessageVersion(message: ChatMessage, versions: AiChatMessageVersion[], index: number): ChatMessage {
  const safeIndex = normalizeAiChatVersionIndex(index, versions.length)
  const activeVersion = versions[safeIndex]
  if (!activeVersion) return message
  const next: ChatMessage = {
    ...message,
    content: activeVersion.content,
    createdAt: activeVersion.createdAt || message.createdAt,
    versions,
    activeVersionIndex: safeIndex,
  }
  if (activeVersion.sources) next.sources = activeVersion.sources
  else delete next.sources
  if (activeVersion.translation) next.translation = activeVersion.translation
  else delete next.translation
	  if (activeVersion.weatherCard) next.weatherCard = activeVersion.weatherCard
	  else delete next.weatherCard
	  if (activeVersion.steps) next.steps = activeVersion.steps
	  else delete next.steps
	  return next
	}

function appendAiChatMessageVersion(message: ChatMessage, nextVersion: ChatMessage): ChatMessage {
  const versions = [
    ...getAiChatMessageVersions(message),
    createAiChatMessageVersion(nextVersion),
  ]
  return applyAiChatMessageVersion({
    ...message,
    createdAt: nextVersion.createdAt,
  }, versions, versions.length - 1)
}

function formatCompactNumber(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '0'
  if (value >= 10_000) return `${(value / 10_000).toFixed(value >= 100_000 ? 0 : 1)}万`
  return `${Math.round(value)}`
}

function normalizeAiChatProcessStepStatus(value: unknown): AiChatProcessStepStatus {
  return value === 'completed' || value === 'failed' || value === 'running' ? value : 'running'
}

function normalizeAiChatProcessStepKind(value: unknown): AiChatProcessStepKind {
  return value === 'tool' || value === 'reasoning' || value === 'status' ? value : 'status'
}

function normalizeAiChatProcessStep(value: unknown): AiChatProcessStep | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Partial<AiChatProcessStep>
  const title = typeof record.title === 'string' ? record.title.replace(/\s+/g, ' ').trim() : ''
  if (!title) return null
  const id = typeof record.id === 'string' && record.id.trim()
    ? record.id.trim().slice(0, 80)
    : `step-${Date.now()}-${Math.random().toString(36).slice(2)}`
  const detail = typeof record.detail === 'string'
    ? Array.from(record.detail.replace(/\s+/g, ' ').trim()).slice(0, AI_CHAT_PROCESS_STEP_DETAIL_MAX_CHARS).join('')
    : ''
  const durationMs = Number(record.durationMs)
  return {
    id,
    kind: normalizeAiChatProcessStepKind(record.kind),
    title: Array.from(title).slice(0, 120).join(''),
    status: normalizeAiChatProcessStepStatus(record.status),
    ...(detail ? { detail } : {}),
    ...(Number.isFinite(durationMs) && durationMs > 0 ? { durationMs } : {}),
  }
}

function normalizeAiChatProcessSteps(value: unknown): AiChatProcessStep[] {
  if (!Array.isArray(value)) return []
  return value
    .map(normalizeAiChatProcessStep)
    .filter(Boolean)
    .slice(0, 8) as AiChatProcessStep[]
}

function upsertAiChatProcessStep(steps: AiChatProcessStep[] | undefined, step: AiChatProcessStep) {
  const current = steps || []
  const index = current.findIndex((item) => item.id === step.id)
  if (index < 0) return [...current, step].slice(-8)
  return current.map((item, itemIndex) => itemIndex === index ? { ...item, ...step } : item)
}

function formatAiChatProcessStepDuration(durationMs: number | undefined) {
  if (!Number.isFinite(durationMs || 0) || !durationMs) return ''
  return `${(durationMs / 1000).toFixed(durationMs >= 10_000 ? 1 : 0)}s`
}

function getAiChatProcessStepDetail(step: AiChatProcessStep) {
  const detail = step.detail?.trim() || ''
  if (step.kind !== 'reasoning') return detail
  if (step.status === 'completed' && (!detail || detail === '正在推理中')) return '推理完成'
  if (step.status === 'failed' && (!detail || detail === '正在推理中')) return '推理失败'
  return detail
}

function createCompressionSummaryMessage(
  summary: string,
  mode: 'manual' = 'manual',
  stats?: { messageCount?: number; sentChars?: number; summaryChars?: number },
): ChatMessage {
  const prefix = AI_CHAT_MANUAL_COMPRESSION_NOTICE_PREFIX
  const foldedLabel = '对话明细已折叠'
  const statCountLabel = '条上下文'
  const statLine = stats && (stats.messageCount || stats.sentChars)
    ? `\n\n压缩统计：${stats.messageCount || 0} ${statCountLabel}，发送 ${formatCompactNumber(stats.sentChars || 0)} 字，摘要 ${formatCompactNumber(stats.summaryChars || summary.length)} 字。`
    : ''
  return {
    ...createMessage('assistant', `${prefix}，${foldedLabel}，后续回答会继承摘要。${statLine}\n\n摘要：${summary}`),
    id: `compression-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  }
}

function parseCompressionSummaryMessage(message: Pick<ChatMessage, 'content'>) {
  const content = message.content || ''
  const marker = '摘要：'
  const markerIndex = content.indexOf(marker)
  if (markerIndex < 0) {
    return {
      heading: content.trim(),
      summary: '',
    }
  }
  return {
    heading: content.slice(0, markerIndex).trim(),
    summary: content.slice(markerIndex + marker.length).trim(),
  }
}

function isCompressionSummaryMessage(message: Pick<ChatMessage, 'role' | 'content' | 'id'>) {
  return message.id.startsWith('compression-') || (
    message.role === 'assistant'
    && (
      message.content.trim().startsWith(AI_CHAT_AUTO_COMPRESSION_NOTICE_PREFIX)
      || message.content.trim().startsWith(AI_CHAT_MANUAL_COMPRESSION_NOTICE_PREFIX)
    )
  )
}

function readRetainedCompressionSummary(source: ChatMessage[]) {
  for (let index = source.length - 1; index >= 0; index -= 1) {
    const message = source[index]
    if (!isCompressionSummaryMessage(message)) continue
    const summary = normalizeCompressedSummary(parseCompressionSummaryMessage(message).summary)
    if (summary) return summary
  }
  return ''
}

function isCompressionFailureMessage(message: Pick<ChatMessage, 'role' | 'content'>) {
  return isAiChatCompressionFailureContent(message.role, message.content)
}

function getDefaultManualCompressionOptions(totalChars: number): CompressionOptions {
  if (totalChars > 0 && totalChars <= MAX_MANUAL_COMPRESS_CHAR_COUNT) {
    return { limitMode: 'unlimited' }
  }
  return { limitMode: 'recent', maxChars: DEFAULT_MANUAL_COMPRESS_CHAR_COUNT }
}

function readMessageCreatedAt(record: Pick<ChatMessage, 'id'> & { createdAt?: unknown }) {
  if (typeof record.createdAt === 'string' && record.createdAt) return record.createdAt
  const timestamp = Number(record.id.match(/^[a-z]+-(\d{13})-/)?.[1])
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : new Date().toISOString()
}

function parseAiChatStreamBlock(block: string) {
  let event = 'message'
  const dataLines: string[] = []

  for (const line of block.split(/\r?\n/)) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim()
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trimStart())
    }
  }

  if (!dataLines.length) return null

  try {
    return {
      event,
      data: JSON.parse(dataLines.join('\n')) as Record<string, unknown>,
    }
  } catch {
    return null
  }
}

function isSkillMode(value: unknown): value is SkillMode {
  return value === 'general' || value === 'quick-video' || value === 'image-text' || value === 'lite-ppt'
}

function isQuickVideoRatio(value: unknown): value is (typeof QUICK_VIDEO_RATIOS)[number] {
  return QUICK_VIDEO_RATIOS.includes(value as (typeof QUICK_VIDEO_RATIOS)[number])
}

function buildQuickVideoDurationOptions(segmentSeconds: unknown) {
  const step = typeof segmentSeconds === 'number' && Number.isFinite(segmentSeconds) && segmentSeconds > 0
    ? Math.floor(segmentSeconds)
    : 10
  return Array.from({ length: QUICK_VIDEO_DURATION_OPTION_COUNT }, (_item, index) => step * (index + 1))
}

function isImageTextRatio(value: unknown): value is (typeof IMAGE_TEXT_RATIOS)[number] {
  return IMAGE_TEXT_RATIOS.includes(value as (typeof IMAGE_TEXT_RATIOS)[number])
}

function isImageTextMode(value: unknown): value is (typeof IMAGE_TEXT_MODES)[number]['value'] {
  return IMAGE_TEXT_MODES.some((mode) => mode.value === value)
}

function isLitePptPageCount(value: unknown): value is (typeof LITE_PPT_PAGE_COUNTS)[number] {
  return LITE_PPT_PAGE_COUNTS.includes(value as (typeof LITE_PPT_PAGE_COUNTS)[number])
}

function isAiChatRawNetworkFailureMessage(value: string) {
  return /failed to fetch|networkerror|load failed/i.test(value)
}

function isAiChatAbortError(value: unknown) {
  return value instanceof DOMException && value.name === 'AbortError'
}

function normalizeLegacyAiChatMessageContent(value: string) {
  const match = value.trim().match(/^(手动压缩失败|自动压缩失败|请求失败)：\s*Failed to fetch\s*$/i)
  if (!match) return value
  return `${match[1]}：${AI_CHAT_NETWORK_FAILURE_HINT}`
}

function isAiChatWebSearchDisabledMessageContent(value: string) {
  return /(需联网搜索但未开启|未开启联网搜索|用户未开启联网搜索功能|尚未开启联网搜索|联网搜索未开启|web search function is not enabled|web search is not enabled|请开启[^。；;\n]*联网搜索|开启[^。；;\n]*联网搜索[^。；;\n]*开关)/iu.test(value)
}

function createAiChatWebSearchDisabledWithSourcesDisplayMessage(sourceCount: number, hasSourceList: boolean) {
  const sourceHint = hasSourceList ? '请以下方列表为准' : '但这条历史记录没有保存完整列表'
  return `联网搜索已经执行，并返回了 ${sourceCount} 条结果。上一段“未开启联网搜索”的正文是过期判断；${sourceHint}。若结果相关性不足，可以换一个更具体的问题继续检索。`
}

function stripAiChatInlineSourceList(content: string) {
  const lines = content.trimEnd().split(/\r?\n/u)
  const sourceStartIndex = lines.findIndex((line) => (
    /^\s{0,3}(?:#{1,6}\s*)?(?:来源|参考来源|资料来源|推荐阅读|延伸阅读|参考链接|相关链接|Sources?|References?)\s*[:：]\s*\S*/iu.test(line)
    || /^\s{0,3}(?:#{1,6}\s*)?(?:来源|参考来源|资料来源|推荐阅读|延伸阅读|参考链接|相关链接|Sources?|References?)\s*$/iu.test(line)
  ))
  if (sourceStartIndex > 0) return lines.slice(0, sourceStartIndex).join('\n').trimEnd()
  const inlineSectionMatch = content.match(/(^|\n|\s|[。；;.!?！？])(?:来源|参考来源|资料来源|推荐阅读|延伸阅读|参考链接|相关链接|Sources?|References?)\s*[:：]/iu)
  if (!inlineSectionMatch?.index) return content
  const boundary = inlineSectionMatch[1] || ''
  const cutIndex = inlineSectionMatch.index + (/^[。；;.!?！？]$/u.test(boundary) ? boundary.length : 0)
  return content.slice(0, cutIndex).trimEnd()
}

function readAiChatWebSearchEvidenceSourceCount(message: ChatMessage) {
  const sourceCount = message.sources?.length || 0
  const stepSourceCount = Math.max(0, ...(message.steps || []).map((step) => {
    const title = step.title || ''
    const detail = step.detail || ''
    if (step.kind !== 'tool' || step.status === 'failed') return 0
    if (!/联网检索|web[_\s-]?search|搜索/iu.test(`${title} ${detail}`)) return 0
    const match = detail.match(/(\d+)\s*条来源/u) || title.match(/(\d+)\s*条来源/u)
    return match ? Number(match[1]) || 0 : 0
  }))
  return Math.max(sourceCount, stepSourceCount)
}

function normalizeCompressedSummary(value: unknown) {
  return sanitizeAiChatCompressedSummary(value)
}

function normalizeCompressionSummaryMessageContent(value: string) {
  const marker = '摘要：'
  const markerIndex = value.indexOf(marker)
  if (markerIndex < 0) return value
  const prefix = value.slice(0, markerIndex + marker.length)
  const summary = normalizeCompressedSummary(value.slice(markerIndex + marker.length))
  return summary ? `${prefix}${summary}` : value.slice(0, markerIndex).trim()
}

function normalizeAiChatTextSources(value: unknown): AiChatTextSource[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const record = item as Partial<AiChatTextSource>
      const title = typeof record.title === 'string' ? record.title.replace(/\s+/g, ' ').trim() : ''
      const url = typeof record.url === 'string' ? record.url.trim() : ''
      const snippet = typeof record.snippet === 'string' ? record.snippet.replace(/\s+/g, ' ').trim() : ''
      if (!title || !/^https?:\/\//i.test(url)) return null
      return {
        title: Array.from(title).slice(0, 120).join(''),
        url,
        ...(snippet ? { snippet: Array.from(snippet).slice(0, 220).join('') } : {}),
      }
    })
    .filter(Boolean)
    .slice(0, 5) as AiChatTextSource[]
}

function normalizeAiChatMessageTranslation(value: unknown): AiChatMessageTranslation | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined
  const record = value as Partial<AiChatMessageTranslation>
  const language = typeof record.language === 'string' ? record.language.trim().slice(0, 40) : ''
  const content = typeof record.content === 'string' ? record.content.trim() : ''
  if (!language || !content) return undefined
  return { language, content }
}

function normalizeAiChatMessageVersions(value: unknown): AiChatMessageVersion[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const record = item as Partial<AiChatMessageVersion>
      if (typeof record.content !== 'string') return null
      const sources = normalizeAiChatTextSources(record.sources)
      const content = sources.length
        ? stripAiChatInlineSourceList(normalizeLegacyAiChatMessageContent(record.content))
        : normalizeLegacyAiChatMessageContent(record.content)
      const translation = normalizeAiChatMessageTranslation(record.translation)
      const steps = normalizeAiChatProcessSteps(record.steps)
      return {
        id: typeof record.id === 'string' && record.id ? record.id : `version-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        content,
        createdAt: readMessageCreatedAt({ id: typeof record.id === 'string' ? record.id : '', createdAt: record.createdAt }),
        ...(sources.length ? { sources } : {}),
        ...(translation ? { translation } : {}),
        ...(record.weatherCard ? { weatherCard: record.weatherCard } : {}),
        ...(steps.length ? { steps } : {}),
      }
    })
    .filter(Boolean) as AiChatMessageVersion[]
}

function aiChatSourceHost(value: string) {
  try {
    return new URL(value).hostname.replace(/^www\./u, '')
  } catch {
    return ''
  }
}

function normalizePersistedMessages(value: unknown): ChatMessage[] | null {
  if (!Array.isArray(value)) return null
  const normalized = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const record = item as Partial<ChatMessage>
      if (!record.id || typeof record.id !== 'string') return null
      if (record.role !== 'user' && record.role !== 'assistant' && record.role !== 'system') return null
      if (typeof record.content !== 'string') return null
      const sources = normalizeAiChatTextSources(record.sources)
      const versions = normalizeAiChatMessageVersions(record.versions)
      const steps = normalizeAiChatProcessSteps(record.steps)
      const content = sources.length
        ? stripAiChatInlineSourceList(normalizeLegacyAiChatMessageContent(record.content))
        : normalizeLegacyAiChatMessageContent(record.content)
      const message: ChatMessage = {
        id: record.id,
        role: record.role,
        content: isCompressionSummaryMessage({ id: record.id, role: record.role, content })
          ? normalizeCompressionSummaryMessageContent(content)
          : content,
        createdAt: readMessageCreatedAt({ id: record.id, createdAt: record.createdAt }),
        ...(record.workflow ? { workflow: record.workflow } : {}),
        ...(record.weatherCard ? { weatherCard: record.weatherCard } : {}),
        ...(sources.length ? { sources } : {}),
        ...(normalizeAiChatMessageTranslation(record.translation) ? { translation: normalizeAiChatMessageTranslation(record.translation) } : {}),
        ...(steps.length ? { steps } : {}),
      }
      if (versions.length > 1) {
        return applyAiChatMessageVersion(
          { ...message, versions },
          versions,
          normalizeAiChatVersionIndex(record.activeVersionIndex, versions.length),
        )
      }
      return message
    })
    .filter((message): message is Exclude<typeof message, null> => Boolean(message))
    .filter((message) => !isCompressionFailureMessage(message))
  const messages = removeWelcomeMessageAfterCompression(normalized)
  return messages.length > 0 ? messages : null
}

function removeWelcomeMessageAfterCompression(source: ChatMessage[]) {
  if (!source.some((message) => isCompressionSummaryMessage(message))) return source
  let removedWelcomeMessage = false
  const messages = source.filter((message) => {
    const keep = message.id !== 'welcome'
    if (!keep) removedWelcomeMessage = true
    return keep
  })
  if (!removedWelcomeMessage) return source
  return messages.length > 0 ? messages : source
}

function createSessionId() {
  return `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function createEmptySession(): ChatSession {
  return {
    id: createSessionId(),
    title: '新对话',
    updatedAt: new Date().toISOString(),
    skill: 'general',
    messages: INITIAL_MESSAGES,
    compressedSummary: '',
    quickVideoRatio: '9:16',
    quickVideoDuration: 10,
    quickVideoWatermarkEnabled: true,
    quickVideoFirstFrameUrl: '',
    quickVideoFirstFrameStorageKey: '',
    quickVideoFirstFrameName: '',
    imageTextRatio: '9:16',
    imageTextPageCount: 2,
    imageTextMode: 'basic',
    imageTextWatermarkEnabled: true,
    imageTextReferenceUrls: [],
    imageTextReferenceNames: [],
    litePptPageCount: 2,
    litePptVisualPrompt: '',
  }
}

function inferSessionTitle(messages: ChatMessage[]) {
  const firstUser = messages.find((message) => message.role === 'user')?.content
  const normalized = stripSkillContextFromMessageContent(firstUser || '').replace(/\s+/g, ' ').trim()
  if (!normalized) return '新对话'
  return Array.from(normalized).slice(0, 18).join('')
}

function stripSkillContextFromMessageContent(value: string) {
  return value.split('\n\n[技能卡片：')[0]?.trim() || ''
}

function normalizeAiChatForkSource(value: unknown): AiChatForkSource | undefined {
  if (!value || typeof value !== 'object') return undefined
  const record = value as Record<string, unknown>
  const messageId = readNonEmptyString(record.messageId)
  if (!messageId) return undefined
  return {
    messageId,
    ...(readNonEmptyString(record.sessionId) ? { sessionId: readNonEmptyString(record.sessionId) } : {}),
    ...(readNonEmptyString(record.title) ? { title: readNonEmptyString(record.title) } : {}),
    ...(readNonEmptyString(record.createdAt) ? { createdAt: readNonEmptyString(record.createdAt) } : {}),
  }
}

function cleanAiChatForkSourceForSession(sessionId: string, forkedFrom?: AiChatForkSource) {
  if (!forkedFrom?.messageId) return undefined
  if (forkedFrom.sessionId === sessionId) return undefined
  return forkedFrom
}

function normalizePersistedSession(value: unknown): ChatSession | null {
  if (!value || typeof value !== 'object') return null
  const record = value as PersistedChatState
  const messages = normalizePersistedMessages(record.messages) || INITIAL_MESSAGES
  const id = typeof record.id === 'string' && record.id ? record.id : createSessionId()
  const forkedFrom = cleanAiChatForkSourceForSession(id, normalizeAiChatForkSource(record.forkedFrom))
  return {
    id,
    title: typeof record.title === 'string' && record.title.trim() ? record.title.trim() : inferSessionTitle(messages),
    customTitle: record.customTitle === true,
    updatedAt: typeof record.updatedAt === 'string' && record.updatedAt ? record.updatedAt : new Date().toISOString(),
    skill: isSkillMode(record.skill) ? record.skill : 'general',
    messages,
    forkedFrom,
    compressedSummary: normalizeCompressedSummary(record.compressedSummary),
    quickVideoRatio: isQuickVideoRatio(record.quickVideoRatio) ? record.quickVideoRatio : '9:16',
    quickVideoDuration: typeof record.quickVideoDuration === 'number' ? record.quickVideoDuration : 10,
    quickVideoWatermarkEnabled: record.quickVideoWatermarkEnabled !== false,
    quickVideoFirstFrameUrl: typeof record.quickVideoFirstFrameUrl === 'string' ? record.quickVideoFirstFrameUrl : '',
    quickVideoFirstFrameStorageKey: typeof record.quickVideoFirstFrameStorageKey === 'string' ? record.quickVideoFirstFrameStorageKey : '',
    quickVideoFirstFrameName: typeof record.quickVideoFirstFrameName === 'string' ? record.quickVideoFirstFrameName : '',
    imageTextRatio: isImageTextRatio(record.imageTextRatio) ? record.imageTextRatio : '9:16',
    imageTextPageCount: typeof record.imageTextPageCount === 'number' ? record.imageTextPageCount : 2,
    imageTextMode: isImageTextMode(record.imageTextMode) ? record.imageTextMode : 'basic',
    imageTextWatermarkEnabled: record.imageTextWatermarkEnabled !== false,
    imageTextReferenceUrls: Array.isArray(record.imageTextReferenceUrls)
      ? record.imageTextReferenceUrls.filter((item): item is string => typeof item === 'string' && !!item.trim())
      : [],
    imageTextReferenceNames: Array.isArray(record.imageTextReferenceNames)
      ? record.imageTextReferenceNames.filter((item): item is string => typeof item === 'string')
      : [],
    litePptPageCount: isLitePptPageCount(record.litePptPageCount) ? record.litePptPageCount : 2,
    litePptVisualPrompt: typeof record.litePptVisualPrompt === 'string' ? record.litePptVisualPrompt.slice(0, 20) : '',
  }
}

function areStringArraysEqual(left: string[] | undefined, right: string[] | undefined) {
  const leftItems = left || []
  const rightItems = right || []
  if (leftItems.length !== rightItems.length) return false
  return leftItems.every((item, index) => item === rightItems[index])
}

function hasActiveSessionUiChanges(
  session: ChatSession,
  next: ChatSession,
) {
  return session.title !== next.title
    || session.customTitle !== next.customTitle
    || session.skill !== next.skill
    || session.messages !== next.messages
    || session.forkedFrom !== next.forkedFrom
    || session.compressedSummary !== next.compressedSummary
    || session.quickVideoRatio !== next.quickVideoRatio
    || session.quickVideoDuration !== next.quickVideoDuration
    || session.quickVideoWatermarkEnabled !== next.quickVideoWatermarkEnabled
    || session.quickVideoFirstFrameUrl !== next.quickVideoFirstFrameUrl
    || session.quickVideoFirstFrameStorageKey !== next.quickVideoFirstFrameStorageKey
    || session.quickVideoFirstFrameName !== next.quickVideoFirstFrameName
    || session.imageTextRatio !== next.imageTextRatio
    || session.imageTextPageCount !== next.imageTextPageCount
    || session.imageTextMode !== next.imageTextMode
    || session.imageTextWatermarkEnabled !== next.imageTextWatermarkEnabled
    || !areStringArraysEqual(session.imageTextReferenceUrls, next.imageTextReferenceUrls)
    || !areStringArraysEqual(session.imageTextReferenceNames, next.imageTextReferenceNames)
    || session.litePptPageCount !== next.litePptPageCount
    || session.litePptVisualPrompt !== next.litePptVisualPrompt
}

function detectVideoRatio(width: number, height: number): string {
  if (!width || !height) return ''
  const d = width / height
  if (d > 1.7 && d < 1.85) return '16:9'
  if (d > 0.9 && d < 1.1) return '1:1'
  if (d > 0.5 && d < 0.6) return '9:16'
  return `${Math.round(d * 100)}:100`
}

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0MB'
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 1024 * 1024 ? 1 : 2)}MB`
}

function storageKeyToDisplayUrl(storageKey: string) {
  const key = storageKey.trim()
  return key ? `/api/storage/sign?key=${encodeURIComponent(key)}&expires=3600` : ''
}

type IdleSchedulerWindow = Window & {
  requestIdleCallback?: (callback: () => void, options?: { timeout?: number }) => number
  cancelIdleCallback?: (handle: number) => void
}

function scheduleIdleTask(callback: () => void, timeoutMs: number) {
  if (typeof window === 'undefined') return () => undefined
  const idleWindow = window as IdleSchedulerWindow
  if (typeof idleWindow.requestIdleCallback === 'function') {
    const handle = idleWindow.requestIdleCallback(callback, { timeout: timeoutMs })
    return () => idleWindow.cancelIdleCallback?.(handle)
  }
  const timer = window.setTimeout(callback, Math.min(timeoutMs, 250))
  return () => window.clearTimeout(timer)
}

function formatWeatherUpdatedAt(value: string) {
  const trimmed = value.trim()
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
  if (!match) return trimmed ? `${trimmed} 更新` : '刚刚更新'
  return `${Number(match[2])}月${Number(match[3])}日 ${match[4]}:${match[5]} 更新`
}

function formatAiChatMessageTime(value: string | undefined) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const weekday = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'][date.getDay()]
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${weekday} ${hours}:${minutes}`
}

function formatForecastDayLabel(value: string, index: number) {
  if (index === 0) return '今天'
  if (index === 1) return '明天'
  if (index === 2) return '后天'
  return value.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$2月$3日').replace(/^0/, '').replace('月0', '月')
}

function weatherIconFor(condition: string) {
  if (/雷/.test(condition)) return '⛈'
  if (/雪|冰雹|冻/.test(condition)) return '❄'
  if (/雨|阵雨|毛毛雨/.test(condition)) return '🌧'
  if (/阴/.test(condition)) return '☁'
  if (/云|多云/.test(condition)) return '⛅'
  if (/雾/.test(condition)) return '🌫'
  if (/晴/.test(condition)) return '☀️'
  return '🌡️'
}

function weatherCardThemeClass(condition: string) {
  if (/雷/.test(condition)) return 'from-[#201b58] via-[#26327e] to-[#111b42]'
  if (/雪|冰雹|冻/.test(condition)) return 'from-[#234761] via-[#285d84] to-[#123047]'
  if (/雨|阵雨|毛毛雨/.test(condition)) return 'from-[#071b35] via-[#123f72] to-[#061525]'
  if (/阴|云|多云|雾/.test(condition)) return 'from-[#24364f] via-[#304a6f] to-[#15253b]'
  if (/晴/.test(condition)) return 'from-[#214a86] via-[#2f64a9] to-[#173761]'
  return 'from-[#183861] via-[#24558e] to-[#102846]'
}

function weatherTemperatureValue(value: string) {
  return value.replace('°C', '').replace('°', '').trim()
}

function parseAspectRatioValue(value: string): number | null {
  const match = value.trim().match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/)
  if (!match) return null
  const width = Number(match[1])
  const height = Number(match[2])
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null
  return width / height
}

function normalizeQuickVideoPreviewRatio(value: string | undefined) {
  return isQuickVideoRatio(value) ? value : '9:16'
}

function getQuickVideoPreviewAspectRatio(value: string | undefined) {
  const ratio = normalizeQuickVideoPreviewRatio(value)
  return ratio.replace(':', ' / ')
}

function getQuickVideoPreviewMaxWidthClass(value: string | undefined) {
  const ratio = normalizeQuickVideoPreviewRatio(value)
  if (ratio === '9:16') return 'max-w-[240px]'
  if (ratio === '1:1') return 'max-w-[340px]'
  return 'max-w-md'
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)
    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({ width: image.naturalWidth, height: image.naturalHeight })
    }
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('无法识别图片尺寸'))
    }
    image.src = objectUrl
  })
}

function readImageFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string' && reader.result.startsWith('data:image/')) {
        resolve(reader.result)
        return
      }
      reject(new Error('无法读取图片文件'))
    }
    reader.onerror = () => reject(new Error('无法读取图片文件'))
    reader.readAsDataURL(file)
  })
}

function toRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  return value as Record<string, unknown>
}

function readNonEmptyString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function resizeAiChatComposerTextarea(textarea: HTMLTextAreaElement) {
  textarea.style.height = `${CHAT_INPUT_MIN_HEIGHT}px`
  const nextHeight = Math.min(textarea.scrollHeight, CHAT_INPUT_MAX_HEIGHT)
  textarea.style.height = `${Math.max(CHAT_INPUT_MIN_HEIGHT, nextHeight)}px`
  textarea.style.overflowY = textarea.scrollHeight > CHAT_INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
}

function normalizeAiChatSettingsSnapshot(value: unknown): AiChatSettingsSnapshot {
  const settings = toRecord(value)
  const model = readNonEmptyString(settings.model)
  return {
    model,
    compressionModel: readNonEmptyString(settings.compressionModel) || model,
    assistantAvatarUrl: readNonEmptyString(settings.assistantAvatarUrl) || DEFAULT_AI_CHAT_AVATAR,
    regenerateDeleteTrailingMessages: settings.regenerateDeleteTrailingMessages === true,
    showRegenerateConfirmDialog: settings.showRegenerateConfirmDialog !== false,
  }
}

function readFreshAiChatSettingsMemoryCache(userId: string) {
  const cached = aiChatSettingsMemoryCache.get(userId)
  if (!cached || Date.now() - cached.loadedAt > AI_CHAT_SETTINGS_MEMORY_CACHE_TTL_MS) return null
  return cached.settings
}

function writeAiChatSettingsMemoryCache(userId: string, settings: AiChatSettingsSnapshot) {
  if (!userId) return
  aiChatSettingsMemoryCache.set(userId, { loadedAt: Date.now(), settings })
}

function serializeAiChatSessionsPayload(sessions: ChatSession[], activeSessionId: string) {
  return JSON.stringify({ sessions, activeSessionId })
}

function readChatSessionsLatestUpdatedAtMs(sessions: ChatSession[]) {
  return sessions.reduce((latest, session) => {
    const updatedAtMs = Date.parse(session.updatedAt || '')
    return Number.isFinite(updatedAtMs) ? Math.max(latest, updatedAtMs) : latest
  }, 0)
}

function readAiChatLocalSessionsCache(userId: string): AiChatLocalSessionsCacheEntry | null {
  if (!userId || typeof window === 'undefined') return null

  try {
    const serializedSessions = window.localStorage.getItem(getChatSessionsStorageKey(userId)) || ''
    const serializedActiveSession = window.localStorage.getItem(getChatStorageKey(userId)) || ''
    const parsedSessions = serializedSessions ? JSON.parse(serializedSessions) : null
    const parsedActiveSession = serializedActiveSession ? JSON.parse(serializedActiveSession) : null
    const storedSessions = Array.isArray(parsedSessions?.sessions)
      ? parsedSessions.sessions.map(normalizePersistedSession).filter(Boolean) as ChatSession[]
      : []
    const activeSession = normalizePersistedSession(parsedActiveSession)
    const sessionMap = new Map(storedSessions.map((session) => [session.id, session]))
    if (activeSession) sessionMap.set(activeSession.id, activeSession)
    const sessions = Array.from(sessionMap.values()).sort((left, right) => (
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
    ))
    if (sessions.length === 0) return null
    const requestedActiveId = typeof parsedSessions?.activeSessionId === 'string' ? parsedSessions.activeSessionId : ''
    const activeSessionId = activeSession?.id || (sessions.some((session) => session.id === requestedActiveId) ? requestedActiveId : sessions[0].id)
    return {
      sessions,
      activeSessionId,
      serializedPayload: serializeAiChatSessionsPayload(sessions, activeSessionId),
      activeSessionPayload: serializeAiChatActiveSessionStoragePayload(
        sessions.find((session) => session.id === activeSessionId) || sessions[0],
      ),
    }
  } catch {
    return null
  }
}

function readFreshAiChatSessionsMemoryCache(userId: string) {
  const cached = aiChatSessionsMemoryCache.get(userId)
  if (!cached || Date.now() - cached.loadedAt > AI_CHAT_SESSIONS_MEMORY_CACHE_TTL_MS) return null
  return cached
}

function writeAiChatSessionsMemoryCache(userId: string, sessions: ChatSession[], activeSessionId: string) {
  const serializedPayload = serializeAiChatSessionsPayload(sessions, activeSessionId)
  if (userId) {
    aiChatSessionsMemoryCache.set(userId, {
      loadedAt: Date.now(),
      sessions,
      activeSessionId,
      serializedPayload,
    })
  }
  return serializedPayload
}

function serializeAiChatActiveSessionStoragePayload(
  session: ChatSession,
  overrides: Partial<PersistedChatState> = {},
) {
  const source = { ...session, ...overrides }
  const payload: PersistedChatState = {
    ...session,
    skill: source.skill,
    messages: source.messages,
    compressedSummary: source.compressedSummary,
    quickVideoRatio: source.quickVideoRatio,
    quickVideoDuration: source.quickVideoDuration,
    quickVideoWatermarkEnabled: source.quickVideoWatermarkEnabled,
    quickVideoFirstFrameUrl: source.quickVideoFirstFrameUrl,
    quickVideoFirstFrameStorageKey: source.quickVideoFirstFrameStorageKey,
    quickVideoFirstFrameName: source.quickVideoFirstFrameName,
    imageTextRatio: source.imageTextRatio,
    imageTextPageCount: source.imageTextPageCount,
    imageTextMode: source.imageTextMode,
    imageTextWatermarkEnabled: source.imageTextWatermarkEnabled,
    imageTextReferenceUrls: source.imageTextReferenceUrls,
    imageTextReferenceNames: source.imageTextReferenceNames,
    litePptPageCount: source.litePptPageCount,
    litePptVisualPrompt: source.litePptVisualPrompt,
  }
  return JSON.stringify(payload)
}

function normalizeAiChatUserModelsPayload(value: unknown): UserModelsPayload {
  const models = toRecord(value)
  return {
    llm: Array.isArray(models.llm) ? models.llm : [],
    image: Array.isArray(models.image) ? models.image : [],
    video: Array.isArray(models.video) ? models.video : [],
    audio: Array.isArray(models.audio) ? models.audio : [],
    lipsync: Array.isArray(models.lipsync) ? models.lipsync : [],
  } as UserModelsPayload
}

function readFreshAiChatBrowserBootstrapCache(userId: string): AiChatBrowserBootstrapCacheEntry | null {
  if (!userId || typeof window === 'undefined') return null

  try {
    const serialized = window.localStorage.getItem(getChatBootstrapStorageKey(userId))
    if (!serialized) return null
    const parsed = JSON.parse(serialized)
    const cachedAt = typeof parsed?.cachedAt === 'number' ? parsed.cachedAt : 0
    if (!cachedAt || Date.now() - cachedAt > AI_CHAT_BROWSER_BOOTSTRAP_CACHE_TTL_MS) return null
    const settings = normalizeAiChatSettingsSnapshot(parsed.settings)
    const models = normalizeAiChatUserModelsPayload(parsed.models)
    const sessions = Array.isArray(parsed.sessions)
      ? parsed.sessions.map(normalizePersistedSession).filter(Boolean) as ChatSession[]
      : []
    if (sessions.length === 0) return null
    const requestedActiveId = typeof parsed.activeSessionId === 'string' ? parsed.activeSessionId : ''
    const activeSessionId = sessions.some((session) => session.id === requestedActiveId) ? requestedActiveId : sessions[0].id
    return {
      settings,
      models,
      sessions,
      activeSessionId,
      serializedPayload: serializeAiChatSessionsPayload(sessions, activeSessionId),
    }
  } catch {
    return null
  }
}

function writeAiChatBrowserBootstrapCache(
  userId: string,
  settings: AiChatSettingsSnapshot,
  models: UserModelsPayload,
  sessions: ChatSession[],
  activeSessionId: string,
) {
  if (!userId || typeof window === 'undefined' || sessions.length === 0) return
  try {
    window.localStorage.setItem(getChatBootstrapStorageKey(userId), JSON.stringify({
      cachedAt: Date.now(),
      settings,
      models,
      sessions,
      activeSessionId,
    }))
  } catch {
    // localStorage can be unavailable in private mode or under quota pressure.
  }
}

function readFreshAiChatModelsMemoryCache(userId: string) {
  const cached = aiChatModelsMemoryCache.get(userId)
  if (!cached || Date.now() - cached.loadedAt > AI_CHAT_MODELS_MEMORY_CACHE_TTL_MS) return null
  return cached.models
}

function writeAiChatModelsMemoryCache(userId: string, models: UserModelsPayload) {
  if (!userId) return
  aiChatModelsMemoryCache.set(userId, { loadedAt: Date.now(), models })
}

function clearAiChatMemoryCaches() {
  aiChatSettingsMemoryCache.clear()
  aiChatSessionsMemoryCache.clear()
  aiChatModelsMemoryCache.clear()
}

function friendlyAiChatWorkflowMessage(value: unknown) {
  const message = readNonEmptyString(value)
  if (!message) return ''
  const normalized = message.toLowerCase()
  if (message.includes('有字图已写入媒体存储') || message.includes('媒体存储')) {
    return '正在保存页面素材'
  }
  if (message.includes('无字底图')) {
    return '页面画面生成失败，请稍后重试或切换图片模型。'
  }
  if (
    message.includes('正在 OCR')
    || message.includes('OCR 识别到')
    || message.includes('文字层')
    || message.includes('页面布局')
  ) {
    return '正在分析页面布局'
  }
  if (message.includes('正在生成第') && message.includes('有字图')) {
    return '正在生成页面预览'
  }
  if (
    (message.includes('正在生成第') && message.includes('无字底图'))
    || message.includes('可编辑底图')
    || message.includes('页面画面')
  ) {
    return '正在完善页面画面'
  }
  if (
    normalized.includes('system_cpu_overloaded')
    || normalized.includes('system cpu overloaded')
    || normalized.includes('system_memory_overloaded')
    || normalized.includes('system memory overloaded')
  ) {
    return '生成服务暂时繁忙，请稍后重试。'
  }
  if (
    normalized.includes('signature version 4 presigned urls')
    || normalized.includes('expiration date less than one week')
  ) {
    return '生成结果保存失败，请稍后重试。'
  }
  if (message.includes('AI PPT 文本规划质量不合格')) {
    return '系统未能生成稳定可用的页面文案，已停止生成。请稍后重试。'
  }
  if (
    (normalized.includes('failed to deserialize the json body') && normalized.includes('image_url'))
    || (normalized.includes('unknown variant') && normalized.includes('image_url') && normalized.includes('expected') && normalized.includes('text'))
  ) {
    return '配置升级中，请稍候再试。'
  }
  if (
    (
      normalized.includes('model is not supported')
      && (normalized.includes('image') || normalized.includes('gpt-image'))
    )
    || (
      normalized.includes('not supported when using codex')
      && normalized.includes('gpt-image')
    )
  ) {
    return '当前图片模型暂时不可用，请稍后重试或切换图片模型。'
  }
  if (
    normalized.includes('openai_compat_')
    || normalized.includes('generate_content_failed')
    || normalized.includes('new_api_error')
  ) {
    return '生成服务暂时不可用，请稍后重试。'
  }
  return message
}

const LITE_PPT_FAILED_FALLBACK_MESSAGE = '生成服务暂时不可用，请稍后重试。'

function isTerminalImageTextProjectStatus(status: string) {
  return status === 'completed' || status === 'partial' || status === 'failed'
}

function isTerminalLitePptProjectStatus(status: string) {
  return status === 'completed' || status === 'failed'
}

type AiChatApiResponseBody = Record<string, unknown>
type AiChatApiError = Error & { sessionExpired?: true }

async function readAiChatJson(response: Response): Promise<AiChatApiResponseBody> {
  const body = await response.json().catch(() => ({}))
  return toRecord(body)
}

async function readAiChatCompressionBody(
  response: Response,
  fallback: string,
): Promise<AiChatApiResponseBody> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes(AI_CHAT_COMPRESS_STREAM_CONTENT_TYPE.split(';')[0])) {
    const body = await readAiChatJson(response)
    if (!response.ok) throw createAiChatApiError(body, fallback)
    return body
  }

  if (!response.body) throw new Error('压缩响应不可读取。')

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const consumeBlock = (block: string): AiChatApiResponseBody | null => {
    const parsed = parseAiChatStreamBlock(block)
    if (!parsed) return null
    if (parsed.event === 'done') return parsed.data
    if (parsed.event === 'error') throw createAiChatApiError(parsed.data, fallback)
    return null
  }

  while (true) {
    const { value, done } = await reader.read()
    if (value) {
      buffer += decoder.decode(value, { stream: !done })
      let boundaryIndex = buffer.indexOf('\n\n')
      while (boundaryIndex >= 0) {
        const block = buffer.slice(0, boundaryIndex)
        buffer = buffer.slice(boundaryIndex + 2)
        const body = consumeBlock(block)
        if (body) return body
        boundaryIndex = buffer.indexOf('\n\n')
      }
    }
    if (done) break
  }

  const tail = buffer.trim()
  if (tail) {
    const body = consumeBlock(tail)
    if (body) return body
  }

  throw new Error('压缩响应中断，请稍后重试。')
}

function isAiChatSessionExpiredPayload(body: AiChatApiResponseBody) {
  const error = toRecord(body.error)
  const details = toRecord(error.details)
  const values = [
    body.reason,
    body.message,
    body.error,
    error.reason,
    error.message,
    details.reason,
  ]

  return values.some((value) => (
    value === API_SESSION_EXPIRED_REASON || value === API_SESSION_EXPIRED_MESSAGE
  ))
}

function createAiChatApiError(body: AiChatApiResponseBody, fallback: string): AiChatApiError {
  const error = toRecord(body.error)
  const sessionExpired = isAiChatSessionExpiredPayload(body)
  const message = sessionExpired
    ? AI_CHAT_SESSION_EXPIRED_MESSAGE
    : readNonEmptyString(error.message)
      || readNonEmptyString(body.error)
      || readNonEmptyString(body.message)
      || fallback
  const apiError = new Error(message) as AiChatApiError
  if (sessionExpired) apiError.sessionExpired = true
  return apiError
}

function isAiChatSessionExpiredError(error: unknown) {
  return error instanceof Error && (error as AiChatApiError).sessionExpired === true
}

function readAiChatErrorMessage(error: unknown, fallback: string) {
  if (isAiChatSessionExpiredError(error)) return AI_CHAT_SESSION_EXPIRED_MESSAGE
  const rawMessage = error instanceof Error ? error.message.trim() : ''
  if (isAiChatRawNetworkFailureMessage(rawMessage)) return AI_CHAT_NETWORK_FAILURE_HINT
  return rawMessage || fallback
}

function createAiChatNetworkError(error: unknown, fallback: string) {
  const rawMessage = error instanceof Error ? error.message.trim() : ''
  if (isAiChatRawNetworkFailureMessage(rawMessage)) {
    return new Error(AI_CHAT_NETWORK_FAILURE_HINT)
  }
  return new Error(rawMessage || fallback)
}

function copyAiChatTextWithSelectionFallback(text: string) {
  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'true')
    textarea.style.position = 'fixed'
    textarea.style.left = '0'
    textarea.style.top = '0'
    textarea.style.width = '1px'
    textarea.style.height = '1px'
    textarea.style.opacity = '0'
    textarea.style.pointerEvents = 'none'
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)
    const ok = document.execCommand('copy')
    document.body.removeChild(textarea)
    return ok
  } catch {
    return false
  }
}

async function copyAiChatTextToClipboard(text: string) {
  if (copyAiChatTextWithSelectionFallback(text)) return true

  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Clipboard API can be blocked in embedded browsers; keep a local fallback.
  }

  return false
}

function splitAiChatSpeechText(text: string) {
  const normalized = text.replace(/\s+/g, ' ').trim()
  if (!normalized) return []
  const chunks: string[] = []
  let current = ''
  for (const part of normalized.split(/(?<=[。！？.!?；;])\s*/u)) {
    if (!part) continue
    if (current && Array.from(`${current}${part}`).length > 180) {
      chunks.push(current)
      current = part
      continue
    }
    current = current ? `${current} ${part}` : part
  }
  if (current) chunks.push(current)
  return chunks.flatMap((chunk) => {
    const chars = Array.from(chunk)
    if (chars.length <= 220) return [chunk]
    const parts: string[] = []
    for (let index = 0; index < chars.length; index += 180) {
      parts.push(chars.slice(index, index + 180).join(''))
    }
    return parts
  })
}

function pickAiChatSpeechVoice(content: string) {
  if (typeof window === 'undefined' || !('speechSynthesis' in window)) return null
  const voices = window.speechSynthesis.getVoices()
  if (!voices.length) return null
  const preferredLang = /[\u4e00-\u9fff]/u.test(content) ? 'zh' : 'en'
  return voices.find((voice) => voice.lang.toLowerCase().startsWith(preferredLang))
    || voices.find((voice) => voice.lang.toLowerCase().startsWith('zh'))
    || voices[0]
    || null
}

type AiChatTaskSnapshot = {
  status: 'running' | 'completed' | 'failed'
  progress?: number
  message?: string
}

function readTaskProgress(value: unknown): number | undefined {
  const parsed = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(parsed)) return undefined
  return Math.max(0, Math.min(100, Math.round(parsed)))
}

function readFiniteNumber(value: unknown, fallback = 0): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeLitePptTextBox(value: unknown): LitePptTextBoxDTO | null {
  const record = toRecord(value)
  const id = readNonEmptyString(record.id)
  const text = typeof record.text === 'string' ? record.text : ''
  if (!id || !text.trim()) return null
  const align = record.align === 'center' || record.align === 'right' ? record.align : 'left'
  return {
    id,
    text,
    x: readFiniteNumber(record.x),
    y: readFiniteNumber(record.y),
    width: Math.max(1, readFiniteNumber(record.width, 1)),
    height: Math.max(1, readFiniteNumber(record.height, 1)),
    fontSize: Math.max(1, readFiniteNumber(record.fontSize, 24)),
    color: readNonEmptyString(record.color) || '#ffffff',
    ...(record.bold === true ? { bold: true } : {}),
    ...(readNonEmptyString(record.backgroundColor) ? { backgroundColor: readNonEmptyString(record.backgroundColor) } : {}),
    ...(typeof record.backgroundOpacity === 'number' ? { backgroundOpacity: record.backgroundOpacity } : {}),
    ...(readNonEmptyString(record.borderColor) ? { borderColor: readNonEmptyString(record.borderColor) } : {}),
    ...(typeof record.borderWidth === 'number' ? { borderWidth: record.borderWidth } : {}),
    ...(typeof record.borderRadius === 'number' ? { borderRadius: record.borderRadius } : {}),
    align,
    ...(typeof record.lineHeight === 'number' ? { lineHeight: record.lineHeight } : {}),
    ...(readNonEmptyString(record.textShadow) ? { textShadow: readNonEmptyString(record.textShadow) } : {}),
  }
}

function normalizeLitePptTextBoxes(value: unknown): LitePptTextBoxDTO[] {
  if (!Array.isArray(value)) return []
  return value.map(normalizeLitePptTextBox).filter(Boolean) as LitePptTextBoxDTO[]
}

function readAiChatTaskSnapshot(task: Record<string, unknown>): AiChatTaskSnapshot {
  const rawStatus = readNonEmptyString(task.status)
  const payload = toRecord(task.payload)
  const error = toRecord(task.error)
  const message =
    friendlyAiChatWorkflowMessage(error.message)
    || friendlyAiChatWorkflowMessage(task.errorMessage)
    || friendlyAiChatWorkflowMessage(payload.message)
    || friendlyAiChatWorkflowMessage(payload.stageLabel)
  const progress = readTaskProgress(task.progress)

  return {
    status: rawStatus === 'completed'
      ? 'completed'
      : rawStatus === 'failed' || rawStatus === 'dismissed'
        ? 'failed'
        : 'running',
    ...(typeof progress === 'number' ? { progress } : {}),
    ...(message ? { message } : {}),
  }
}

type QuickVideoTaskSnapshot = AiChatTaskSnapshot

function readQuickVideoTaskSnapshot(task: Record<string, unknown>): QuickVideoTaskSnapshot {
  return readAiChatTaskSnapshot(task)
}

async function fetchAiChatTaskSnapshot(taskId?: string): Promise<AiChatTaskSnapshot | null> {
  if (!taskId) return null
  const response = await fetch(`/api/tasks/${encodeURIComponent(taskId)}`, { cache: 'no-store' })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) return null
  const task = toRecord(body?.task)
  if (!readNonEmptyString(task.id)) return null
  return readAiChatTaskSnapshot(task)
}

async function fetchQuickVideoTaskSnapshot(taskId?: string): Promise<QuickVideoTaskSnapshot | null> {
  return fetchAiChatTaskSnapshot(taskId)
}

async function fetchLatestQuickVideoTaskSnapshot(workflow: Extract<AiChatWorkflow, { type: 'quick-video' }>): Promise<QuickVideoTaskSnapshot | null> {
  const params = new URLSearchParams()
  params.set('projectId', workflow.projectId)
  params.set('targetId', workflow.episodeId)
  params.set('limit', '5')
  params.append('type', 'quick_video_one_click_run')
  const response = await fetch(`/api/tasks?${params.toString()}`, { cache: 'no-store' })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) return null
  const tasks: Record<string, unknown>[] = Array.isArray(body?.tasks) ? body.tasks.map(toRecord) : []
  const task = tasks.find((item) => readNonEmptyString(item.id)) || null
  return task ? readQuickVideoTaskSnapshot(task) : null
}

function createEmptySessionState(): { sessions: ChatSession[]; activeSessionId: string } {
  const session = createEmptySession()
  return { sessions: [session], activeSessionId: session.id }
}

export default function WorkspaceAiChatPage() {
  const queryClient = useQueryClient()
  const invalidateAiChatWorkspaceProjects = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.aiChatWorkspaceProjects.all() })
  }, [queryClient])
  const initialSessionsRef = useRef<{ sessions: ChatSession[]; activeSessionId: string } | null>(null)
  if (!initialSessionsRef.current) initialSessionsRef.current = createEmptySessionState()
  const initialSessions = initialSessionsRef.current
  const initialActiveSession = initialSessions.sessions.find((session) => session.id === initialSessions.activeSessionId) || initialSessions.sessions[0]
  const [skill, setSkill] = useState<SkillMode>(() => initialActiveSession.skill || 'general')
  const { data: session, status: sessionStatus } = useSession()
  const currentSessionUserId = (session?.user as { id?: string } | undefined)?.id || ''
  const isAiChatAuthenticated = sessionStatus === 'authenticated'
  const [aiChatBootstrapMode, setAiChatBootstrapMode] = useState<AiChatBootstrapMode>('pending')
  const modelsQuery = useUserModels({ enabled: isAiChatAuthenticated && aiChatBootstrapMode === 'fallback' })
  const quickVideoSettingsQuery = useQuickVideoSettings({ enabled: isAiChatAuthenticated && skill === 'quick-video' })
  const imageTextSettingsQuery = useImageTextSettings({ enabled: isAiChatAuthenticated && skill === 'image-text' })
  const litePptSettingsQuery = useLitePptSettings({ enabled: isAiChatAuthenticated && skill === 'lite-ppt' })

  const prefetchAiChatSkillSettings = useCallback((targetSkill: SkillMode) => {
    if (!isAiChatAuthenticated) return
    if (targetSkill === 'quick-video') {
      void queryClient.prefetchQuery({
        queryKey: queryKeys.quickVideo.settings(),
        staleTime: 60_000,
        queryFn: async () => {
          const res = await apiFetch('/api/quick-video/settings')
          if (!res.ok) throw new Error('Failed to fetch quick video settings')
          return res.json()
        },
      })
      return
    }
    if (targetSkill === 'image-text') {
      void queryClient.prefetchQuery({
        queryKey: queryKeys.imageText.settings(),
        staleTime: 60_000,
        queryFn: async () => {
          const res = await apiFetch('/api/image-text/settings')
          if (!res.ok) throw new Error('Failed to fetch image text settings')
          return res.json()
        },
      })
      return
    }
    if (targetSkill === 'lite-ppt') {
      void queryClient.prefetchQuery({
        queryKey: queryKeys.litePpt.settings(),
        staleTime: 60_000,
        queryFn: async () => {
          const res = await apiFetch('/api/lite-ppt/settings')
          if (!res.ok) throw new Error('Failed to fetch lite ppt settings')
          return res.json()
        },
      })
    }
  }, [isAiChatAuthenticated, queryClient])
  const [selectedModel, setSelectedModel] = useState('')
  const [compressionModel, setCompressionModel] = useState('')
  const [assistantAvatarUrl, setAssistantAvatarUrl] = useState(DEFAULT_AI_CHAT_AVATAR)
  const [regenerateDeleteTrailingMessages, setRegenerateDeleteTrailingMessages] = useState(false)
  const [showRegenerateConfirmDialog, setShowRegenerateConfirmDialog] = useState(true)
  const [webSearchEnabled, setWebSearchEnabled] = useState(false)
  const [sessions, setSessions] = useState<ChatSession[]>(initialSessions.sessions)
  const [activeSessionId, setActiveSessionId] = useState(initialSessions.activeSessionId)
  const [messages, setMessages] = useState<ChatMessage[]>(() => initialActiveSession.messages || INITIAL_MESSAGES)
  const [showAllMessages, setShowAllMessages] = useState(false)
  const [compressedSummary, setCompressedSummary] = useState(() => normalizeCompressedSummary(initialActiveSession.compressedSummary))
  const [input, setInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [streamingAssistantId, setStreamingAssistantId] = useState('')
  const [isCompressing, setIsCompressing] = useState(false)
  const [error, setError] = useState('')
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [renamingSessionId, setRenamingSessionId] = useState('')
  const [renamingTitle, setRenamingTitle] = useState('')
  const [serverSessionsReady, setServerSessionsReady] = useState(false)
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)
  const [previewImageQuickVideoUrl, setPreviewImageQuickVideoUrl] = useState<string | null>(null)
  const [mobileSessionPanelOpen, setMobileSessionPanelOpen] = useState(false)
  const [mobileSkillPanelOpen, setMobileSkillPanelOpen] = useState(false)
  const [mobileSkillCardMenuOpen, setMobileSkillCardMenuOpen] = useState(false)
  const [mobileComposerToolsOpen, setMobileComposerToolsOpen] = useState(false)
  const [desktopSidebarCollapsed, setDesktopSidebarCollapsed] = useState(false)
  const [desktopSidebarContentMode, setDesktopSidebarContentMode] = useState<'expanded' | 'collapsed'>('expanded')
  const [desktopSessionListMaxHeight, setDesktopSessionListMaxHeight] = useState<number | null>(null)
  const [compressionOptionsOpen, setCompressionOptionsOpen] = useState(false)
  const [regenerateConfirmMessage, setRegenerateConfirmMessage] = useState<ChatMessage | null>(null)
  const [manualCompressionLimitMode, setManualCompressionLimitMode] = useState<CompressionLimitMode>('start')
  const [manualCompressionMaxChars, setManualCompressionMaxChars] = useState(String(DEFAULT_MANUAL_COMPRESS_CHAR_COUNT))
  const [compressionOptionsError, setCompressionOptionsError] = useState('')
  const [editingMessageId, setEditingMessageId] = useState('')
  const [editingMessageText, setEditingMessageText] = useState('')
  const [editingMessageError, setEditingMessageError] = useState('')
  const [editingCompressionSummaryId, setEditingCompressionSummaryId] = useState('')
  const [editingCompressionSummaryText, setEditingCompressionSummaryText] = useState('')
  const [editingCompressionSummaryError, setEditingCompressionSummaryError] = useState('')
  const [messageMenuId, setMessageMenuId] = useState('')
  const [copiedMessageId, setCopiedMessageId] = useState('')
  const [selectCopyMessage, setSelectCopyMessage] = useState<ChatMessage | null>(null)
  const [previewMessage, setPreviewMessage] = useState<ChatMessage | null>(null)
  const [translatingMessageId, setTranslatingMessageId] = useState('')
  const [speakingMessageId, setSpeakingMessageId] = useState('')
  const [canUseBrowserSpeech, setCanUseBrowserSpeech] = useState(false)
  const [isImportingDocument, setIsImportingDocument] = useState(false)
  const [isAiChatClientMounted, setIsAiChatClientMounted] = useState(false)
  const [aiChatHeavyPreviewsReady, setAiChatHeavyPreviewsReady] = useState(false)
  const [expandedCompressionSummaryIds, setExpandedCompressionSummaryIds] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    setIsAiChatClientMounted(true)
    if (typeof window !== 'undefined') {
      setCanUseBrowserSpeech('speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined')
    }
  }, [])

  useEffect(() => {
    setAiChatHeavyPreviewsReady(false)
    if (typeof window === 'undefined') return

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number
      cancelIdleCallback?: (handle: number) => void
    }
    let idleId: number | null = null
    let timerId: number | null = null
    const revealPreviews = () => {
      idleId = null
      timerId = null
      setAiChatHeavyPreviewsReady(true)
    }

    if (typeof idleWindow.requestIdleCallback === 'function') {
      idleId = idleWindow.requestIdleCallback(revealPreviews, {
        timeout: AI_CHAT_HEAVY_PREVIEW_IDLE_TIMEOUT_MS,
      })
    } else {
      timerId = window.setTimeout(revealPreviews, AI_CHAT_HEAVY_PREVIEW_IDLE_DELAY_MS)
    }

    return () => {
      if (idleId !== null) idleWindow.cancelIdleCallback?.(idleId)
      if (timerId !== null) window.clearTimeout(timerId)
    }
  }, [activeSessionId])

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel()
      }
    }
  }, [])
  const [quickVideoRatio, setQuickVideoRatio] = useState<(typeof QUICK_VIDEO_RATIOS)[number]>(() => initialActiveSession.quickVideoRatio || '9:16')
  const [quickVideoDuration, setQuickVideoDuration] = useState(() => initialActiveSession.quickVideoDuration || 10)
  const [quickVideoWatermarkEnabled, setQuickVideoWatermarkEnabled] = useState(() => initialActiveSession.quickVideoWatermarkEnabled !== false)
  const [quickVideoFirstFrameUrl, setQuickVideoFirstFrameUrl] = useState(() => initialActiveSession.quickVideoFirstFrameUrl || '')
  const [quickVideoFirstFrameStorageKey, setQuickVideoFirstFrameStorageKey] = useState(() => initialActiveSession.quickVideoFirstFrameStorageKey || '')
  const [quickVideoFirstFrameName, setQuickVideoFirstFrameName] = useState(() => initialActiveSession.quickVideoFirstFrameName || '')
  const [quickVideoFirstFrameError, setQuickVideoFirstFrameError] = useState('')
  const [quickVideoFirstFrameUploading, setQuickVideoFirstFrameUploading] = useState(false)
  const [imageTextRatio, setImageTextRatio] = useState<(typeof IMAGE_TEXT_RATIOS)[number]>(() => initialActiveSession.imageTextRatio || '9:16')
  const [imageTextPageCount, setImageTextPageCount] = useState(() => initialActiveSession.imageTextPageCount || 2)
  const [imageTextMode, setImageTextMode] = useState<(typeof IMAGE_TEXT_MODES)[number]['value']>(() => initialActiveSession.imageTextMode || 'basic')
  const [imageTextWatermarkEnabled, setImageTextWatermarkEnabled] = useState(() => initialActiveSession.imageTextWatermarkEnabled !== false)
  const [imageTextReferenceUrls, setImageTextReferenceUrls] = useState<string[]>(() => initialActiveSession.imageTextReferenceUrls || [])
  const [imageTextReferenceNames, setImageTextReferenceNames] = useState<string[]>(() => initialActiveSession.imageTextReferenceNames || [])
  const [imageTextReferenceError, setImageTextReferenceError] = useState('')
  const [imageTextReferenceUploading, setImageTextReferenceUploading] = useState(false)
  const [litePptPageCount, setLitePptPageCount] = useState(() => initialActiveSession.litePptPageCount || 2)
  const [litePptVisualPrompt, setLitePptVisualPrompt] = useState(() => initialActiveSession.litePptVisualPrompt || '')
  const pageShellRef = useRef<HTMLElement | null>(null)
  const messageListRef = useRef<HTMLDivElement | null>(null)
  const composerTextareaRef = useRef<HTMLTextAreaElement | null>(null)
  const quickVideoFirstFrameInputRef = useRef<HTMLInputElement | null>(null)
  const imageTextReferenceInputRef = useRef<HTMLInputElement | null>(null)
  const textFileInputRef = useRef<HTMLInputElement | null>(null)
  const activeSessionIdRef = useRef(activeSessionId)
  const workspaceRefreshAfterSaveRef = useRef(false)
  const lastLocalSessionsPayloadRef = useRef('')
  const lastLocalActiveSessionPayloadRef = useRef('')
  const lastServerSessionsPayloadRef = useRef('')
  const latestServerSavePayloadRef = useRef('')
  const pendingServerSessionsPayloadRef = useRef('')
  const localSessionsUserIdRef = useRef('')
  const localSessionMutationVersionRef = useRef(0)
  const scrollFrameRef = useRef<number | null>(null)
  const scrollSettleTimerRef = useRef<number | null>(null)
  const shouldAutoScrollMessagesRef = useRef(true)
  const lastMessageListScrollTopRef = useRef(0)
  const lastAutoScrollMessageCountRef = useRef(0)
  const lastAutoScrollLastMessageIdRef = useRef('')
  const messageListAutoScrollLockedRef = useRef(false)
  const messageListUserInteractingRef = useRef(false)
  const messageListInteractionReleaseTimerRef = useRef<number | null>(null)
  const sessionExpiredHandledRef = useRef(false)
  const aiChatRequestAbortControllerRef = useRef<AbortController | null>(null)
  const aiChatSubmitInFlightRef = useRef(false)
  const speechRunIdRef = useRef(0)
  const copiedMessageResetTimerRef = useRef<number | null>(null)
  const desktopSidebarRef = useRef<HTMLElement | null>(null)
  const desktopSessionListRef = useRef<HTMLDivElement | null>(null)
  const desktopSkillOverlayRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    return () => {
      if (copiedMessageResetTimerRef.current !== null) {
        window.clearTimeout(copiedMessageResetTimerRef.current)
      }
    }
  }, [])

  const modelOptions = useMemo(() => modelsQuery.data?.llm || [], [modelsQuery.data?.llm])
  const quickVideoTextModel = quickVideoSettingsQuery.data?.settings.textModel || ''
  const quickVideoSegmentSeconds = quickVideoSettingsQuery.data?.settings.durationSettings.segmentSeconds
  const quickVideoDurationOptions = useMemo(
    () => buildQuickVideoDurationOptions(quickVideoSegmentSeconds),
    [quickVideoSegmentSeconds],
  )
  const quickVideoFirstFrameSource = quickVideoFirstFrameStorageKey || quickVideoFirstFrameUrl
  const quickVideoFirstFrameDisplayUrl = quickVideoFirstFrameStorageKey
    ? storageKeyToDisplayUrl(quickVideoFirstFrameStorageKey)
    : quickVideoFirstFrameUrl
  const imageTextTextModel = imageTextSettingsQuery.data?.settings.textModel || ''
  const litePptTextModel = litePptSettingsQuery.data?.settings.textModel || ''
  const effectiveModel = skill === 'quick-video'
    ? quickVideoTextModel
    : skill === 'image-text'
      ? imageTextTextModel
      : skill === 'lite-ppt'
        ? litePptTextModel || selectedModel
        : selectedModel
  const matchedActiveSession = sessions.find((session) => session.id === activeSessionId)
  const activeSession = matchedActiveSession || sessions[0]
  const activeSessionForkSource = matchedActiveSession
    ? cleanAiChatForkSourceForSession(matchedActiveSession.id, matchedActiveSession.forkedFrom)
    : undefined
  const shouldCollapseMessageHistory = messages.length > AI_CHAT_RECENT_MESSAGE_RENDER_LIMIT + AI_CHAT_HISTORY_COLLAPSE_BUFFER
  const visibleMessages = useMemo(() => {
    if (!shouldCollapseMessageHistory || showAllMessages) return messages
    return messages.slice(-AI_CHAT_RECENT_MESSAGE_RENDER_LIMIT)
  }, [messages, shouldCollapseMessageHistory, showAllMessages])
  const hiddenMessageCount = messages.length - visibleMessages.length
  const selectedSkillCard = SKILL_CARDS.find((card) => card.id === skill && card.id !== 'general') || null
  const canSend = input.trim().length > 0 && effectiveModel && !isSending && !isCompressing
  const canManualCompressContext = !settingsLoading
    && !!(compressionModel || selectedModel)
    && !isSending
    && !isCompressing
    && getCompressibleMessages(messages).length >= 2
  const assistantAvatarMeta = useMemo<ChatAvatarMeta>(() => ({
    avatar: assistantAvatarUrl || DEFAULT_AI_CHAT_AVATAR,
    title: AI_CHAT_ASSISTANT_TITLE,
  }), [assistantAvatarUrl])
  const userAvatarMeta = useMemo<ChatAvatarMeta>(() => ({
    avatar: session?.user?.image || DEFAULT_USER_AVATAR,
    title: session?.user?.name || 'User',
  }), [session?.user?.image, session?.user?.name])
  const canImportTextFile = !isSending && !isCompressing && !isImportingDocument

  const applyAiChatSettingsSnapshot = useCallback((settings: AiChatSettingsSnapshot) => {
    setSelectedModel(settings.model)
    setCompressionModel(settings.compressionModel)
    setAssistantAvatarUrl(settings.assistantAvatarUrl)
    setRegenerateDeleteTrailingMessages(settings.regenerateDeleteTrailingMessages)
    setShowRegenerateConfirmDialog(settings.showRegenerateConfirmDialog)
  }, [])

  function markAiChatLocalSessionMutation() {
    localSessionMutationVersionRef.current += 1
  }

  const handleImportTextFile = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file || !canImportTextFile) return
    if (file.size > AI_CHAT_TEXT_FILE_MAX_BYTES) {
      setError('文档不能超过 5MB。请先截取关键段落再导入。')
      return
    }
    const formData = new FormData()
    formData.set('file', file)
    setIsImportingDocument(true)
    try {
      const response = await apiFetch('/api/ai-chat/import-document', {
        method: 'POST',
        body: formData,
      })
      const body = await readAiChatJson(response)
      if (!response.ok) throw createAiChatApiError(body, '文档文本提取失败。')
      const normalized = typeof body.text === 'string' ? body.text.trim() : ''
      if (!normalized) {
        setError('这个文档没有可导入的文本。')
        return
      }
      const clipped = Array.from(normalized).slice(0, AI_CHAT_TEXT_FILE_MAX_CHARS).join('')
      const suffix = body.truncated === true || clipped.length < normalized.length ? '\n\n[已截取前 12000 字]' : ''
      const block = `[导入文档：${file.name}]\n${clipped}${suffix}`
      setInput((current) => {
        const trimmed = current.trim()
        return trimmed ? `${trimmed}\n\n${block}` : block
      })
      setError('')
      window.requestAnimationFrame(() => {
        const textarea = composerTextareaRef.current
        if (!textarea) return
        resizeAiChatComposerTextarea(textarea)
        textarea.focus()
      })
    } catch (err) {
      setError(readAiChatErrorMessage(err, '文档文本提取失败。请换成 txt、md、html、docx 或可复制文本的 pdf。'))
    } finally {
      setIsImportingDocument(false)
    }
  }, [canImportTextFile])

  const handleSessionExpired = useCallback(() => {
    if (sessionExpiredHandledRef.current) return
    sessionExpiredHandledRef.current = true
    clearAiChatMemoryCaches()
    setError(AI_CHAT_SESSION_EXPIRED_MESSAGE)
    void clearAuthAndOpenSignIn(getPageLocale())
  }, [])

  useEffect(() => {
    if (!isAiChatClientMounted || sessionStatus !== 'unauthenticated') return
    // SSO 登录进行中，等待 SSO 完成，不触发 session expired
    if (typeof window !== 'undefined' && window.__ssoLoginPending) return
    handleSessionExpired()
  }, [handleSessionExpired, isAiChatClientMounted, sessionStatus])

  useLayoutEffect(() => {
    const shell = pageShellRef.current
    if (!isAiChatClientMounted || sessionStatus !== 'authenticated' || !shell || typeof window === 'undefined') return

    const nav = document.querySelector('nav')
    const updateShellHeight = () => {
      const navHeight = nav?.getBoundingClientRect().height || 64
      shell.style.setProperty('--ai-chat-nav-height', `${Math.ceil(navHeight)}px`)
    }
    let shellHeightFrame: number | null = null
    const scheduleShellHeightUpdate = () => {
      if (shellHeightFrame !== null) return
      shellHeightFrame = window.requestAnimationFrame(() => {
        shellHeightFrame = null
        updateShellHeight()
      })
    }

    scheduleShellHeightUpdate()

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleShellHeightUpdate) : null
    if (nav && resizeObserver) resizeObserver.observe(nav)
    window.addEventListener('resize', scheduleShellHeightUpdate)

    return () => {
      if (shellHeightFrame !== null) {
        window.cancelAnimationFrame(shellHeightFrame)
        shellHeightFrame = null
      }
      resizeObserver?.disconnect()
      window.removeEventListener('resize', scheduleShellHeightUpdate)
    }
  }, [isAiChatClientMounted, sessionStatus])

  useLayoutEffect(() => {
    const textarea = composerTextareaRef.current
    if (!textarea) return
    resizeAiChatComposerTextarea(textarea)
  }, [input])

  useLayoutEffect(() => {
    const sidebar = desktopSidebarRef.current
    const sessionList = desktopSessionListRef.current
    if (!isAiChatClientMounted || desktopSidebarContentMode === 'collapsed' || !sidebar || !sessionList || typeof window === 'undefined') {
      setDesktopSessionListMaxHeight(null)
      return
    }

    let frameId: number | null = null
    const updateSessionListMaxHeight = () => {
      const listRect = sessionList.getBoundingClientRect()
      const overlayRect = desktopSkillOverlayRef.current?.getBoundingClientRect() ?? null
      const sidebarRect = sidebar.getBoundingClientRect()
      const sidebarBottomPadding = 20
      const overlayGap = 4
      const availableHeight = overlayRect
        ? overlayRect.top - listRect.top - overlayGap
        : sidebarRect.bottom - sidebarBottomPadding - listRect.top
      const nextMaxHeight = Math.max(0, Math.min(352, Math.floor(availableHeight)))
      setDesktopSessionListMaxHeight((current) => (current === nextMaxHeight ? current : nextMaxHeight))
    }
    const scheduleSessionListMaxHeightUpdate = () => {
      if (frameId !== null) return
      frameId = window.requestAnimationFrame(() => {
        frameId = null
        updateSessionListMaxHeight()
      })
    }

    scheduleSessionListMaxHeightUpdate()

    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(scheduleSessionListMaxHeightUpdate) : null
    resizeObserver?.observe(sidebar)
    if (desktopSkillOverlayRef.current) resizeObserver?.observe(desktopSkillOverlayRef.current)
    window.addEventListener('resize', scheduleSessionListMaxHeightUpdate)

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
        frameId = null
      }
      resizeObserver?.disconnect()
      window.removeEventListener('resize', scheduleSessionListMaxHeightUpdate)
    }
  }, [desktopSidebarContentMode, isAiChatClientMounted, skill, quickVideoFirstFrameSource, imageTextReferenceUrls.length])

  const handleCollapseDesktopSidebar = useCallback(() => {
    setDesktopSidebarContentMode('collapsed')
    setDesktopSidebarCollapsed(true)
  }, [])

  const handleExpandDesktopSidebar = useCallback(() => {
    setDesktopSidebarContentMode('expanded')
    setDesktopSidebarCollapsed(false)
  }, [])

  const handleToggleMobileSessionPanelFromRail = useCallback(() => {
    setMobileSkillPanelOpen(false)
    setMobileSkillCardMenuOpen(false)
    setMobileComposerToolsOpen(false)
    setMobileSessionPanelOpen((isOpen) => !isOpen)
  }, [])

  useEffect(() => {
    if (skill !== 'quick-video') return
    setQuickVideoDuration(quickVideoDurationOptions[0] || 10)
  }, [quickVideoDurationOptions, skill])

  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !currentSessionUserId) {
      setSettingsLoading(false)
      return
    }
    if (aiChatBootstrapMode !== 'fallback') return

    const cachedSettings = readFreshAiChatSettingsMemoryCache(currentSessionUserId)
    if (cachedSettings) {
      applyAiChatSettingsSnapshot(cachedSettings)
      setSettingsLoading(false)
      return
    }

    let cancelled = false
    const controller = new AbortController()

    async function loadSettings() {
      setSettingsLoading(true)
      try {
        const response = await fetch('/api/ai-chat/settings', { cache: 'no-store', signal: controller.signal })
        const body = await readAiChatJson(response)
        if (!response.ok) throw createAiChatApiError(body, 'AI对话设置加载失败。')
        const settings = normalizeAiChatSettingsSnapshot(body.settings)
        writeAiChatSettingsMemoryCache(currentSessionUserId, settings)
        if (!cancelled) {
          applyAiChatSettingsSnapshot(settings)
        }
      } catch (err) {
        if (controller.signal.aborted) return
        if (!cancelled) {
          if (isAiChatSessionExpiredError(err)) {
            handleSessionExpired()
            return
          }
          setError(readAiChatErrorMessage(err, 'AI对话设置加载失败。'))
        }
      } finally {
        if (!cancelled) setSettingsLoading(false)
      }
    }

    void loadSettings()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [aiChatBootstrapMode, applyAiChatSettingsSnapshot, currentSessionUserId, handleSessionExpired, sessionStatus])

  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !currentSessionUserId) {
      localSessionsUserIdRef.current = ''
      setServerSessionsReady(false)
      lastLocalSessionsPayloadRef.current = ''
      lastLocalActiveSessionPayloadRef.current = ''
      lastServerSessionsPayloadRef.current = ''
      latestServerSavePayloadRef.current = ''
      pendingServerSessionsPayloadRef.current = ''
      return
    }
    if (localSessionsUserIdRef.current === currentSessionUserId) return

    localSessionsUserIdRef.current = currentSessionUserId
    setServerSessionsReady(false)
    lastLocalSessionsPayloadRef.current = ''
    lastLocalActiveSessionPayloadRef.current = ''
    lastServerSessionsPayloadRef.current = ''
    latestServerSavePayloadRef.current = ''
    pendingServerSessionsPayloadRef.current = ''

    lastServerSessionsPayloadRef.current = resetToEmptySessionState()
  }, [currentSessionUserId, sessionStatus])

  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !currentSessionUserId) {
      setAiChatBootstrapMode('pending')
      return
    }

    const cachedSettings = readFreshAiChatSettingsMemoryCache(currentSessionUserId)
    const cachedSessions = readFreshAiChatSessionsMemoryCache(currentSessionUserId)
    const cachedModels = readFreshAiChatModelsMemoryCache(currentSessionUserId)
    if (cachedSettings && cachedSessions && cachedModels) {
      applyAiChatSettingsSnapshot(cachedSettings)
      queryClient.setQueryData(queryKeys.userModels.all(), cachedModels)
      const nextSession = applySessionCollection(cachedSessions.sessions, cachedSessions.activeSessionId)
      const serializedPayload = writeAiChatSessionsMemoryCache(currentSessionUserId, cachedSessions.sessions, nextSession.id)
      lastServerSessionsPayloadRef.current = ''
      lastLocalSessionsPayloadRef.current = serializedPayload
      lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextSession)
      setSettingsLoading(false)
      setServerSessionsReady(true)
      setAiChatBootstrapMode('ready')
      return
    }

    const cachedBrowserBootstrap = readFreshAiChatBrowserBootstrapCache(currentSessionUserId)
    const cachedLocalSessions = readAiChatLocalSessionsCache(currentSessionUserId)
    const cachedBrowserSessions = cachedLocalSessions || (cachedBrowserBootstrap ? {
      sessions: cachedBrowserBootstrap.sessions,
      activeSessionId: cachedBrowserBootstrap.activeSessionId,
      serializedPayload: cachedBrowserBootstrap.serializedPayload,
      activeSessionPayload: '',
    } : null)
    const hasBrowserWarmStart = !!(cachedBrowserBootstrap || cachedBrowserSessions)
    if (cachedBrowserBootstrap) {
      applyAiChatSettingsSnapshot(cachedBrowserBootstrap.settings)
      queryClient.setQueryData(queryKeys.userModels.all(), cachedBrowserBootstrap.models)
      writeAiChatSettingsMemoryCache(currentSessionUserId, cachedBrowserBootstrap.settings)
      writeAiChatModelsMemoryCache(currentSessionUserId, cachedBrowserBootstrap.models)
      setSettingsLoading(false)
      setAiChatBootstrapMode('ready')
    }
    if (cachedBrowserSessions) {
      const nextSession = applySessionCollection(cachedBrowserSessions.sessions, cachedBrowserSessions.activeSessionId)
      const serializedPayload = writeAiChatSessionsMemoryCache(currentSessionUserId, cachedBrowserSessions.sessions, nextSession.id)
      lastServerSessionsPayloadRef.current = ''
      lastLocalSessionsPayloadRef.current = serializedPayload
      lastLocalActiveSessionPayloadRef.current = cachedBrowserSessions.activeSessionPayload || serializeAiChatActiveSessionStoragePayload(nextSession)
      setServerSessionsReady(true)
    }

    let cancelled = false
    const controller = new AbortController()
    const serverLoadStartedAtMutationVersion = localSessionMutationVersionRef.current

    async function loadAiChatBootstrap() {
      if (!hasBrowserWarmStart) {
        setAiChatBootstrapMode('pending')
        setSettingsLoading(true)
        setServerSessionsReady(false)
      } else if (!cachedBrowserBootstrap) {
        setAiChatBootstrapMode('pending')
        setSettingsLoading(true)
      }
      try {
        const response = await fetch('/api/ai-chat/bootstrap', { cache: 'no-store', signal: controller.signal })
        const body = await readAiChatJson(response)
        if (!response.ok) throw createAiChatApiError(body, 'AI对话首屏数据加载失败。')

        const settings = normalizeAiChatSettingsSnapshot(body.settings)
        const models = normalizeAiChatUserModelsPayload(body.models)
        writeAiChatSettingsMemoryCache(currentSessionUserId, settings)
        writeAiChatModelsMemoryCache(currentSessionUserId, models)

        if (cancelled) return
        applyAiChatSettingsSnapshot(settings)
        queryClient.setQueryData(queryKeys.userModels.all(), models)

        const loadedSessions = Array.isArray(body.sessions)
          ? body.sessions.map(normalizePersistedSession).filter(Boolean) as ChatSession[]
          : []
        if (localSessionMutationVersionRef.current === serverLoadStartedAtMutationVersion) {
          const cachedSessionsUpdatedAtMs = cachedBrowserSessions
            ? readChatSessionsLatestUpdatedAtMs(cachedBrowserSessions.sessions)
            : 0
          const loadedSessionsUpdatedAtMs = readChatSessionsLatestUpdatedAtMs(loadedSessions)
          if (cachedBrowserSessions && cachedSessionsUpdatedAtMs > loadedSessionsUpdatedAtMs) {
            const nextActiveId = typeof body.activeSessionId === 'string'
              ? body.activeSessionId
              : loadedSessions[0]?.id || ''
            lastServerSessionsPayloadRef.current = serializeAiChatSessionsPayload(loadedSessions, nextActiveId)
            setAiChatBootstrapMode('ready')
            setServerSessionsReady(true)
            return
          }
          if (loadedSessions.length > 0) {
            const nextActiveId = typeof body.activeSessionId === 'string'
              ? body.activeSessionId
              : loadedSessions[0].id
            const nextSession = applySessionCollection(loadedSessions, nextActiveId)
            lastServerSessionsPayloadRef.current = writeAiChatSessionsMemoryCache(currentSessionUserId, loadedSessions, nextSession.id)
            lastLocalSessionsPayloadRef.current = lastServerSessionsPayloadRef.current
            lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextSession)
            writeAiChatBrowserBootstrapCache(currentSessionUserId, settings, models, loadedSessions, nextSession.id)
          } else {
            lastServerSessionsPayloadRef.current = resetToEmptySessionState()
            const nextState = JSON.parse(lastServerSessionsPayloadRef.current) as { sessions: ChatSession[]; activeSessionId: string }
            writeAiChatSessionsMemoryCache(currentSessionUserId, nextState.sessions, nextState.activeSessionId)
            lastLocalSessionsPayloadRef.current = lastServerSessionsPayloadRef.current
            lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextState.sessions[0])
            writeAiChatBrowserBootstrapCache(currentSessionUserId, settings, models, nextState.sessions, nextState.activeSessionId)
          }
        }
        setAiChatBootstrapMode('ready')
        setServerSessionsReady(true)
      } catch (err) {
        if (controller.signal.aborted) return
        if (!cancelled) {
          if (isAiChatSessionExpiredError(err)) {
            handleSessionExpired()
            return
          }
          if (!hasBrowserWarmStart || !cachedBrowserBootstrap) {
            setAiChatBootstrapMode('fallback')
          }
        }
      } finally {
        if (!cancelled) setSettingsLoading(false)
      }
    }

    void loadAiChatBootstrap()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [applyAiChatSettingsSnapshot, currentSessionUserId, handleSessionExpired, queryClient, sessionStatus])

  useEffect(() => {
    if (sessionStatus !== 'authenticated' || !currentSessionUserId || aiChatBootstrapMode !== 'fallback') return

    let cancelled = false
    const controller = new AbortController()
    const serverLoadStartedAtMutationVersion = localSessionMutationVersionRef.current

    const cachedSessions = readFreshAiChatSessionsMemoryCache(currentSessionUserId)
    if (cachedSessions) {
      if (localSessionMutationVersionRef.current === serverLoadStartedAtMutationVersion) {
        const nextSession = applySessionCollection(cachedSessions.sessions, cachedSessions.activeSessionId)
        const serializedPayload = writeAiChatSessionsMemoryCache(currentSessionUserId, cachedSessions.sessions, nextSession.id)
        lastServerSessionsPayloadRef.current = ''
        lastLocalSessionsPayloadRef.current = serializedPayload
        lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextSession)
        setServerSessionsReady(true)
      }
      return () => {
        cancelled = true
        controller.abort()
      }
    }

    async function loadServerSessions() {
      try {
        const response = await fetch('/api/ai-chat/sessions', { cache: 'no-store', signal: controller.signal })
        const body = await readAiChatJson(response)
        if (!response.ok) throw createAiChatApiError(body, 'AI对话记录加载失败。')
        const loadedSessions = Array.isArray(body.sessions)
          ? body.sessions.map(normalizePersistedSession).filter(Boolean) as ChatSession[]
          : []
        if (!cancelled && loadedSessions.length > 0) {
          if (localSessionMutationVersionRef.current !== serverLoadStartedAtMutationVersion) return
          const nextActiveId = typeof body.activeSessionId === 'string'
            ? body.activeSessionId
            : loadedSessions[0].id
          const nextSession = applySessionCollection(loadedSessions, nextActiveId)
          lastServerSessionsPayloadRef.current = writeAiChatSessionsMemoryCache(currentSessionUserId, loadedSessions, nextSession.id)
          lastLocalSessionsPayloadRef.current = lastServerSessionsPayloadRef.current
          lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextSession)
        } else if (!cancelled) {
          if (localSessionMutationVersionRef.current !== serverLoadStartedAtMutationVersion) return
          lastServerSessionsPayloadRef.current = resetToEmptySessionState()
          const nextState = JSON.parse(lastServerSessionsPayloadRef.current) as { sessions: ChatSession[]; activeSessionId: string }
          writeAiChatSessionsMemoryCache(currentSessionUserId, nextState.sessions, nextState.activeSessionId)
          lastLocalSessionsPayloadRef.current = lastServerSessionsPayloadRef.current
          lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextState.sessions[0])
        }
      } catch (err) {
        if (controller.signal.aborted) return
        if (!cancelled) {
          if (isAiChatSessionExpiredError(err)) {
            handleSessionExpired()
            return
          }
          lastServerSessionsPayloadRef.current = resetToEmptySessionState()
          const nextState = JSON.parse(lastServerSessionsPayloadRef.current) as { sessions: ChatSession[]; activeSessionId: string }
          lastLocalSessionsPayloadRef.current = lastServerSessionsPayloadRef.current
          lastLocalActiveSessionPayloadRef.current = serializeAiChatActiveSessionStoragePayload(nextState.sessions[0])
          setError(readAiChatErrorMessage(err, 'AI对话记录加载失败。'))
        }
      } finally {
        if (!cancelled) setServerSessionsReady(true)
      }
    }

    void loadServerSessions()

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [aiChatBootstrapMode, currentSessionUserId, handleSessionExpired, sessionStatus])

  useEffect(() => {
    if (selectedModel || !modelOptions.length || settingsLoading) return
    setSelectedModel(modelOptions[0].value)
  }, [modelOptions, selectedModel, settingsLoading])

  useEffect(() => {
    activeSessionIdRef.current = activeSessionId
  }, [activeSessionId])

  useEffect(() => {
    setShowAllMessages(false)
  }, [activeSessionId])

  useEffect(() => {
    setSessions((current) => {
      let changed = false
      const nextSessions = current.map((session) => {
        if (session.id !== activeSessionId) return session
        const nextSession: ChatSession = {
          ...session,
          title: session.customTitle ? session.title : inferSessionTitle(messages),
          skill,
          messages,
          forkedFrom: cleanAiChatForkSourceForSession(session.id, session.forkedFrom),
          compressedSummary,
          quickVideoRatio,
          quickVideoDuration,
          quickVideoWatermarkEnabled,
          quickVideoFirstFrameUrl,
          quickVideoFirstFrameStorageKey,
          quickVideoFirstFrameName,
          imageTextRatio,
          imageTextPageCount,
          imageTextMode,
          imageTextWatermarkEnabled,
          imageTextReferenceUrls,
          imageTextReferenceNames,
          litePptPageCount,
          litePptVisualPrompt,
        }
        if (!hasActiveSessionUiChanges(session, nextSession)) return session
        changed = true
        return { ...nextSession, updatedAt: new Date().toISOString() }
      })
      return changed ? nextSessions : current
    })
  }, [
    activeSessionId,
    compressedSummary,
    imageTextMode,
    imageTextPageCount,
    imageTextReferenceNames,
    imageTextReferenceUrls,
    imageTextRatio,
    imageTextWatermarkEnabled,
    litePptPageCount,
    litePptVisualPrompt,
    messages,
    quickVideoDuration,
    quickVideoFirstFrameStorageKey,
    quickVideoFirstFrameName,
    quickVideoFirstFrameUrl,
    quickVideoRatio,
    quickVideoWatermarkEnabled,
    skill,
  ])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!currentSessionUserId || !serverSessionsReady) return
    return scheduleIdleTask(() => {
      writeAiChatSessionsMemoryCache(currentSessionUserId, sessions, activeSessionId)
      const serializedSessions = JSON.stringify({ sessions, activeSessionId })
      if (serializedSessions !== lastLocalSessionsPayloadRef.current) {
        lastLocalSessionsPayloadRef.current = serializedSessions
        window.localStorage.setItem(getChatSessionsStorageKey(currentSessionUserId), serializedSessions)
      }
      const activeSession = sessions.find((session) => session.id === activeSessionId)
      if (!activeSession) return
      const serializedActiveSession = serializeAiChatActiveSessionStoragePayload(activeSession, {
        skill,
        messages,
        compressedSummary,
        quickVideoRatio,
        quickVideoDuration,
        quickVideoWatermarkEnabled,
        quickVideoFirstFrameUrl,
        quickVideoFirstFrameStorageKey,
        quickVideoFirstFrameName,
        imageTextRatio,
        imageTextPageCount,
        imageTextMode,
        imageTextWatermarkEnabled,
        imageTextReferenceUrls,
        imageTextReferenceNames,
        litePptPageCount,
        litePptVisualPrompt,
      })
      if (serializedActiveSession !== lastLocalActiveSessionPayloadRef.current) {
        lastLocalActiveSessionPayloadRef.current = serializedActiveSession
        window.localStorage.setItem(getChatStorageKey(currentSessionUserId), serializedActiveSession)
      }
    }, AI_CHAT_LOCAL_PERSIST_IDLE_TIMEOUT_MS)
  }, [activeSessionId, compressedSummary, currentSessionUserId, imageTextMode, imageTextPageCount, imageTextReferenceNames, imageTextReferenceUrls, imageTextRatio, imageTextWatermarkEnabled, litePptPageCount, litePptVisualPrompt, messages, quickVideoDuration, quickVideoFirstFrameName, quickVideoFirstFrameStorageKey, quickVideoFirstFrameUrl, quickVideoRatio, quickVideoWatermarkEnabled, serverSessionsReady, sessions, skill])

  useEffect(() => {
    if (!serverSessionsReady || !currentSessionUserId) return
    let cancelled = false
    const timer = window.setTimeout(() => {
      const serializedPayload = writeAiChatSessionsMemoryCache(currentSessionUserId, sessions, activeSessionId)
      latestServerSavePayloadRef.current = serializedPayload
      if (
        serializedPayload === lastServerSessionsPayloadRef.current
        || serializedPayload === pendingServerSessionsPayloadRef.current
      ) return
      pendingServerSessionsPayloadRef.current = serializedPayload
      void fetch('/api/ai-chat/sessions', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: serializedPayload,
      }).then(async (response) => {
        const body = await readAiChatJson(response)
        if (!response.ok) throw createAiChatApiError(body, 'AI对话记录保存失败。')
        if (latestServerSavePayloadRef.current === serializedPayload) {
          lastServerSessionsPayloadRef.current = serializedPayload
        }
        if (pendingServerSessionsPayloadRef.current === serializedPayload) {
          pendingServerSessionsPayloadRef.current = ''
        }
        if (!cancelled && response.ok && (workspaceRefreshAfterSaveRef.current || hasAiChatWorkspaceWorkflow(sessions))) {
          workspaceRefreshAfterSaveRef.current = false
          invalidateAiChatWorkspaceProjects()
        }
      }).catch((err) => {
        if (pendingServerSessionsPayloadRef.current === serializedPayload) {
          pendingServerSessionsPayloadRef.current = ''
        }
        if (cancelled) return
        if (isAiChatSessionExpiredError(err)) {
          handleSessionExpired()
          return
        }
        setError(readAiChatErrorMessage(err, 'AI对话记录保存失败。'))
      })
    }, AI_CHAT_SERVER_SAVE_DELAY_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [activeSessionId, currentSessionUserId, handleSessionExpired, invalidateAiChatWorkspaceProjects, serverSessionsReady, sessions])

  const isMessageListNearBottom = useCallback((el: HTMLDivElement | null = messageListRef.current, thresholdPx = AI_CHAT_AUTO_SCROLL_BOTTOM_THRESHOLD_PX) => {
    if (!el) return true
    return el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx
  }, [])

  const updateMessageListAutoScrollState = useCallback(function updateMessageListAutoScrollState() {
    const el = messageListRef.current
    if (!el) return
    const currentScrollTop = el.scrollTop
    const previousScrollTop = lastMessageListScrollTopRef.current
    if (messageListAutoScrollLockedRef.current) {
      shouldAutoScrollMessagesRef.current = false
      if (!messageListUserInteractingRef.current && isMessageListNearBottom(el, AI_CHAT_AUTO_SCROLL_UNLOCK_THRESHOLD_PX)) {
        messageListAutoScrollLockedRef.current = false
        shouldAutoScrollMessagesRef.current = true
      }
      lastMessageListScrollTopRef.current = currentScrollTop
      return
    }
    if (currentScrollTop < previousScrollTop) {
      shouldAutoScrollMessagesRef.current = false
    } else if (isMessageListNearBottom(el)) {
      shouldAutoScrollMessagesRef.current = true
    }
    lastMessageListScrollTopRef.current = currentScrollTop
  }, [isMessageListNearBottom])

  const stopPendingMessageListAutoScroll = useCallback(() => {
    if (scrollFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollFrameRef.current)
      scrollFrameRef.current = null
    }
    if (scrollSettleTimerRef.current !== null) {
      window.clearTimeout(scrollSettleTimerRef.current)
      scrollSettleTimerRef.current = null
    }
  }, [])

  const beginMessageListUserInteraction = useCallback(() => {
    messageListAutoScrollLockedRef.current = true
    messageListUserInteractingRef.current = true
    shouldAutoScrollMessagesRef.current = false
    stopPendingMessageListAutoScroll()
    if (messageListInteractionReleaseTimerRef.current !== null) {
      window.clearTimeout(messageListInteractionReleaseTimerRef.current)
      messageListInteractionReleaseTimerRef.current = null
    }
  }, [stopPendingMessageListAutoScroll])

  const scheduleEndMessageListUserInteraction = useCallback((delayMs = 0) => {
    if (typeof window === 'undefined') return
    if (messageListInteractionReleaseTimerRef.current !== null) {
      window.clearTimeout(messageListInteractionReleaseTimerRef.current)
    }
    messageListInteractionReleaseTimerRef.current = window.setTimeout(() => {
      messageListInteractionReleaseTimerRef.current = null
      messageListUserInteractingRef.current = false
      updateMessageListAutoScrollState()
    }, delayMs)
  }, [updateMessageListAutoScrollState])

  const scrollMessagesToSafeBottom = useCallback((options?: { force?: boolean }) => {
    const force = options?.force === true
    if (!force && (messageListUserInteractingRef.current || !shouldAutoScrollMessagesRef.current)) return
    const el = messageListRef.current
    if (!el) return
    messageListAutoScrollLockedRef.current = false
    messageListUserInteractingRef.current = false
    el.scrollTop = el.scrollHeight
    lastMessageListScrollTopRef.current = el.scrollTop
    shouldAutoScrollMessagesRef.current = true
  }, [])

  const scheduleScrollMessagesToSafeBottom = useCallback((withSettle = false, options?: { force?: boolean }) => {
    const force = options?.force === true
    if (!force && (messageListUserInteractingRef.current || !shouldAutoScrollMessagesRef.current)) return
    if (scrollFrameRef.current === null) {
      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null
        scrollMessagesToSafeBottom({ force })
      })
    }
    if (!withSettle) return
    if (scrollSettleTimerRef.current !== null) window.clearTimeout(scrollSettleTimerRef.current)
    scrollSettleTimerRef.current = window.setTimeout(() => {
      scrollSettleTimerRef.current = null
      scrollMessagesToSafeBottom({ force })
    }, 220)
  }, [scrollMessagesToSafeBottom])

  useEffect(() => {
    const messageList = messageListRef.current
    if (!messageList) return
    lastMessageListScrollTopRef.current = messageList.scrollTop
    updateMessageListAutoScrollState()
    const handlePointerDown = () => beginMessageListUserInteraction()
    const handleWheel = () => {
      beginMessageListUserInteraction()
      scheduleEndMessageListUserInteraction(180)
    }
    const handleTouchStart = () => beginMessageListUserInteraction()
    const handlePointerRelease = () => scheduleEndMessageListUserInteraction()
    const handleTouchEnd = () => scheduleEndMessageListUserInteraction(120)
    messageList.addEventListener('scroll', updateMessageListAutoScrollState, { passive: true })
    messageList.addEventListener('pointerdown', handlePointerDown, { passive: true })
    messageList.addEventListener('wheel', handleWheel, { passive: true })
    messageList.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('pointerup', handlePointerRelease, { passive: true })
    window.addEventListener('pointercancel', handlePointerRelease, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    return () => {
      messageList.removeEventListener('scroll', updateMessageListAutoScrollState)
      messageList.removeEventListener('pointerdown', handlePointerDown)
      messageList.removeEventListener('wheel', handleWheel)
      messageList.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('pointerup', handlePointerRelease)
      window.removeEventListener('pointercancel', handlePointerRelease)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [beginMessageListUserInteraction, scheduleEndMessageListUserInteraction, updateMessageListAutoScrollState])

  useEffect(() => {
    const currentMessageCount = messages.length
    const currentLastMessageId = messages[messages.length - 1]?.id || ''
    const previousMessageCount = lastAutoScrollMessageCountRef.current
    const previousLastMessageId = lastAutoScrollLastMessageIdRef.current
    const shouldFollowMessageChange = currentMessageCount !== previousMessageCount
      || currentLastMessageId !== previousLastMessageId
      || !!streamingAssistantId

    lastAutoScrollMessageCountRef.current = currentMessageCount
    lastAutoScrollLastMessageIdRef.current = currentLastMessageId

    if (!shouldFollowMessageChange) return
    scheduleScrollMessagesToSafeBottom(true)
  }, [messages, scheduleScrollMessagesToSafeBottom, streamingAssistantId])

  useEffect(() => () => {
    stopPendingMessageListAutoScroll()
    if (messageListInteractionReleaseTimerRef.current !== null) window.clearTimeout(messageListInteractionReleaseTimerRef.current)
  }, [stopPendingMessageListAutoScroll])

  useEffect(() => {
    const runningImageTextMessages = messages.filter((message) => {
      if (message.workflow?.type !== 'image-text') return false
      if (message.workflow.status !== 'completed' && message.workflow.status !== 'failed') return true
      return message.workflow.status === 'failed'
        && !message.workflow.failureReason
        && message.content === IMAGE_TEXT_GENERIC_FAILURE_MESSAGE
    })
    if (!runningImageTextMessages.length) return

    let cancelled = false
    async function poll() {
      for (const message of runningImageTextMessages) {
        const workflow = message.workflow
        if (!workflow || workflow.type !== 'image-text') continue
        try {
          const response = await fetch(`/api/image-text/projects/${encodeURIComponent(workflow.projectId)}`, { cache: 'no-store' })
          const body = await response.json().catch(() => ({}))
          if (!response.ok) continue
          const project = toRecord(body.project)
          const pages = Array.isArray(project.pages) ? project.pages.map(toRecord) : []
          const projectStatus = readNonEmptyString(project.status)
          const pageFailureReason = pages
            .map((page) => readNonEmptyString(page.errorMessage)
              || readNonEmptyString(page.error)
              || readNonEmptyString(page.message)
              || readNonEmptyString(page.failureReason))
            .find(Boolean)
          const rawProjectFailureReason = readNonEmptyString(project.errorMessage)
            || readNonEmptyString(project.error)
            || readNonEmptyString(project.message)
            || readNonEmptyString(project.failureReason)
            || pageFailureReason
            || readNonEmptyString(body.errorMessage)
            || readNonEmptyString(body.error)
            || readNonEmptyString(body.message)
          const projectFailureReason = normalizeImageTextFailureReasonForChat(rawProjectFailureReason)
          const projectTerminal = isTerminalImageTextProjectStatus(projectStatus)
          const projectPageCount = typeof project.pageCount === 'number' && Number.isFinite(project.pageCount) ? project.pageCount : 0
          const plannedPageCount = Math.max(1, workflow.targetPageCount || projectPageCount || pages.length || imageTextPageCount)
          const pageSlots = projectTerminal && pages.length > 0
            ? pages
            : Array.from({ length: plannedPageCount }, (_, index) => pages[index] || {})
          const targetPageCount = Math.max(1, pageSlots.length)
          const pageImages = pageSlots
            .map((page: { imageUrl?: unknown }) => (typeof page.imageUrl === 'string' ? page.imageUrl.trim() : ''))
          const images = pageImages
            .filter(Boolean)
          const pageStatuses = pageSlots.map((page: { imageUrl?: unknown; status?: unknown }) => {
            if (typeof page.imageUrl === 'string' && page.imageUrl.trim()) return 'completed'
            if (page.status === 'failed') return 'failed'
            if (projectTerminal) return 'failed'
            if (page.status === 'queued' || page.status === 'generating' || page.status === 'running') return 'running'
            return 'pending'
          })
          const pageTerminal = pageStatuses.every((status) => status === 'completed' || status === 'failed')
          const terminal = projectTerminal || pageTerminal
          const failed = projectStatus === 'failed' || (terminal && images.length === 0)
          if (cancelled) return
          if (images.length > 0 || failed || pageStatuses.some((status) => status === 'running' || status === 'pending')) {
            setMessages((current) => current.map((item) => {
              if (item.id !== message.id || item.workflow?.type !== 'image-text') return item
              const nextStatus = terminal ? (failed ? 'failed' : 'completed') : 'running'
              return {
                ...item,
                content: failed
                  ? projectFailureReason
                    ? `图片生成失败：${projectFailureReason}`
                    : '图片生成失败，请进入项目查看原因。'
                  : terminal
                    ? projectStatus === 'partial'
                      ? `图片已生成 ${images.length}/${targetPageCount}，部分页面失败。`
                      : '图片已生成。'
                    : images.length > 0
                      ? `图片已生成 ${images.length}/${targetPageCount}，剩余图片生成中...`
                      : '图片生成中...',
                workflow: {
                  ...item.workflow,
                  status: nextStatus,
                  images,
                  pageImages,
                  targetPageCount,
                  pageStatuses,
                  ...(projectFailureReason ? { failureReason: projectFailureReason } : {}),
                },
              }
            }))
          }
        } catch {
          // 轮询失败不打断用户对话，下一轮继续。
        }
      }
    }

    let timer: number | null = null
    const stopPolling = () => {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
    const startPolling = () => {
      if (timer !== null || document.hidden) return
      void poll()
      timer = window.setInterval(() => {
        void poll()
      }, 4000)
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling()
        return
      }
      startPolling()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    startPolling()
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPolling()
    }
  }, [imageTextPageCount, messages])

  useEffect(() => {
    const runningQuickVideoMessages = messages.filter((message) => {
      const workflow = message.workflow
      if (workflow?.type !== 'quick-video' || workflow.status === 'failed') return false
      if (workflow.status !== 'completed') return true
      return !!workflow.videoUrl && !workflow.posterUrl && workflow.posterLookupCompleted !== true
    })
    if (!runningQuickVideoMessages.length) return

    let cancelled = false
    async function poll() {
      for (const message of runningQuickVideoMessages) {
        const workflow = message.workflow
        if (!workflow || workflow.type !== 'quick-video') continue
        try {
          let record: { status?: unknown; videoUrl?: unknown; posterUrl?: unknown; posterKey?: unknown; ratio?: unknown; progress?: unknown; message?: unknown } | null = null
          const response = await fetch(`/api/quick-video/${workflow.projectId}/screening-booth?episodeId=${encodeURIComponent(workflow.episodeId)}`, { cache: 'no-store' })
          const body = await response.json().catch(() => ({}))
          if (response.ok) {
            const results = Array.isArray(body?.results) ? body.results : []
            const target = results.find((item: { id?: unknown }) => typeof workflow.resultId === 'string' && item.id === workflow.resultId)
              || results.find((item: { status?: unknown; videoUrl?: unknown }) => item.status === 'completed' && typeof item.videoUrl === 'string' && item.videoUrl)
              || results.find((item: { status?: unknown }) => item.status === 'running')
              || results[0]
            if (target && typeof target === 'object') {
              record = target as { status?: unknown; videoUrl?: unknown; posterUrl?: unknown; posterKey?: unknown; ratio?: unknown; progress?: unknown; message?: unknown }
            }
          }
          const taskSnapshot =
            await fetchQuickVideoTaskSnapshot(workflow.taskId)
            || await fetchLatestQuickVideoTaskSnapshot(workflow)
          if ((!record && !taskSnapshot) || cancelled) continue

          const videoUrl = typeof record?.videoUrl === 'string' ? record.videoUrl.trim() : ''
          const posterUrl = typeof record?.posterUrl === 'string' && record.posterUrl.trim()
            ? record.posterUrl.trim()
            : typeof record?.posterKey === 'string' && record.posterKey.trim()
              ? record.posterKey.trim()
              : ''
          const ratio = typeof record?.ratio === 'string' && isQuickVideoRatio(record.ratio) ? record.ratio : ''
          const cardStatus = record?.status === 'completed'
            ? 'completed'
            : record?.status === 'failed' || record?.status === 'stopped'
              ? 'failed'
              : 'running'
          const status = taskSnapshot?.status === 'failed' && !videoUrl ? 'failed' : cardStatus
          const progress = typeof taskSnapshot?.progress === 'number'
            ? taskSnapshot.progress
            : typeof record?.progress === 'number'
              ? record.progress
              : undefined
          const statusMessage = taskSnapshot?.message || (typeof record?.message === 'string' ? record.message.trim() : '')

          setMessages((current) => current.map((item) => {
            if (item.id !== message.id || item.workflow?.type !== 'quick-video') return item
            const nextVideoUrl = videoUrl || item.workflow.videoUrl
            const nextPosterUrl = posterUrl || item.workflow.posterUrl
            const nextRatio = ratio || item.workflow.ratio
            const nextStatus = nextVideoUrl ? 'completed' : status
            return {
              ...item,
              content: nextStatus === 'completed'
                ? '视频已生成。'
                : nextStatus === 'failed'
                  ? `视频生成失败：${statusMessage || '请进入项目查看原因。'}`
                  : '视频生成中...',
              workflow: {
                ...item.workflow,
                status: nextStatus,
                ...(nextVideoUrl ? { videoUrl: nextVideoUrl } : {}),
                ...(nextPosterUrl ? { posterUrl: nextPosterUrl } : {}),
                ...(nextStatus === 'completed' ? { posterLookupCompleted: true } : {}),
                ...(nextRatio ? { ratio: nextRatio } : {}),
                ...(typeof progress === 'number' ? { progress } : {}),
                ...(statusMessage ? { message: statusMessage } : {}),
              },
            }
          }))
        } catch {
          // 轮询失败不打断用户对话，下一轮继续。
        }
      }
    }

    let timer: number | null = null
    const stopPolling = () => {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
    const startPolling = () => {
      if (timer !== null || document.hidden) return
      void poll()
      timer = window.setInterval(() => {
        void poll()
      }, 4000)
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling()
        return
      }
      startPolling()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    startPolling()
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPolling()
    }
  }, [messages])

  useEffect(() => {
    const runningLitePptMessages = messages.filter((message) => (
      message.workflow?.type === 'lite-ppt'
      && message.workflow.status !== 'failed'
      && (
        message.workflow.status !== 'completed'
        || !message.workflow.pageImages?.length
      )
    ))
    if (!runningLitePptMessages.length) return

    let cancelled = false
    async function poll() {
      for (const message of runningLitePptMessages) {
        const workflow = message.workflow
        if (!workflow || workflow.type !== 'lite-ppt') continue
        try {
          const taskSnapshotPromise = fetchAiChatTaskSnapshot(workflow.taskId).catch(() => null)
          const response = await fetch(`/api/lite-ppt/projects/${encodeURIComponent(workflow.projectId)}`, { cache: 'no-store' })
          const body = await response.json().catch(() => ({}))
          const taskSnapshot = await taskSnapshotPromise
          if ((!response.ok && !taskSnapshot) || cancelled) continue
          const project = response.ok ? toRecord(body.project) : {}
          const pages = Array.isArray(project.pages) ? project.pages.map(toRecord) : []
          const projectStatus = readNonEmptyString(project.status)
          const terminal = isTerminalLitePptProjectStatus(projectStatus)
          const pageCount = typeof project.pageCount === 'number' && Number.isFinite(project.pageCount)
            ? Math.max(1, Math.floor(project.pageCount))
            : workflow.targetPageCount || pages.length || 2
          const pageImages = pages
            .map((page) => readNonEmptyString(page.backgroundImageUrl))
            .filter(Boolean)
          const pageTextBoxes = pages.map((page) => normalizeLitePptTextBoxes(page.textBoxes))
          const allPagesFailed = pages.length > 0 && pages.every((page) => readNonEmptyString(page.status) === 'failed')
          const anyPageCompleted = pages.some((page) => readNonEmptyString(page.status) === 'completed')
          const pageFailureMessage = allPagesFailed
            ? friendlyAiChatWorkflowMessage(pages.map((page) => readNonEmptyString(page.errorMessage)).find(Boolean))
            : ''
          const failed = projectStatus === 'failed' || taskSnapshot?.status === 'failed' || allPagesFailed
          const completed = !failed && (projectStatus === 'completed' || taskSnapshot?.status === 'completed' || (terminal && anyPageCompleted))
          const progress = completed ? 100 : taskSnapshot?.progress
          const statusMessage = friendlyAiChatWorkflowMessage(taskSnapshot?.message) || pageFailureMessage

          setMessages((current) => current.map((item) => {
            if (item.id !== message.id || item.workflow?.type !== 'lite-ppt') return item
            const nextStatus = failed ? 'failed' : completed ? 'completed' : 'running'
            const nextContent = nextStatus === 'completed'
              ? 'AI PPT 已生成，可以进入项目调整文字位置/大小并导出 PPTX。'
              : nextStatus === 'failed'
                ? `AI PPT 生成失败：${statusMessage || '请进入项目查看原因。'}`
                : 'AI PPT 生成中...'
            const nextProgress = typeof progress === 'number' ? progress : item.workflow.progress
            const nextMessage = statusMessage
              || friendlyAiChatWorkflowMessage(item.workflow.message)
              || (nextStatus === 'failed' ? LITE_PPT_FAILED_FALLBACK_MESSAGE : '')
            const nextPageImages = pageImages.length > 0 ? pageImages : item.workflow.pageImages
            const nextPageTextBoxes = pageImages.length > 0 ? pageTextBoxes : item.workflow.pageTextBoxes
            const nextPreviewLookupCompleted = (nextStatus === 'completed' && response.ok && (projectStatus === 'completed' || pageImages.length > 0))
              || (nextStatus === 'failed' && response.ok)
              ? true
              : item.workflow.previewLookupCompleted
            if (
              item.workflow.status === nextStatus
              && item.workflow.targetPageCount === pageCount
              && item.workflow.progress === nextProgress
              && item.workflow.message === nextMessage
              && item.workflow.pageImages === nextPageImages
              && item.workflow.pageTextBoxes === nextPageTextBoxes
              && item.workflow.previewLookupCompleted === nextPreviewLookupCompleted
              && item.content === nextContent
            ) {
              return item
            }
            return {
              ...item,
              content: nextContent,
              workflow: {
                ...item.workflow,
                status: nextStatus,
                targetPageCount: pageCount,
                ...(typeof nextProgress === 'number' ? { progress: nextProgress } : {}),
                ...(nextMessage ? { message: nextMessage } : {}),
                ...(nextPageImages?.length ? { pageImages: nextPageImages } : {}),
                ...(nextPageTextBoxes?.length ? { pageTextBoxes: nextPageTextBoxes } : {}),
                ...(nextPreviewLookupCompleted ? { previewLookupCompleted: true } : {}),
              },
            }
          }))
        } catch {
          // 轮询失败不打断用户对话，下一轮继续。
        }
      }
    }

    let timer: number | null = null
    const stopPolling = () => {
      if (timer !== null) {
        window.clearInterval(timer)
        timer = null
      }
    }
    const startPolling = () => {
      if (timer !== null || document.hidden) return
      void poll()
      timer = window.setInterval(() => {
        void poll()
      }, 4000)
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling()
        return
      }
      startPolling()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    startPolling()
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stopPolling()
    }
  }, [messages])

  function buildSkillContext() {
    if (skill === 'quick-video') {
      return `\n\n[技能卡片：AI短视频]\n视频比例：${quickVideoRatio}\n目标时长：${quickVideoDuration}秒\n水印：${quickVideoWatermarkEnabled ? '开' : '关'}${quickVideoFirstFrameSource ? '\n用户已上传首帧图' : ''}`
    }
    if (skill === 'image-text') {
      return `\n\n[技能卡片：AI图文]\n模式：${IMAGE_TEXT_MODES.find((mode) => mode.value === imageTextMode)?.label || '小红书模式'}\n默认画幅：${imageTextRatio}\n默认页数：${imageTextPageCount}页\n水印：${imageTextWatermarkEnabled ? '开' : '关'}${imageTextReferenceUrls.length ? `\n参考图：${imageTextReferenceUrls.length}张` : ''}`
    }
    if (skill === 'lite-ppt') {
      return `\n\n[技能卡片：AI PPT]\n默认画幅：16:9\n默认页数：${litePptPageCount}页${litePptVisualPrompt.trim() ? `\n视觉方向：${litePptVisualPrompt.trim()}` : ''}\n保留可编辑文字层和 PPTX 导出`
    }
    return ''
  }

  function unlockSkillCardAfterSubmit() {
    if (skill === 'general') return
    cancelSelectedSkillCard()
  }

  function cancelSelectedSkillCard() {
    setSkill('general')
    setMobileSkillCardMenuOpen(false)
    setMobileSkillPanelOpen(false)
    setMobileComposerToolsOpen(false)
  }

  function getCompressibleMessages(source: ChatMessage[]) {
    return source.filter((message) => (
      message.id !== 'welcome'
      && !isCompressionSummaryMessage(message)
      && !isCompressionFailureMessage(message)
    ))
  }

  function getManualCompressionStats(source: ChatMessage[]) {
    const compressible = getCompressibleMessages(source)
    return {
      totalMessages: compressible.length,
      totalChars: compressible.reduce((sum, message) => sum + message.content.length, 0),
    }
  }

  function applySession(session: ChatSession) {
    shouldAutoScrollMessagesRef.current = true
    setActiveSessionId(session.id)
    setSkill(session.skill || 'general')
    setMessages(session.messages ? removeWelcomeMessageAfterCompression(session.messages) : INITIAL_MESSAGES)
    setCompressedSummary(normalizeCompressedSummary(session.compressedSummary))
    setQuickVideoRatio(session.quickVideoRatio || '9:16')
    setQuickVideoDuration(session.quickVideoDuration || 10)
    setQuickVideoWatermarkEnabled(session.quickVideoWatermarkEnabled !== false)
    setQuickVideoFirstFrameUrl(session.quickVideoFirstFrameUrl || '')
    setQuickVideoFirstFrameStorageKey(session.quickVideoFirstFrameStorageKey || '')
    setQuickVideoFirstFrameName(session.quickVideoFirstFrameName || '')
    setQuickVideoFirstFrameError('')
    setImageTextRatio(session.imageTextRatio || '9:16')
    setImageTextPageCount(session.imageTextPageCount || 2)
    setImageTextMode(session.imageTextMode || 'basic')
    setImageTextWatermarkEnabled(session.imageTextWatermarkEnabled !== false)
    setImageTextReferenceUrls(session.imageTextReferenceUrls || [])
    setImageTextReferenceNames(session.imageTextReferenceNames || [])
    setImageTextReferenceError('')
    setLitePptPageCount(session.litePptPageCount || 2)
    setLitePptVisualPrompt(session.litePptVisualPrompt || '')
    setError('')
  }

  function applySessionCollection(nextSessions: ChatSession[], activeId: string) {
    const nextSession = nextSessions.find((session) => session.id === activeId) || nextSessions[0]
    setSessions(nextSessions)
    applySession(nextSession)
    return nextSession
  }

  function resetToEmptySessionState() {
    const emptySession = createEmptySession()
    setSessions([emptySession])
    applySession(emptySession)
    return JSON.stringify({ sessions: [emptySession], activeSessionId: emptySession.id })
  }

	  function handleCreateSession() {
	    markAiChatLocalSessionMutation()
	    const session = createEmptySession()
    setSessions((current) => [session, ...current])
    applySession(session)
  }

	  function handleDeleteSession(sessionId: string) {
	    markAiChatLocalSessionMutation()
	    if (sessionId === renamingSessionId) {
      setRenamingSessionId('')
      setRenamingTitle('')
    }
    workspaceRefreshAfterSaveRef.current = true
    invalidateAiChatWorkspaceProjects()
    setSessions((current) => {
      const remaining = current.filter((session) => session.id !== sessionId)
      const nextSessions = remaining.length > 0 ? remaining : [createEmptySession()]
      if (sessionId === activeSessionIdRef.current) {
        window.setTimeout(() => applySession(nextSessions[0]), 0)
      }
      return nextSessions
    })
  }

  function startRenameSession(session: ChatSession) {
    setRenamingSessionId(session.id)
    setRenamingTitle(session.title)
  }

  function cancelRenameSession() {
    setRenamingSessionId('')
    setRenamingTitle('')
  }

	  function commitRenameSession(sessionId: string) {
	    const nextTitle = renamingTitle.trim()
	    cancelRenameSession()
	    if (!nextTitle) return
	    markAiChatLocalSessionMutation()
	    workspaceRefreshAfterSaveRef.current = true
    invalidateAiChatWorkspaceProjects()
    setSessions((current) => current.map((item) => (
      item.id === sessionId
        ? { ...item, title: nextTitle, customTitle: true, updatedAt: new Date().toISOString() }
        : item
    )))
  }

  function renderSessionItems(options?: { compact?: boolean }) {
    const compact = options?.compact === true
    return sessions.map((session) => {
      const active = session.id === activeSessionId
      const renaming = session.id === renamingSessionId
      const itemPadding = compact ? 'pr-[4.5rem]' : 'pr-[5.75rem]'
      const actionBase = compact
	        ? 'flex h-7 w-7 items-center justify-center rounded-lg border bg-white hover:bg-blue-50'
	        : 'flex h-7 w-7 items-center justify-center rounded-lg border bg-white opacity-0 group-hover:opacity-100'
      return (
        <div key={session.id} className="group relative rounded-xl">
          {renaming ? (
            <div
	              className={`min-w-0 rounded-xl px-3 py-2 ${itemPadding} text-left ${
                active
                  ? 'border-2 border-blue-300 bg-blue-50 text-blue-700 shadow-none'
                  : 'border border-blue-200 bg-white text-blue-700'
              }`}
            >
              <input
                value={renamingTitle}
                onChange={(event) => setRenamingTitle(event.target.value)}
                onBlur={() => commitRenameSession(session.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    commitRenameSession(session.id)
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault()
                    cancelRenameSession()
                  }
                }}
                onClick={(event) => event.stopPropagation()}
                autoFocus
                className="h-6 w-full rounded-md border border-blue-200 bg-white px-2 text-xs font-black text-blue-700 outline-none focus:border-blue-400"
                aria-label="对话标题"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => {
                applySession(session)
              }}
	              className={`min-w-0 w-full rounded-xl px-3 py-2 ${itemPadding} text-left ${
                active
                  ? 'border-2 border-blue-300 bg-blue-50 text-blue-700 shadow-none'
                  : 'border border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
              }`}
            >
              <span className="block truncate text-xs font-black">{session.title}</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => startRenameSession(session)}
            className={`absolute ${compact ? 'right-10' : 'right-11'} top-1/2 -translate-y-1/2 border-blue-200 text-blue-600 ${actionBase}`}
            aria-label="修改标题"
          >
            <AppIcon name="edit" className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => handleDeleteSession(session.id)}
            className={`absolute ${compact ? 'right-2' : 'right-3'} top-1/2 -translate-y-1/2 border-red-100 text-red-500 ${actionBase.replace('hover:bg-blue-50', 'hover:bg-red-50')}`}
            aria-label="删除对话"
          >
            <AppIcon name="trash" className="h-3.5 w-3.5" />
          </button>
        </div>
      )
    })
  }

  async function uploadImageToTemp(file: File, type?: string) {
    const imageBase64 = await readImageFileAsDataUrl(file)
    const response = await fetch('/api/asset-hub/upload-temp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageBase64, ...(type ? { type } : {}) }),
    })
    const body = await readAiChatJson(response)
    if (!response.ok) throw createAiChatApiError(body, '上传失败')
    const url = typeof body.url === 'string' ? body.url.trim() : ''
    const key = typeof body.key === 'string' ? body.key.trim() : ''
    if (!url) throw new Error('上传失败')
    return { url, key }
  }

  async function validateQuickVideoFirstFrame(file: File) {
    if (!file.type.startsWith('image/')) throw new Error('请上传图片文件')
    if (file.size > QUICK_VIDEO_USER_FIRST_FRAME_MAX_BYTES) {
      throw new Error(`用户首帧图片不能超过 ${formatFileSize(QUICK_VIDEO_USER_FIRST_FRAME_MAX_BYTES)}`)
    }
    const { width, height } = await readImageDimensions(file)
    if (width < QUICK_VIDEO_USER_FIRST_FRAME_MIN_EDGE || height < QUICK_VIDEO_USER_FIRST_FRAME_MIN_EDGE) {
      throw new Error(`用户首帧图片宽高都不能低于 ${QUICK_VIDEO_USER_FIRST_FRAME_MIN_EDGE}px`)
    }
    if (width > QUICK_VIDEO_USER_FIRST_FRAME_MAX_EDGE || height > QUICK_VIDEO_USER_FIRST_FRAME_MAX_EDGE) {
      throw new Error(`用户首帧图片宽高都不能超过 ${QUICK_VIDEO_USER_FIRST_FRAME_MAX_EDGE}px`)
    }
    const targetRatio = parseAspectRatioValue(quickVideoRatio)
    if (targetRatio) {
      const drift = Math.abs((width / height) - targetRatio) / targetRatio
      if (drift > QUICK_VIDEO_USER_FIRST_FRAME_RATIO_TOLERANCE) {
        throw new Error(`用户首帧比例需接近 ${quickVideoRatio}，当前约为 ${detectVideoRatio(width, height)}`)
      }
    }
  }

  async function handleQuickVideoFirstFrameChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file || quickVideoFirstFrameUploading || isSending) return
    setQuickVideoFirstFrameError('')
    setQuickVideoFirstFrameUploading(true)
    try {
      await validateQuickVideoFirstFrame(file)
      const uploaded = await uploadImageToTemp(file, 'quick-video-user-first-frame')
      setQuickVideoFirstFrameUrl(uploaded.url)
      setQuickVideoFirstFrameStorageKey(uploaded.key || '')
      setQuickVideoFirstFrameName(file.name)
    } catch (err) {
      if (isAiChatSessionExpiredError(err)) {
        handleSessionExpired()
        return
      }
      setQuickVideoFirstFrameError(readAiChatErrorMessage(err, '上传失败，请重试'))
    } finally {
      setQuickVideoFirstFrameUploading(false)
    }
  }

  async function handleImageTextReferenceChange(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files || [])
    event.currentTarget.value = ''
    if (!files.length || imageTextReferenceUploading || isSending) return
    const remaining = IMAGE_TEXT_REFERENCE_LIMIT - imageTextReferenceUrls.length
    if (remaining <= 0) {
      setImageTextReferenceError(`参考图最多 ${IMAGE_TEXT_REFERENCE_LIMIT} 张`)
      return
    }
    const imageFiles = files.filter((file) => file.type.startsWith('image/')).slice(0, remaining)
    if (!imageFiles.length) {
      setImageTextReferenceError('请上传图片文件')
      return
    }
    setImageTextReferenceError('')
    setImageTextReferenceUploading(true)
    try {
      const uploaded = await Promise.all(imageFiles.map((file) => uploadImageToTemp(file)))
      const urls = uploaded.map((item) => item.url)
      setImageTextReferenceUrls((current) => [...current, ...urls].slice(0, IMAGE_TEXT_REFERENCE_LIMIT))
      setImageTextReferenceNames((current) => [...current, ...imageFiles.map((file) => file.name)].slice(0, IMAGE_TEXT_REFERENCE_LIMIT))
    } catch (err) {
      if (isAiChatSessionExpiredError(err)) {
        handleSessionExpired()
        return
      }
      setImageTextReferenceError(readAiChatErrorMessage(err, '上传失败，请重试'))
    } finally {
      setImageTextReferenceUploading(false)
    }
  }

  function handleImageTextResultForQuickVideo(imageUrl: string) {
    if (!imageUrl) return
    setQuickVideoFirstFrameUrl(imageUrl)
    setQuickVideoFirstFrameStorageKey('')
    setQuickVideoFirstFrameName('AI图文生成图')
    setQuickVideoFirstFrameError('')
    setPreviewImageUrl(null)
    setPreviewImageQuickVideoUrl(null)
    if (isQuickVideoRatio(imageTextRatio)) setQuickVideoRatio(imageTextRatio)
    setSkill('quick-video')
  }

  function hasAiChatWorkspaceWorkflow(source: ChatSession[]) {
    return source.some((chatSession) => (
      chatSession.messages || []
    ).some((message) => (
      message.workflow?.type === 'image-text' || message.workflow?.type === 'quick-video'
      || message.workflow?.type === 'lite-ppt'
    )))
  }

  async function compactContextMessages(
    messagesToCompress: ChatMessage[],
    recent: ChatMessage[],
    mode: 'manual',
    options: CompressionOptions = { limitMode: 'start', maxChars: DEFAULT_MANUAL_COMPRESS_CHAR_COUNT },
    baseSummary = compressedSummary,
  ): Promise<{ messages: ChatMessage[]; summary: string }> {
    setIsCompressing(true)
    try {
      const failurePrefix = '手动压缩失败。'
      let response: Response
      try {
        response = await fetch('/api/ai-chat/compress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            model: compressionModel || selectedModel,
            summary: baseSummary,
            messages: messagesToCompress.map(({ role, content }) => ({ role, content })),
            limitMode: options.limitMode,
            maxChars: options.maxChars,
            stream: true,
          }),
        })
      } catch (err) {
        throw createAiChatNetworkError(err, failurePrefix)
      }
      const body = await readAiChatCompressionBody(response, failurePrefix)

      const summary = normalizeCompressedSummary(readNonEmptyString(body.summary))
      if (!summary) throw new Error('手动压缩没有返回摘要。')

      setCompressedSummary(summary)
      const compactedMessages = [
        createCompressionSummaryMessage(summary, mode, {
          messageCount: messagesToCompress.length,
          sentChars: readFiniteNumber(body.sentChars),
          summaryChars: summary.length,
        }),
        ...recent,
      ]
      setMessages(compactedMessages)
      return { messages: compactedMessages, summary }
    } finally {
      setIsCompressing(false)
    }
  }

	  async function handleManualCompressContext(options: CompressionOptions): Promise<boolean> {
	    if (isSending || isCompressing) return false
    const messagesToCompress = getCompressibleMessages(messages)
    if (messagesToCompress.length < 2) {
      const message = '当前对话还没有足够上下文可压缩。'
      setCompressionOptionsError(message)
      setError(message)
      return false
    }

	    markAiChatLocalSessionMutation()
	    shouldAutoScrollMessagesRef.current = true
    setError('')
    setCompressionOptionsError('')
    try {
      await compactContextMessages(messagesToCompress, [], 'manual', options)
      scheduleScrollMessagesToSafeBottom(true, { force: true })
      return true
    } catch (err) {
      if (isAiChatSessionExpiredError(err)) {
        handleSessionExpired()
        return false
      }
      const message = readAiChatErrorMessage(err, '手动压缩失败。')
      setError(message)
      setCompressionOptionsError(message)
      return false
    }
  }

  function handleOpenManualCompressionOptions() {
    if (!canManualCompressContext) return
    const manualStats = getManualCompressionStats(messages)
    const defaults = getDefaultManualCompressionOptions(manualStats.totalChars)
    setCompressionOptionsError('')
    setManualCompressionLimitMode(defaults.limitMode)
    setManualCompressionMaxChars(String(defaults.maxChars || DEFAULT_MANUAL_COMPRESS_CHAR_COUNT))
    setCompressionOptionsOpen(true)
  }

  async function handleSubmitManualCompressionOptions() {
    if (isCompressing) return
    let maxChars: number | undefined
    if (manualCompressionLimitMode !== 'unlimited') {
      maxChars = Number(manualCompressionMaxChars)
      if (!Number.isFinite(maxChars) || maxChars <= 0) {
        setCompressionOptionsError('请输入大于 0 的压缩字数。')
        return
      }
      if (maxChars > MAX_MANUAL_COMPRESS_CHAR_COUNT) {
        setCompressionOptionsError(`压缩字数最多 ${MAX_MANUAL_COMPRESS_CHAR_COUNT}，超长内容请分段处理。`)
        return
      }
    } else {
      const stats = getManualCompressionStats(messages)
      if (stats.totalChars > MAX_MANUAL_COMPRESS_CHAR_COUNT) {
        setCompressionOptionsError(`完整压缩超过 ${MAX_MANUAL_COMPRESS_CHAR_COUNT} 字，请选择“最开始”或“最近”。`)
        return
      }
    }

    const ok = await handleManualCompressContext({
      limitMode: manualCompressionLimitMode,
      maxChars,
    })
    if (ok) setCompressionOptionsOpen(false)
  }

  function handleOpenCompressionSummaryEdit(message: ChatMessage, summary: string) {
    if (isSending || isCompressing) return
    setExpandedCompressionSummaryIds((current) => {
      const next = new Set(current)
      next.add(message.id)
      return next
    })
    setEditingCompressionSummaryId(message.id)
    setEditingCompressionSummaryText(summary)
    setEditingCompressionSummaryError('')
  }

  function handleCancelCompressionSummaryEdit() {
    setEditingCompressionSummaryId('')
    setEditingCompressionSummaryText('')
    setEditingCompressionSummaryError('')
  }

	  function handleSaveCompressionSummaryEdit(message: ChatMessage) {
	    if (isSending || isCompressing) return
    const nextSummary = normalizeCompressedSummary(editingCompressionSummaryText)
    if (!nextSummary) {
      setEditingCompressionSummaryError('摘要内容不能为空。')
      return
    }
    const parsed = parseCompressionSummaryMessage(message)
	    const nextContent = parsed.heading
	      ? `${parsed.heading}\n\n摘要：${nextSummary}`
	      : `摘要：${nextSummary}`
	    markAiChatLocalSessionMutation()
	    setCompressedSummary(nextSummary)
    setMessages((current) => current.map((item) => (
      item.id === message.id ? { ...item, content: nextContent } : item
    )))
    handleCancelCompressionSummaryEdit()
  }

  function buildCurrentSkillOptions() {
    return {
      locale: document.documentElement?.lang || 'zh',
      aiChatSessionId: activeSessionId,
      aiChatTitle: activeSession?.title || '',
      ratio: quickVideoRatio,
      duration: quickVideoDuration,
      videoWatermarkEnabled: quickVideoWatermarkEnabled,
      userUploadedFirstFrameImageUrl: quickVideoFirstFrameSource,
      aspectRatio: imageTextRatio,
      pageCount: skill === 'lite-ppt' ? litePptPageCount : imageTextPageCount,
      litePptPageCount,
      litePptVisualPrompt,
      visualPrompt: skill === 'lite-ppt' ? litePptVisualPrompt : undefined,
      polishLevel: imageTextMode,
      imageWatermarkEnabled: imageTextWatermarkEnabled,
      referenceUrls: imageTextReferenceUrls,
    }
  }

  function toModelMessages(source: ChatMessage[]) {
    return source
      .filter((message) => (
        message.id !== 'welcome'
        && !isCompressionSummaryMessage(message)
        && !isCompressionFailureMessage(message)
      ))
      .map(({ role, content }) => ({ role, content }))
  }

  async function runAiChatTurn(
    requestMessages: ChatMessage[],
    activeSummary: string,
    options?: {
      failurePrefix?: string
      placement?: AssistantMessagePlacement
    },
  ) {
    const failurePrefix = options?.failurePrefix || '发送失败。'
    const placement = options?.placement || { type: 'append' as const }

    let streamingMessageId = ''
    let streamedAssistantContent = ''
    let pendingAssistantFlushTimer: number | null = null
    let flushStreamingAssistantContent: (() => void) | null = null
    let cancelStreamingAssistantFlush: (() => void) | null = null
    const abortController = new AbortController()
    aiChatRequestAbortControllerRef.current = abortController
    const assistantMessage = {
      ...createMessage('assistant', ''),
      ...(placement.type === 'replace' ? { id: placement.messageId } : {}),
    }
    const usageSessionId = activeSessionId || undefined
    const usageMessageId = assistantMessage.id

    try {
      const response = await fetch('/api/ai-chat/chat', {
        method: 'POST',
        signal: abortController.signal,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: effectiveModel,
          skill,
          skillOptions: buildCurrentSkillOptions(),
          summary: activeSummary,
          webSearchEnabled,
          sessionId: usageSessionId,
          messageId: usageMessageId,
          messages: toModelMessages(requestMessages),
        }),
      })
      if (!response.ok) {
        const body = await readAiChatJson(response)
        throw createAiChatApiError(body, failurePrefix)
      }

      const contentType = response.headers.get('content-type') || ''
      if (contentType.includes('text/event-stream')) {
        if (!response.body) throw new Error('流式响应不可读取。')

        let completedWeatherCard: AiChatWeatherCard | undefined
        let completedWorkflow: AiChatWorkflow | undefined
        let completedSources: AiChatTextSource[] = []
        streamingMessageId = assistantMessage.id
        setStreamingAssistantId(assistantMessage.id)
        setMessages((current) => (
          placement.type === 'replace'
            ? current.map((item) => (
              item.id === placement.messageId
                ? (() => {
                  const versions = getAiChatMessageVersions(item)
                  const next: ChatMessage = {
                    ...item,
                    content: '',
                    createdAt: assistantMessage.createdAt,
                    versions,
                    activeVersionIndex: normalizeAiChatVersionIndex(item.activeVersionIndex, versions.length),
                  }
                  delete next.sources
                  delete next.translation
                  delete next.weatherCard
                  return next
                })()
                : item
            ))
            : [...current, assistantMessage]
        ))

        const reader = response.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        let lastRenderedAssistantContent = ''

	        const renderAssistantContent = (content: string) => {
	          setMessages((current) => current.map((message) => (
	            message.id === assistantMessage.id
	              ? { ...message, content }
	              : message
	          )))
	        }

	        const applyProcessStep = (value: unknown) => {
	          const step = normalizeAiChatProcessStep(value)
	          if (!step) return
	          setMessages((current) => current.map((message) => (
	            message.id === assistantMessage.id
	              ? { ...message, steps: upsertAiChatProcessStep(message.steps, step) }
	              : message
	          )))
	        }

        cancelStreamingAssistantFlush = () => {
          if (pendingAssistantFlushTimer === null) return
          window.clearTimeout(pendingAssistantFlushTimer)
          pendingAssistantFlushTimer = null
        }

        flushStreamingAssistantContent = () => {
          cancelStreamingAssistantFlush?.()
          if (lastRenderedAssistantContent === streamedAssistantContent) return
          lastRenderedAssistantContent = streamedAssistantContent
          renderAssistantContent(streamedAssistantContent)
        }

        const scheduleAssistantContentFlush = () => {
          if (pendingAssistantFlushTimer !== null) return
          pendingAssistantFlushTimer = window.setTimeout(() => {
            pendingAssistantFlushTimer = null
            if (lastRenderedAssistantContent === streamedAssistantContent) return
            lastRenderedAssistantContent = streamedAssistantContent
            renderAssistantContent(streamedAssistantContent)
          }, AI_CHAT_STREAM_RENDER_THROTTLE_MS)
        }

        const appendDelta = (delta: string) => {
          if (!delta) return
          streamedAssistantContent += delta
          scheduleAssistantContentFlush()
        }

        while (true) {
          const { done, value } = await reader.read()
          buffer += decoder.decode(value || new Uint8Array(), { stream: !done })
          const blocks = buffer.split(/\r?\n\r?\n/)
          buffer = blocks.pop() || ''

          for (const block of blocks) {
            const parsed = parseAiChatStreamBlock(block)
            if (!parsed) continue
	            if (parsed.event === 'chunk') {
	              appendDelta(typeof parsed.data.delta === 'string' ? parsed.data.delta : '')
	            } else if (parsed.event === 'step') {
	              applyProcessStep(parsed.data)
	            } else if (parsed.event === 'done') {
              const weatherCard = parsed.data.weatherCard as AiChatWeatherCard | undefined
              const workflow = parsed.data.workflow as AiChatWorkflow | undefined
              const sources = normalizeAiChatTextSources(parsed.data.sources)
              completedWeatherCard = weatherCard
              completedWorkflow = workflow
              completedSources = sources
              if (weatherCard || workflow || sources.length) {
                flushStreamingAssistantContent?.()
                setMessages((current) => current.map((message) => (
                  message.id === assistantMessage.id
                    ? {
                      ...message,
                      ...(weatherCard ? { weatherCard } : {}),
                      ...(workflow ? { workflow } : {}),
                      ...(sources.length ? { sources } : {}),
                    }
                    : message
                )))
                if (workflow) invalidateAiChatWorkspaceProjects()
              }
            } else if (parsed.event === 'error') {
              throw new Error(typeof parsed.data.message === 'string' ? parsed.data.message : '发送失败。')
            }
          }

          if (done) break
        }

        if (buffer.trim()) {
          const parsed = parseAiChatStreamBlock(buffer)
	          if (parsed?.event === 'chunk') {
	            appendDelta(typeof parsed.data.delta === 'string' ? parsed.data.delta : '')
	          } else if (parsed?.event === 'step') {
	            applyProcessStep(parsed.data)
	          } else if (parsed?.event === 'done') {
            const weatherCard = parsed.data.weatherCard as AiChatWeatherCard | undefined
            const workflow = parsed.data.workflow as AiChatWorkflow | undefined
            const sources = normalizeAiChatTextSources(parsed.data.sources)
            completedWeatherCard = weatherCard
            completedWorkflow = workflow
            completedSources = sources
            if (weatherCard || workflow || sources.length) {
              flushStreamingAssistantContent?.()
              setMessages((current) => current.map((message) => (
                message.id === assistantMessage.id
                  ? {
                    ...message,
                    ...(weatherCard ? { weatherCard } : {}),
                    ...(workflow ? { workflow } : {}),
                    ...(sources.length ? { sources } : {}),
                  }
                  : message
              )))
              if (workflow) invalidateAiChatWorkspaceProjects()
            }
          } else if (parsed?.event === 'error') {
            throw new Error(typeof parsed.data.message === 'string' ? parsed.data.message : '发送失败。')
          }
        }

        flushStreamingAssistantContent?.()
        if (!streamedAssistantContent.trim()) {
          throw new Error('模型没有返回内容。')
        }
	        if (placement.type === 'replace') {
	          setMessages((current) => {
	            const next = current.map((item) => {
	              if (item.id !== placement.messageId) return item
	              const finalAssistantMessage: ChatMessage = {
	                ...assistantMessage,
	                content: streamedAssistantContent,
	                createdAt: new Date().toISOString(),
	                ...(completedWeatherCard ? { weatherCard: completedWeatherCard } : {}),
	                ...(completedWorkflow ? { workflow: completedWorkflow } : {}),
	                ...(completedSources.length ? { sources: completedSources } : {}),
	                ...(item.steps?.length ? { steps: item.steps } : {}),
	              }
	              return appendAiChatMessageVersion(item, finalAssistantMessage)
	            })
	            return next
	          })
	        } else {
	          setMessages((current) => {
	            const next = current.map((item) => (
	              item.id === assistantMessage.id
	                ? {
	                  ...item,
	                  content: streamedAssistantContent,
	                  ...(completedWeatherCard ? { weatherCard: completedWeatherCard } : {}),
	                  ...(completedWorkflow ? { workflow: completedWorkflow } : {}),
	                  ...(completedSources.length ? { sources: completedSources } : {}),
	                }
	                : item
	            ))
	            return next
	          })
	        }
	      } else {
        const body = await readAiChatJson(response)
        const workflow = body.workflow as AiChatWorkflow | undefined
        const sources = normalizeAiChatTextSources(body.sources)
        const assistantMessage = {
          ...createMessage(
            'assistant',
            typeof body.content === 'string' ? body.content : '',
            workflow,
            body.weatherCard as AiChatWeatherCard | undefined,
          ),
          ...(sources.length ? { sources } : {}),
          ...(placement.type === 'replace' ? { id: placement.messageId } : {}),
        }
	        setMessages((current) => (
	          (() => {
	            const next = placement.type === 'replace'
	              ? current.map((item) => item.id === placement.messageId ? appendAiChatMessageVersion(item, assistantMessage) : item)
	              : [...current, assistantMessage]
	            return next
	          })()
	        ))
        if (workflow) invalidateAiChatWorkspaceProjects()
      }
    } catch (err) {
      if (isAiChatAbortError(err)) {
        cancelStreamingAssistantFlush?.()
        flushStreamingAssistantContent?.()
        if (streamingMessageId) {
          setMessages((current) => current.map((item) => (
            item.id === streamingMessageId
              ? { ...item, content: `${(streamedAssistantContent || item.content).trim() || '已停止生成。'}\n\n[已停止生成]` }
              : item
          )))
        }
        return
      }
      if (isAiChatSessionExpiredError(err)) {
        handleSessionExpired()
        cancelStreamingAssistantFlush?.()
        return
      }
      const message = readAiChatErrorMessage(err, failurePrefix)
      if (message.includes(QUICK_VIDEO_USER_FIRST_FRAME_MISSING_MESSAGE)) {
        setQuickVideoFirstFrameUrl('')
        setQuickVideoFirstFrameStorageKey('')
        setQuickVideoFirstFrameName('')
        setQuickVideoFirstFrameError(QUICK_VIDEO_USER_FIRST_FRAME_MISSING_MESSAGE)
      }
      setError(message)
      cancelStreamingAssistantFlush?.()
      if (streamingMessageId) {
        const content = streamedAssistantContent
          ? `${streamedAssistantContent}\n\n请求失败：${message}`
          : `请求失败：${message}`
        setMessages((current) => current.map((item) => (
          item.id === streamingMessageId ? { ...item, content } : item
        )))
      } else {
        setMessages((current) => [...current, createMessage('assistant', `请求失败：${message}`)])
      }
    } finally {
      cancelStreamingAssistantFlush?.()
      if (aiChatRequestAbortControllerRef.current === abortController) {
        aiChatRequestAbortControllerRef.current = null
      }
      setStreamingAssistantId('')
      setIsSending(false)
    }
  }

  function handleStopAiChatGeneration() {
    aiChatRequestAbortControllerRef.current?.abort()
  }

	  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
	    event.preventDefault()
	    const text = input.trim()
	    if (!text || !effectiveModel || isSending || isCompressing || aiChatSubmitInFlightRef.current) return

	    aiChatSubmitInFlightRef.current = true
	    markAiChatLocalSessionMutation()
	    shouldAutoScrollMessagesRef.current = true
	    setIsSending(true)
	    setError('')

	    try {
	      const userMessage = createMessage('user', `${text}${buildSkillContext()}`)
	      const nextMessages = [...messages, userMessage]
	      setMessages(nextMessages)
	      setInput('')
	      unlockSkillCardAfterSubmit()
	      scheduleScrollMessagesToSafeBottom(true, { force: true })
	      await runAiChatTurn(nextMessages, compressedSummary)
	    } catch (err) {
	      if (isAiChatSessionExpiredError(err)) {
	        handleSessionExpired()
	        setIsSending(false)
	        return
	      }
	      const message = readAiChatErrorMessage(err, '发送失败。')
	      setError(message)
	      setIsSending(false)
	    } finally {
	      aiChatSubmitInFlightRef.current = false
	    }
	  }

  async function handleCopyMessage(message: ChatMessage) {
    const content = stripSkillContextFromMessageContent(message.content)
    if (!content) return
    const copied = await copyAiChatTextToClipboard(content)
    setMessageMenuId('')
    if (copied) {
      if (copiedMessageResetTimerRef.current !== null) {
        window.clearTimeout(copiedMessageResetTimerRef.current)
      }
      setCopiedMessageId(message.id)
      copiedMessageResetTimerRef.current = window.setTimeout(() => {
        setCopiedMessageId((current) => current === message.id ? '' : current)
        copiedMessageResetTimerRef.current = null
      }, AI_CHAT_COPY_FEEDBACK_DURATION_MS)
      setError('')
      setSelectCopyMessage(null)
      return
    }
    setError('')
    setSelectCopyMessage(message)
  }

  function handleOpenSelectCopy(message: ChatMessage) {
    setMessageMenuId('')
    setSelectCopyMessage(message)
  }

  function handleOpenMessagePreview(message: ChatMessage) {
    setMessageMenuId('')
    setPreviewMessage(message)
  }

  function downloadAiChatMessage(message: ChatMessage, format: 'markdown' | 'txt') {
    const content = stripSkillContextFromMessageContent(message.content)
    if (!content) return
    const messageList = messageListRef.current
    const scrollTopBeforeDownload = messageList?.scrollTop
    const activeElementBeforeDownload = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
    const extension = format === 'markdown' ? 'md' : 'txt'
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `xunbao-ai-chat-message-${new Date().toISOString().slice(0, 10)}.${extension}`
    link.tabIndex = -1
    link.style.position = 'fixed'
    link.style.left = '-9999px'
    link.style.top = '0'
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
    setMessageMenuId('')
    window.requestAnimationFrame(() => {
      if (messageList && typeof scrollTopBeforeDownload === 'number') {
        messageList.scrollTop = scrollTopBeforeDownload
      }
      if (activeElementBeforeDownload && document.contains(activeElementBeforeDownload)) {
        activeElementBeforeDownload.focus({ preventScroll: true })
      }
    })
  }

  function handleForkConversationAtMessage(message: ChatMessage) {
    const targetIndex = messages.findIndex((item) => item.id === message.id)
    if (targetIndex < 0 || !matchedActiveSession) return
    markAiChatLocalSessionMutation()
    const forkedAt = new Date().toISOString()
    const forkMessages = messages.slice(0, targetIndex + 1)
    const forkSession: ChatSession = {
      ...matchedActiveSession,
      id: createSessionId(),
      title: `${matchedActiveSession.title || inferSessionTitle(messages)} 分支`,
      customTitle: true,
      updatedAt: forkedAt,
      skill: 'general',
      messages: forkMessages,
      forkedFrom: {
        sessionId: matchedActiveSession.id,
        title: matchedActiveSession.title || inferSessionTitle(messages),
        messageId: message.id,
        createdAt: forkedAt,
      },
      compressedSummary,
    }
    setSessions((current) => [forkSession, ...current])
    applySession(forkSession)
    setMessageMenuId('')
  }

  function handleDeleteMessage(message: ChatMessage) {
    if (message.id === 'welcome' || isSending || isCompressing) return
    setMessages((current) => {
      const next = current.filter((item) => item.id !== message.id)
      return next.some((item) => item.role === 'user' || item.role === 'assistant') ? next : INITIAL_MESSAGES
    })
    setMessageMenuId('')
  }

  function handleSelectMessageVersion(message: ChatMessage, direction: -1 | 1) {
    if (message.id === 'welcome' || isSending || isCompressing) return
    const versions = getAiChatMessageVersions(message)
    if (versions.length <= 1) return
    const currentIndex = normalizeAiChatVersionIndex(message.activeVersionIndex, versions.length)
    const nextIndex = normalizeAiChatVersionIndex(currentIndex + direction, versions.length)
    if (nextIndex === currentIndex) return
    setMessages((current) => current.map((item) => (
      item.id === message.id
        ? applyAiChatMessageVersion(item, versions, nextIndex)
        : item
    )))
    setMessageMenuId('')
  }

  function handleDeleteCurrentMessageVersion(message: ChatMessage) {
    if (message.id === 'welcome' || isSending || isCompressing) return
    const versions = getAiChatMessageVersions(message)
    if (versions.length <= 1) {
      handleDeleteMessage(message)
      return
    }

    const currentIndex = normalizeAiChatVersionIndex(message.activeVersionIndex, versions.length)
    const nextVersions = versions.filter((_, index) => index !== currentIndex)
    const nextIndex = Math.min(currentIndex, nextVersions.length - 1)
    setMessages((current) => current.map((item) => {
      if (item.id !== message.id) return item
      if (nextVersions.length <= 1) {
        const [onlyVersion] = nextVersions
        if (!onlyVersion) return item
        const next: ChatMessage = {
          ...item,
          content: onlyVersion.content,
          createdAt: onlyVersion.createdAt || item.createdAt,
        }
        delete next.versions
        delete next.activeVersionIndex
        if (onlyVersion.sources) next.sources = onlyVersion.sources
        else delete next.sources
        if (onlyVersion.translation) next.translation = onlyVersion.translation
        else delete next.translation
        if (onlyVersion.weatherCard) next.weatherCard = onlyVersion.weatherCard
        else delete next.weatherCard
        return next
      }
      return applyAiChatMessageVersion(item, nextVersions, nextIndex)
    }))
    setMessageMenuId('')
  }

  async function handleTranslateMessage(message: ChatMessage, targetLanguage: string) {
    const content = stripSkillContextFromMessageContent(message.content)
    if (!content || translatingMessageId) return
    if (!selectedModel) {
      setError('请先在设置中心配置 AI对话模型。')
      return
    }
    setMessageMenuId('')
    setTranslatingMessageId(message.id)
    setError('')
    try {
      const response = await fetch('/api/ai-chat/translate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: selectedModel,
          text: content,
          targetLanguage,
        }),
      })
      const body = await readAiChatJson(response)
      if (!response.ok) throw createAiChatApiError(body, '翻译失败。')
      const translation = typeof body.translation === 'string' ? body.translation.trim() : ''
      if (!translation) throw new Error('翻译模型没有返回内容。')
      setMessages((current) => current.map((item) => (
        item.id === message.id
          ? (() => {
            const nextTranslation = { language: targetLanguage, content: translation }
            if (!item.versions?.length) return { ...item, translation: nextTranslation }
            const activeIndex = normalizeAiChatVersionIndex(item.activeVersionIndex, item.versions.length)
            return {
              ...item,
              translation: nextTranslation,
              versions: item.versions.map((version, index) => (
                index === activeIndex ? { ...version, translation: nextTranslation } : version
              )),
            }
          })()
          : item
      )))
    } catch (err) {
      if (isAiChatSessionExpiredError(err)) {
        handleSessionExpired()
        return
      }
      setError(readAiChatErrorMessage(err, '翻译失败。'))
    } finally {
      setTranslatingMessageId('')
    }
  }

  function handleSpeakMessage(message: ChatMessage) {
    const content = stripSkillContextFromMessageContent(message.content)
    if (!content || typeof window === 'undefined' || !('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') {
      setCanUseBrowserSpeech(false)
      setMessageMenuId('')
      return
    }
    if (speakingMessageId === message.id) {
      speechRunIdRef.current += 1
      window.speechSynthesis.cancel()
      setSpeakingMessageId('')
      return
    }
    const chunks = splitAiChatSpeechText(content)
    if (!chunks.length) return
    const runId = speechRunIdRef.current + 1
    speechRunIdRef.current = runId
    window.speechSynthesis.cancel()
    setSpeakingMessageId(message.id)
    setMessageMenuId('')
    setError('')

    const speakChunk = (index: number) => {
      if (speechRunIdRef.current !== runId) return
      const chunk = chunks[index]
      if (!chunk) {
        setSpeakingMessageId('')
        return
      }
      const utterance = new SpeechSynthesisUtterance(chunk)
      utterance.lang = /[\u4e00-\u9fff]/u.test(chunk) ? 'zh-CN' : 'en-US'
      utterance.rate = 1
      const voice = pickAiChatSpeechVoice(chunk)
      if (voice) utterance.voice = voice
      utterance.onend = () => speakChunk(index + 1)
      utterance.onerror = () => {
        if (speechRunIdRef.current !== runId) return
        setSpeakingMessageId('')
        setCanUseBrowserSpeech(false)
      }
      window.speechSynthesis.speak(utterance)
      window.setTimeout(() => {
        if (speechRunIdRef.current === runId && window.speechSynthesis.paused) {
          window.speechSynthesis.resume()
        }
      }, 60)
    }

    speakChunk(0)
  }

  function handleOpenEditMessage(message: ChatMessage) {
    if (isSending || isCompressing || skill !== 'general' || message.role !== 'user') return
    setMessageMenuId('')
    setEditingMessageId(message.id)
    setEditingMessageText(stripSkillContextFromMessageContent(message.content))
    setEditingMessageError('')
  }

  function handleCloseEditMessage() {
    if (isSending) return
    setEditingMessageId('')
    setEditingMessageText('')
    setEditingMessageError('')
  }

	  async function handleSubmitEditedMessage() {
	    if (!effectiveModel || isSending || isCompressing || skill !== 'general' || aiChatSubmitInFlightRef.current) return
    const text = editingMessageText.trim()
    if (!text) {
      setEditingMessageError('消息内容不能为空。')
      return
    }

    const targetIndex = messages.findIndex((message) => message.id === editingMessageId && message.role === 'user')
    if (targetIndex < 0) {
      setEditingMessageError('没有找到要编辑的消息。')
      return
    }

	    aiChatSubmitInFlightRef.current = true
	    markAiChatLocalSessionMutation()
	    shouldAutoScrollMessagesRef.current = true
	    setIsSending(true)
    setError('')
    setEditingMessageId('')
    setEditingMessageText('')
    setEditingMessageError('')

    const editedUserMessage: ChatMessage = {
      ...messages[targetIndex],
      content: text,
      createdAt: new Date().toISOString(),
    }
    const nextMessages = [
      ...messages.slice(0, targetIndex),
      appendAiChatMessageVersion(messages[targetIndex], editedUserMessage),
    ]
	    const activeSummary = readRetainedCompressionSummary(nextMessages)
	    try {
	      setMessages(nextMessages)
	      setCompressedSummary(activeSummary)
	      scheduleScrollMessagesToSafeBottom(true, { force: true })
	      await runAiChatTurn(nextMessages, activeSummary, { failurePrefix: '重新发送失败。' })
	    } catch (err) {
	      if (isAiChatSessionExpiredError(err)) {
	        handleSessionExpired()
	        setIsSending(false)
	        return
	      }
	      const message = readAiChatErrorMessage(err, '重新发送失败。')
	      setError(message)
	      setIsSending(false)
	    } finally {
	      aiChatSubmitInFlightRef.current = false
	    }
	  }

	  async function handleRegenerateAssistantMessage(message: ChatMessage, confirmed = false) {
	    if (!effectiveModel || isSending || isCompressing || skill !== 'general' || message.role !== 'assistant' || aiChatSubmitInFlightRef.current) return
    if (!confirmed && showRegenerateConfirmDialog) {
      setRegenerateConfirmMessage(message)
      setMessageMenuId('')
      setError('')
      return
    }
    const targetIndex = messages.findIndex((item) => item.id === message.id)
    if (targetIndex <= 0) {
      setError('没有找到可重新生成的上下文。')
      return
    }
    const contextMessages = messages.slice(0, targetIndex)
    const hasUserContext = contextMessages.some((item) => item.role === 'user')
    if (!hasUserContext) {
      setError('重新生成需要先有用户消息。')
      return
    }

	    aiChatSubmitInFlightRef.current = true
	    markAiChatLocalSessionMutation()
	    shouldAutoScrollMessagesRef.current = true
    setIsSending(true)
    setError('')
    if (regenerateDeleteTrailingMessages) {
      setMessages(contextMessages)
    }
    scheduleScrollMessagesToSafeBottom(true, { force: true })
	    try {
	      await runAiChatTurn(contextMessages, compressedSummary, {
	        failurePrefix: '重新生成失败。',
	        placement: regenerateDeleteTrailingMessages ? { type: 'append' } : { type: 'replace', messageId: message.id },
	      })
	    } finally {
	      aiChatSubmitInFlightRef.current = false
	    }
	  }

  async function confirmRegenerateAssistantMessage() {
    const pendingMessage = regenerateConfirmMessage
    if (!pendingMessage || isSending || isCompressing) return
    setRegenerateConfirmMessage(null)
    const latestMessage = messages.find((message) => message.id === pendingMessage.id) || pendingMessage
    await handleRegenerateAssistantMessage(latestMessage, true)
  }

  const renderMobileSkillSettings = () => {
    if (skill === 'quick-video') {
      return (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-black text-[var(--glass-text-primary)]">AI短视频参数</div>
            <div className="mt-1 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">首帧、比例和时长会随当前对话保存。</div>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3">
            <div className="flex items-start gap-3">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-blue-100 bg-white">
                {quickVideoFirstFrameSource ? (
                  <img src={toDisplayImageUrl(quickVideoFirstFrameDisplayUrl) || quickVideoFirstFrameDisplayUrl} alt="用户首帧图" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                ) : (
                  <AppIcon name="image" className="h-6 w-6 text-blue-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-black text-[var(--glass-text-primary)]">用户首帧图</div>
                <div className="mt-1 line-clamp-2 text-[11px] font-semibold leading-4 text-[var(--glass-text-tertiary)]">
                  {quickVideoFirstFrameSource ? (quickVideoFirstFrameName || '已上传图片') : `跟随比例 ${quickVideoRatio}，3MB内`}
                </div>
                <div className="mt-2 flex gap-2">
                  {quickVideoFirstFrameSource ? (
                    <button
                      type="button"
                      onClick={() => {
                        setQuickVideoFirstFrameUrl('')
                        setQuickVideoFirstFrameStorageKey('')
                        setQuickVideoFirstFrameName('')
                        setQuickVideoFirstFrameError('')
                      }}
                      disabled={isSending}
                      className="h-8 flex-1 rounded-lg border border-blue-100 bg-white text-xs font-black text-[var(--glass-text-secondary)] disabled:opacity-50"
                    >
                      移除
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => quickVideoFirstFrameInputRef.current?.click()}
                    disabled={quickVideoFirstFrameUploading || isSending}
                    className="h-8 flex-1 rounded-lg bg-blue-600 px-2 text-xs font-black text-white disabled:opacity-50"
                  >
                    {quickVideoFirstFrameUploading ? '上传中' : quickVideoFirstFrameSource ? '更换' : '上传'}
                  </button>
                </div>
              </div>
            </div>
            {quickVideoFirstFrameError ? <p className="mt-2 text-xs font-bold text-red-500">{quickVideoFirstFrameError}</p> : null}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <select value={quickVideoRatio} onChange={(event) => setQuickVideoRatio(event.target.value as typeof quickVideoRatio)} className="h-10 rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
              {QUICK_VIDEO_RATIOS.map((ratio) => <option key={ratio} value={ratio}>{ratio}</option>)}
            </select>
            <select value={quickVideoDuration} onChange={(event) => setQuickVideoDuration(Number(event.target.value))} className="h-10 rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
              {quickVideoDurationOptions.map((seconds) => <option key={seconds} value={seconds}>{seconds}秒</option>)}
            </select>
          </div>
          <label className="flex h-10 cursor-pointer items-center justify-between rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold text-[#111827]">
            <span>加水印</span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-[var(--glass-text-secondary)]">{quickVideoWatermarkEnabled ? '开' : '关'}</span>
              <input
                type="checkbox"
                checked={quickVideoWatermarkEnabled}
                onChange={(event) => setQuickVideoWatermarkEnabled(event.target.checked)}
                className="peer sr-only"
              />
              <span aria-hidden="true" className="relative h-5 w-9 rounded-full bg-slate-300 transition peer-checked:bg-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-[#eee9fb] after:shadow-sm after:transition after:content-[''] peer-checked:after:translate-x-4" />
            </span>
          </label>
        </div>
      )
    }

    if (skill === 'image-text') {
      return (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-black text-[var(--glass-text-primary)]">AI图文参数</div>
            <div className="mt-1 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">参考图、模式、画幅和页数会随当前对话保存。</div>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-black text-[var(--glass-text-primary)]">参考图 {imageTextReferenceUrls.length}/{IMAGE_TEXT_REFERENCE_LIMIT}</span>
              <button
                type="button"
                onClick={() => imageTextReferenceInputRef.current?.click()}
                disabled={imageTextReferenceUploading || isSending || imageTextReferenceUrls.length >= IMAGE_TEXT_REFERENCE_LIMIT}
                className="h-8 rounded-lg bg-blue-600 px-3 text-xs font-black text-white disabled:opacity-50"
              >
                {imageTextReferenceUploading ? '上传中' : '上传图片'}
              </button>
            </div>
            {imageTextReferenceUrls.length ? (
              <div className="mt-2 grid grid-cols-5 gap-2">
                {imageTextReferenceUrls.map((url, index) => (
                  <button
                    key={`${url}-${index}`}
                    type="button"
                    onClick={() => {
                      setImageTextReferenceUrls((current) => current.filter((_, itemIndex) => itemIndex !== index))
                      setImageTextReferenceNames((current) => current.filter((_, itemIndex) => itemIndex !== index))
                    }}
                    className="relative aspect-square overflow-hidden rounded-xl border border-blue-100 bg-white"
                    title={imageTextReferenceNames[index] || '点击移除'}
                  >
                    <img src={toDisplayImageUrl(url) || url} alt={`参考图 ${index + 1}`} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                    <span className="absolute right-0.5 top-0.5 rounded bg-black/55 px-1 text-[10px] font-black text-white">×</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">可上传产品、人物、风格参考图</p>
            )}
            {imageTextReferenceError ? <p className="mt-2 text-xs font-bold text-red-500">{imageTextReferenceError}</p> : null}
          </div>
          <select value={imageTextMode} onChange={(event) => setImageTextMode(event.target.value as typeof imageTextMode)} className="h-10 w-full rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
            {IMAGE_TEXT_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select value={imageTextRatio} onChange={(event) => setImageTextRatio(event.target.value as typeof imageTextRatio)} className="h-10 rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
              {IMAGE_TEXT_RATIOS.map((ratio) => <option key={ratio} value={ratio}>{ratio}</option>)}
            </select>
            <select value={imageTextPageCount} onChange={(event) => setImageTextPageCount(Number(event.target.value))} className="h-10 rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
              {IMAGE_TEXT_PAGE_COUNTS.map((count) => <option key={count} value={count}>{count}页</option>)}
            </select>
          </div>
          <label className="flex h-10 cursor-pointer items-center justify-between rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold text-[#111827]">
            <span>加水印</span>
            <span className="flex items-center gap-2">
              <span className="text-xs text-[var(--glass-text-secondary)]">{imageTextWatermarkEnabled ? '开' : '关'}</span>
              <input
                type="checkbox"
                checked={imageTextWatermarkEnabled}
                onChange={(event) => setImageTextWatermarkEnabled(event.target.checked)}
                className="peer sr-only"
              />
              <span aria-hidden="true" className="relative h-5 w-9 rounded-full bg-slate-300 transition peer-checked:bg-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-[#eee9fb] after:shadow-sm after:transition after:content-[''] peer-checked:after:translate-x-4" />
            </span>
          </label>
        </div>
      )
    }

    if (skill === 'lite-ppt') {
      return (
        <div className="space-y-3">
          <div>
            <div className="text-sm font-black text-[var(--glass-text-primary)]">AI PPT 参数</div>
            <div className="mt-1 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">默认 16:9，支持可编辑文字层和 PPTX 导出。</div>
          </div>
          <select value={litePptPageCount} onChange={(event) => setLitePptPageCount(Number(event.target.value))} className="h-10 w-full rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold">
            {LITE_PPT_PAGE_COUNTS.map((count) => <option key={count} value={count}>{count}页</option>)}
          </select>
          <input
            value={litePptVisualPrompt}
            onChange={(event) => setLitePptVisualPrompt(event.target.value.slice(0, 20))}
            placeholder="视觉方向，如：玩偶风格"
            className="h-10 w-full rounded-xl border border-blue-100 bg-white px-3 text-sm font-bold"
          />
        </div>
      )
    }

    return (
      <div className="space-y-2">
        <div className="text-sm font-black text-[var(--glass-text-primary)]">普通对话</div>
        <div className="text-[11px] font-semibold leading-5 text-[var(--glass-text-tertiary)]">当前技能没有额外参数。切换到底部的 AI短视频、AI图文 或 AI PPT 后，可在这里展开设置。</div>
      </div>
    )
  }

  if (!isAiChatClientMounted) {
    return (
      <main data-ai-chat-workbench-shell="true" className="fixed inset-x-0 bottom-0 top-[var(--ai-chat-nav-height,2.75rem)] min-h-0 w-full overflow-hidden bg-[#f7f3ff] px-3 pb-2 pt-2 lg:bg-[radial-gradient(circle_at_18%_12%,rgba(96,165,250,0.22),transparent_32%),linear-gradient(135deg,#f8fbff_0%,#eef4ff_48%,#f7f0ff_100%)] lg:px-4 lg:pb-3 lg:pt-2">
        <section className="mx-auto h-full min-h-0 w-full max-w-7xl rounded-[2rem] border-x border-b border-white/60 bg-white/72 shadow-[0_24px_70px_rgba(46,58,120,0.14)] backdrop-blur-xl" />
      </main>
    )
  }

  if (sessionStatus !== 'authenticated') {
    return (
      <main data-ai-chat-workbench-shell="true" className="fixed inset-x-0 bottom-0 top-[var(--ai-chat-nav-height,2.75rem)] min-h-0 w-full overflow-hidden bg-[#f7f3ff] px-3 pb-2 pt-2 lg:bg-[radial-gradient(circle_at_18%_12%,rgba(96,165,250,0.22),transparent_32%),linear-gradient(135deg,#f8fbff_0%,#eef4ff_48%,#f7f0ff_100%)] lg:px-4 lg:pb-3 lg:pt-2">
        <section className="mx-auto grid h-full min-h-0 w-full max-w-7xl place-items-center rounded-[2rem] border-x border-b border-white/60 bg-white/72 shadow-[0_24px_70px_rgba(46,58,120,0.14)] backdrop-blur-xl">
          <div className="rounded-2xl border border-blue-100 bg-white/90 px-5 py-4 text-sm font-bold text-slate-700 shadow-[0_16px_40px_rgba(37,99,235,0.12)]">
            请先登录后使用 AI 对话。
          </div>
        </section>
      </main>
    )
  }

  const manualCompressionStats = getManualCompressionStats(messages)
  const parsedManualCompressionMaxChars = Number(manualCompressionMaxChars)

  return (
    <main ref={pageShellRef} data-ai-chat-workbench-shell="true" className="fixed inset-x-0 bottom-0 top-[var(--ai-chat-nav-height,2.75rem)] min-h-0 w-full overflow-hidden bg-[#f7f3ff] px-3 pb-2 pt-2 lg:bg-[#f3f7ff] lg:px-4 lg:pb-3 lg:pt-2">
      <section className={`ai-chat-shell-grid relative mx-auto grid h-full min-h-0 w-full max-w-7xl grid-cols-[3.25rem_minmax(0,1fr)] overflow-hidden rounded-[2rem] border-x border-b border-white/60 bg-white/72 ${desktopSidebarCollapsed ? 'ai-chat-shell-grid-collapsed lg:grid-cols-[3.25rem_minmax(0,1fr)]' : 'ai-chat-shell-grid-expanded lg:grid-cols-[320px_minmax(0,1fr)]'}`}>
        {mobileSessionPanelOpen ? (
          <div
            data-ai-chat-mobile-session-panel="true"
            className="absolute left-5 top-[4.75rem] z-40 flex max-h-[min(58vh,360px)] w-[min(20rem,calc(100%-2rem))] flex-col rounded-2xl border border-white/70 bg-white/94 p-3 shadow-[0_22px_54px_rgba(46,58,120,0.22)] backdrop-blur-xl lg:hidden"
          >
            <button
              type="button"
              onClick={() => {
                handleCreateSession()
                setMobileSessionPanelOpen(false)
              }}
              className="flex min-w-0 w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#2672ff_0%,#884dff_100%)] px-3 py-2 text-xs font-black text-white shadow-[0_10px_22px_rgba(78,91,220,0.18)]"
            >
              <AppIcon name="plus" className="h-4 w-4" />
              新建对话
            </button>
            <div className="custom-scrollbar mt-3 min-h-0 space-y-1 overflow-y-auto pr-1">
              {renderSessionItems({ compact: true })}
            </div>
          </div>
        ) : null}

        {mobileSkillPanelOpen ? (
          <div className="absolute bottom-[6.75rem] left-1/2 z-40 max-h-[min(62vh,430px)] w-[min(24rem,calc(100%-2rem))] -translate-x-1/2 overflow-y-auto rounded-2xl border border-white/70 bg-white/94 p-4 shadow-[0_22px_54px_rgba(46,58,120,0.22)] backdrop-blur-xl lg:hidden">
            {renderMobileSkillSettings()}
          </div>
        ) : null}

        <div className="ai-chat-sidebar-clip min-h-0 overflow-hidden">
          <div className={`ai-chat-sidebar-panel ai-chat-sidebar-panel-collapsed flex min-h-0 flex-col items-center gap-2 bg-white/48 px-2 py-5 ${desktopSidebarCollapsed ? '' : 'lg:hidden'}`}>
            <button
              type="button"
              onClick={handleToggleMobileSessionPanelFromRail}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white text-blue-700 shadow-[0_8px_18px_rgba(37,99,235,0.08)] transition hover:bg-blue-50 lg:hidden"
              title={mobileSessionPanelOpen ? '关闭对话列表' : '展开对话列表'}
              aria-label={mobileSessionPanelOpen ? '关闭对话列表' : '展开对话列表'}
            >
              <AppIcon name={mobileSessionPanelOpen ? 'panelLeftClose' : 'panelLeftOpen'} className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleExpandDesktopSidebar}
              className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white text-blue-700 shadow-[0_8px_18px_rgba(37,99,235,0.08)] transition hover:bg-blue-50 lg:inline-flex"
              title="展开侧边栏"
              aria-label="展开侧边栏"
            >
              <AppIcon name="panelLeftOpen" className="h-5 w-5" />
            </button>
          </div>
        <aside ref={desktopSidebarRef} aria-hidden={desktopSidebarCollapsed} className={`ai-chat-sidebar-panel ai-chat-sidebar-panel-expanded custom-scrollbar relative z-0 flex min-h-0 flex-col overflow-hidden border-r border-blue-200/90 bg-white/48 p-5 max-lg:hidden ${desktopSidebarCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100'}`}>
          <div className="relative -mx-5 -mt-5 min-h-0 overflow-visible px-5 pb-4 pt-5">
            <div className="relative">
              <div className="mb-4 flex items-center justify-between gap-2.5 px-1">
                <div className="flex min-w-0 items-center gap-2.5">
                  <PreferredFixedImage
                    basePath="/xinbao-ai-logo"
                    alt=""
                    ariaHidden
                    loading="lazy"
                    decoding="async"
	                  className="block h-[42px] w-[42px] shrink-0 overflow-hidden rounded-xl"
                  />
                  <div className="min-w-0">
                    <div className="text-xl font-black leading-6 tracking-tight text-[var(--glass-text-primary)]">讯宝AI</div>
                    <div className="mt-0.5 text-xs font-semibold leading-4 text-[var(--glass-text-tertiary)]">智能创作助手</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCollapseDesktopSidebar}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-blue-100 bg-white text-blue-700 shadow-[0_8px_18px_rgba(37,99,235,0.08)] transition hover:bg-blue-50"
                  title="折叠侧边栏"
                  aria-label="折叠侧边栏"
                >
                  <AppIcon name="panelLeftClose" className="h-5 w-5" />
                </button>
              </div>
              <button
                type="button"
                onClick={handleCreateSession}
                className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#2672ff_0%,#884dff_100%)] px-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(78,91,220,0.18)]"
              >
                <AppIcon name="plus" className="h-4 w-4" />
                新建对话
              </button>
              <div
                ref={desktopSessionListRef}
                className="ai-chat-session-scroll mt-3 max-h-[22rem] space-y-1 overflow-y-auto rounded-xl border border-blue-200/70 p-1 pr-2"
                style={desktopSessionListMaxHeight !== null ? { maxHeight: `${desktopSessionListMaxHeight}px` } : undefined}
              >
                {renderSessionItems()}
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute inset-x-5 bottom-5 z-10">
            <div ref={desktopSkillOverlayRef} className="pointer-events-auto">
          {skill === 'quick-video' ? (
            <div className="contents">
              <input
                ref={quickVideoFirstFrameInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleQuickVideoFirstFrameChange}
                disabled={quickVideoFirstFrameUploading || isSending}
              />
              <div className="ai-chat-skill-panel-fade-in relative z-10 mb-1 mt-3 rounded-[1.25rem] border border-blue-100 bg-white p-3 shadow-[0_12px_26px_rgba(55,95,180,0.10)]">
                <div className="flex items-start gap-3">
                  <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-blue-100 bg-blue-50">
                    {quickVideoFirstFrameSource ? (
                      <img src={toDisplayImageUrl(quickVideoFirstFrameDisplayUrl) || quickVideoFirstFrameDisplayUrl} alt="用户首帧图" className="h-full w-full object-cover" loading="lazy" decoding="async" />
                    ) : (
                      <AppIcon name="image" className="h-7 w-7 text-blue-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-black text-[var(--glass-text-primary)]">用户首帧图</div>
                    <div className="mt-1 line-clamp-2 text-[11px] font-semibold leading-4 text-[var(--glass-text-tertiary)]">
                      {quickVideoFirstFrameSource ? (quickVideoFirstFrameName || '已上传图片') : `跟随比例 ${quickVideoRatio}，512-2048px，3MB内`}
                    </div>
                  </div>
                </div>
                {quickVideoFirstFrameError ? <p className="mt-2 text-xs font-bold text-red-500">{quickVideoFirstFrameError}</p> : null}
                <div className="mt-2 flex gap-2">
                  {quickVideoFirstFrameSource ? (
                    <button
                      type="button"
                      onClick={() => {
                        setQuickVideoFirstFrameUrl('')
                        setQuickVideoFirstFrameStorageKey('')
                        setQuickVideoFirstFrameName('')
                        setQuickVideoFirstFrameError('')
                      }}
                      disabled={isSending}
                      className="h-8 flex-1 rounded-lg border border-blue-100 bg-white text-xs font-black text-[var(--glass-text-secondary)] disabled:opacity-50"
                    >
                      移除
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => quickVideoFirstFrameInputRef.current?.click()}
                    disabled={quickVideoFirstFrameUploading || isSending}
                    className="h-8 flex-1 rounded-lg bg-blue-600 text-xs font-black text-white disabled:opacity-50"
                  >
                    {quickVideoFirstFrameUploading ? '上传中' : quickVideoFirstFrameSource ? '更换图片' : '上传图片'}
                  </button>
                </div>
              </div>
              <div className="relative z-10 aspect-square shrink-0 overflow-hidden rounded-[1.35rem] border border-blue-100 bg-blue-50 shadow-[0_14px_30px_rgba(55,95,180,0.12)]">
                <PreferredFixedImage
                  basePath="/xinbao-chat-mascot"
                  alt="讯宝"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 block h-full w-full"
                  imageClassName="h-full w-full object-cover object-center"
                />
                <div className="ai-chat-skill-panel-fade-in absolute inset-0 z-10 overflow-y-auto bg-[linear-gradient(180deg,rgba(15,23,42,0.28)_0%,rgba(15,23,42,0.58)_100%)] p-3 backdrop-blur-md">
                  <span className="text-xs font-bold text-white/92">AI短视频参数</span>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <select
                      value={quickVideoRatio}
                      onChange={(event) => setQuickVideoRatio(event.target.value as typeof quickVideoRatio)}
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    >
                      {QUICK_VIDEO_RATIOS.map((ratio) => (
                        <option key={ratio} value={ratio}>
                          {ratio}
                        </option>
                      ))}
                    </select>
                    <select
                      value={quickVideoDuration}
                      onChange={(event) => setQuickVideoDuration(Number(event.target.value))}
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    >
                      {quickVideoDurationOptions.map((seconds) => (
                        <option key={seconds} value={seconds}>
                          {seconds}秒
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="mt-2 flex h-10 cursor-pointer items-center justify-between rounded-xl border border-white/70 bg-white px-3 text-sm font-bold text-[#111827]">
                    <span>加水印</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-[var(--glass-text-secondary)]">{quickVideoWatermarkEnabled ? '开' : '关'}</span>
                      <input
                        type="checkbox"
                        checked={quickVideoWatermarkEnabled}
                        onChange={(event) => setQuickVideoWatermarkEnabled(event.target.checked)}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className="relative h-5 w-9 rounded-full bg-slate-300 transition peer-checked:bg-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-[#eee9fb] after:shadow-sm after:transition after:content-[''] peer-checked:after:translate-x-4"
                      />
                    </span>
                  </label>
                </div>
              </div>
              <p className="mt-2 text-center text-[11px] font-semibold text-[var(--glass-text-tertiary)]">
                AI也可能会犯错。请核查重要信息
              </p>
            </div>
          ) : null}

          {skill === 'image-text' ? (
            <div className="contents">
              <input
                ref={imageTextReferenceInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageTextReferenceChange}
                disabled={imageTextReferenceUploading || isSending || imageTextReferenceUrls.length >= IMAGE_TEXT_REFERENCE_LIMIT}
              />
              <div className="ai-chat-skill-panel-fade-in relative z-10 mb-1 mt-3 rounded-[1.25rem] border border-blue-100 bg-white p-3 shadow-[0_12px_26px_rgba(55,95,180,0.10)]">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-black text-[var(--glass-text-primary)]">参考图 {imageTextReferenceUrls.length}/{IMAGE_TEXT_REFERENCE_LIMIT}</span>
                  <button
                    type="button"
                    onClick={() => imageTextReferenceInputRef.current?.click()}
                    disabled={imageTextReferenceUploading || isSending || imageTextReferenceUrls.length >= IMAGE_TEXT_REFERENCE_LIMIT}
                    className="h-8 rounded-lg bg-blue-600 px-3 text-xs font-black text-white disabled:opacity-50"
                  >
                    {imageTextReferenceUploading ? '上传中' : '上传图片'}
                  </button>
                </div>
                {imageTextReferenceUrls.length ? (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {imageTextReferenceUrls.map((url, index) => (
                      <button
                        key={`${url}-${index}`}
                        type="button"
                        onClick={() => {
                          setImageTextReferenceUrls((current) => current.filter((_, itemIndex) => itemIndex !== index))
                          setImageTextReferenceNames((current) => current.filter((_, itemIndex) => itemIndex !== index))
                        }}
                        className="relative aspect-square overflow-hidden rounded-xl border border-blue-100 bg-white"
                        title={imageTextReferenceNames[index] || '点击移除'}
                      >
                        <img src={toDisplayImageUrl(url) || url} alt={`参考图 ${index + 1}`} className="h-full w-full object-cover" loading="lazy" decoding="async" />
                        <span className="absolute right-0.5 top-0.5 rounded bg-black/55 px-1 text-[10px] font-black text-white">×</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">可上传产品、人物、风格参考图</p>
                )}
                {imageTextReferenceError ? <p className="mt-2 text-xs font-bold text-red-500">{imageTextReferenceError}</p> : null}
              </div>
              <div className="relative z-10 aspect-square shrink-0 overflow-hidden rounded-[1.35rem] border border-blue-100 bg-blue-50 shadow-[0_14px_30px_rgba(55,95,180,0.12)]">
                <PreferredFixedImage
                  basePath="/xinbao-chat-mascot"
                  alt="讯宝"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 block h-full w-full"
                  imageClassName="h-full w-full object-cover object-center"
                />
                <div className="ai-chat-skill-panel-fade-in absolute inset-0 z-10 overflow-y-auto bg-[linear-gradient(180deg,rgba(15,23,42,0.28)_0%,rgba(15,23,42,0.58)_100%)] p-3 backdrop-blur-md">
                  <span className="text-xs font-bold text-white/92">AI图文参数</span>
                  <div className="mt-3 grid gap-2">
                    <select
                      value={imageTextMode}
                      onChange={(event) => setImageTextMode(event.target.value as typeof imageTextMode)}
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    >
                      {IMAGE_TEXT_MODES.map((mode) => (
                        <option key={mode.value} value={mode.value}>
                          {mode.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <select
                      value={imageTextRatio}
                      onChange={(event) => setImageTextRatio(event.target.value as typeof imageTextRatio)}
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    >
                      {IMAGE_TEXT_RATIOS.map((ratio) => (
                        <option key={ratio} value={ratio}>
                          {ratio}
                        </option>
                      ))}
                    </select>
                    <select
                      value={imageTextPageCount}
                      onChange={(event) => setImageTextPageCount(Number(event.target.value))}
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    >
                      {IMAGE_TEXT_PAGE_COUNTS.map((count) => (
                        <option key={count} value={count}>
                          {count}页
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="mt-2 flex h-10 cursor-pointer items-center justify-between rounded-xl border border-white/70 bg-white px-3 text-sm font-bold text-[#111827]">
                    <span>加水印</span>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-[var(--glass-text-secondary)]">{imageTextWatermarkEnabled ? '开' : '关'}</span>
                      <input
                        type="checkbox"
                        checked={imageTextWatermarkEnabled}
                        onChange={(event) => setImageTextWatermarkEnabled(event.target.checked)}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className="relative h-5 w-9 rounded-full bg-slate-300 transition peer-checked:bg-blue-600 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-300 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-[#eee9fb] after:shadow-sm after:transition after:content-[''] peer-checked:after:translate-x-4"
                      />
                    </span>
                  </label>
                </div>
              </div>
              <p className="mt-2 text-center text-[11px] font-semibold text-[var(--glass-text-tertiary)]">
                AI也可能会犯错。请核查重要信息
              </p>
            </div>
          ) : null}
          {skill === 'lite-ppt' ? (
            <div className="contents">
              <div className="relative z-10 mt-3 aspect-square shrink-0 overflow-hidden rounded-[1.35rem] border border-blue-100 bg-blue-50 shadow-[0_14px_30px_rgba(55,95,180,0.12)]">
                <PreferredFixedImage
                  basePath="/xinbao-chat-mascot"
                  alt="讯宝"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 block h-full w-full"
                  imageClassName="h-full w-full object-cover object-center"
                />
                <div className="ai-chat-skill-panel-fade-in absolute inset-0 z-10 overflow-y-auto bg-[linear-gradient(180deg,rgba(15,23,42,0.28)_0%,rgba(15,23,42,0.58)_100%)] p-3 backdrop-blur-md">
                  <span className="text-xs font-bold text-white/92">AI PPT 参数</span>
                  <div className="mt-3 grid gap-2">
                    <select value={litePptPageCount} onChange={(event) => setLitePptPageCount(Number(event.target.value))} className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold">
                      {LITE_PPT_PAGE_COUNTS.map((count) => <option key={count} value={count}>{count}页</option>)}
                    </select>
                    <input
                      value={litePptVisualPrompt}
                      onChange={(event) => setLitePptVisualPrompt(event.target.value.slice(0, 20))}
                      placeholder="视觉方向"
                      className="h-10 rounded-xl border border-white/70 bg-white px-3 text-sm font-bold"
                    />
                    <div className="rounded-xl border border-white/70 bg-white px-3 py-2 text-xs font-bold leading-5 text-[var(--glass-text-secondary)]">
                      16:9 · 可调整文字位置/大小 · 可导出 PPTX
                    </div>
                  </div>
                </div>
              </div>
              <p className="mt-2 text-center text-[11px] font-semibold text-[var(--glass-text-tertiary)]">
                AI也可能会犯错。请核查重要信息
              </p>
            </div>
          ) : null}
          {skill === 'general' ? (
            <div className="contents">
              <div className="relative z-10 mt-3 aspect-square shrink-0 overflow-hidden rounded-[1.35rem] border border-blue-100 bg-blue-50 shadow-[0_14px_30px_rgba(55,95,180,0.12)]">
                <PreferredFixedImage
                  basePath="/xinbao-chat-mascot"
                  alt="讯宝"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 block h-full w-full"
                  imageClassName="h-full w-full object-cover object-center"
                />
              </div>
              <p className="mt-2 text-center text-[11px] font-semibold text-[var(--glass-text-tertiary)]">
                AI也可能会犯错。请核查重要信息
              </p>
            </div>
          ) : null}
            </div>
          </div>
        </aside>
        </div>

        <div className="relative z-10 flex min-h-0 flex-col bg-white/48">
          {error ? (
            <div className="mx-5 mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
              {error}
            </div>
          ) : null}

          <div ref={messageListRef} className="custom-scrollbar min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-20 pt-6 scroll-pb-20">
            {hiddenMessageCount > 0 ? (
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => setShowAllMessages(true)}
                  className="inline-flex h-9 items-center gap-2 rounded-full border border-blue-100 bg-white/88 px-4 text-xs font-black text-blue-700 shadow-[0_8px_18px_rgba(38,114,255,0.08)] transition hover:bg-blue-50"
                >
                  <AppIcon name="chevronDown" className="h-3.5 w-3.5" />
                  展开前面 {hiddenMessageCount} 条历史消息
                </button>
              </div>
            ) : null}
		            {visibleMessages.map((message) => {
		              const isUser = message.role === 'user'
              const compressionSummary = !isUser && isCompressionSummaryMessage(message)
	                ? parseCompressionSummaryMessage(message)
	                : null
	              const compressionSummaryExpanded = !!compressionSummary && expandedCompressionSummaryIds.has(message.id)
	              const compressionSummaryEditing = !!compressionSummary && editingCompressionSummaryId === message.id
	              const isWeatherMessage = !isUser && !!message.weatherCard
              const imageTextWorkflow = message.workflow?.type === 'image-text' ? message.workflow : null
              const imageTextImages = imageTextWorkflow?.images || []
              const imageTextPageImages = imageTextWorkflow?.pageImages?.length ? imageTextWorkflow.pageImages : imageTextImages
              const imageTextTargetCount = imageTextWorkflow ? Math.max(1, imageTextWorkflow.targetPageCount || imageTextPageImages.length || 1) : 0
              const quickVideoWorkflow = message.workflow?.type === 'quick-video' ? message.workflow : null
              const quickVideoPending = !!quickVideoWorkflow && !quickVideoWorkflow.videoUrl && quickVideoWorkflow.status !== 'failed'
              const quickVideoPosterUrl = quickVideoWorkflow?.posterUrl
                ? toDisplayImageUrl(quickVideoWorkflow.posterUrl) || quickVideoWorkflow.posterUrl
                : undefined
              const litePptWorkflow = message.workflow?.type === 'lite-ppt' ? message.workflow : null
              const litePptProgress = litePptWorkflow ? readTaskProgress(litePptWorkflow.progress) : undefined
              const litePptProgressWidth = `${Math.max(5, litePptProgress ?? 8)}%`
              const rawLitePptWorkflowMessage = friendlyAiChatWorkflowMessage(litePptWorkflow?.message)
              const litePptWorkflowMessage = litePptWorkflow?.status === 'failed'
                ? (
                  rawLitePptWorkflowMessage && !rawLitePptWorkflowMessage.includes('AI PPT 已生成')
                    ? rawLitePptWorkflowMessage
                    : LITE_PPT_FAILED_FALLBACK_MESSAGE
                )
                : rawLitePptWorkflowMessage
              const litePptPageImages = litePptWorkflow?.pageImages || []
              const litePptPageTextBoxes = litePptWorkflow?.pageTextBoxes || []
              const canOpenLitePptProject = !!litePptWorkflow
                && litePptWorkflow.status === 'completed'
                && litePptPageImages.length > 0
              const showWorkflowMediaPreviews = aiChatHeavyPreviewsReady
                || imageTextWorkflow?.status === 'running'
                || quickVideoWorkflow?.status === 'running'
                || litePptWorkflow?.status === 'running'
              const rawTextContent = message.content || (!isUser && streamingAssistantId === message.id ? '正在思考...' : '')
              const displayRawTextContent = isUser
                ? stripSkillContextFromMessageContent(rawTextContent)
                : stripAiChatInlineSourceList(rawTextContent)
              const webSearchEvidenceSourceCount = !isUser ? readAiChatWebSearchEvidenceSourceCount(message) : 0
              const hasWebSearchEvidence = webSearchEvidenceSourceCount > 0
              const isWebSearchDisabledMessageWithSources = hasWebSearchEvidence
                && isAiChatWebSearchDisabledMessageContent(rawTextContent)
              const textContent = isWebSearchDisabledMessageWithSources
                ? createAiChatWebSearchDisabledWithSourcesDisplayMessage(webSearchEvidenceSourceCount, !!message.sources?.length)
                : displayRawTextContent
              const messageAvatarMeta = isUser ? userAvatarMeta : assistantAvatarMeta
              const assistantAvatarLoading = !isUser && streamingAssistantId === message.id
              const assistantResponseTime = !isUser ? formatAiChatMessageTime(message.createdAt) : ''
              const messageBelongsToSkillWorkflow = !!message.workflow || message.content.includes('\n\n[技能卡片：')
              const canUseMessageActions = message.id !== 'welcome'
	                && !messageBelongsToSkillWorkflow
	                && !compressionSummary
	                && !isSending
                && !isCompressing
                && streamingAssistantId !== message.id
                && !!stripSkillContextFromMessageContent(message.content)
		              const messageActionButtonClass = isUser
		                ? 'inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-200 bg-white text-blue-700 shadow-[0_6px_14px_rgba(38,114,255,0.12)] transition hover:border-blue-300 hover:bg-blue-50'
		                : 'inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-100 bg-white text-blue-700 shadow-[0_6px_14px_rgba(38,114,255,0.08)] transition hover:bg-blue-50'
		              const getMessageActionButtonClass = (active = false) => {
		                if (!active) return messageActionButtonClass
		                return 'inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(37,99,235,0.22)] transition hover:bg-blue-700'
		              }
	              const messageVersions = getAiChatMessageVersions(message)
	              const hasMessageVersions = canUseMessageActions && messageVersions.length > 1
	              const activeMessageVersionIndex = normalizeAiChatVersionIndex(message.activeVersionIndex, messageVersions.length)
	              const messageActionsOpen = !isUser && messageMenuId.startsWith(`${message.id}:`)
	              const messageCopied = copiedMessageId === message.id
	              const userMessageActionVisibilityClass = messageActionsOpen
	                ? 'pointer-events-auto opacity-100'
	                : 'pointer-events-none opacity-0 group-hover/user-message:pointer-events-auto group-hover/user-message:opacity-100 group-focus-within/user-message:pointer-events-auto group-focus-within/user-message:opacity-100'
	              const messageVersionControls = hasMessageVersions && !isUser ? (
	                <div className="flex items-center justify-start gap-1 px-1">
	                  <div className="inline-flex h-7 items-center overflow-hidden rounded-full border border-blue-100 bg-white text-[11px] font-black text-blue-700 shadow-[0_6px_14px_rgba(38,114,255,0.08)]">
	                    <button
	                      type="button"
	                      onClick={() => handleSelectMessageVersion(message, -1)}
	                      disabled={activeMessageVersionIndex <= 0}
	                      className="grid h-7 w-7 place-items-center transition hover:bg-blue-50 disabled:opacity-35"
	                      title="上一个版本"
	                      aria-label="上一个版本"
	                    >
	                      <AppIcon name="chevronLeft" className="h-3.5 w-3.5" />
	                    </button>
	                    <span className="min-w-[4.7rem] border-x border-blue-100 px-2 text-center">
	                      版本 {activeMessageVersionIndex + 1}/{messageVersions.length}
	                    </span>
	                    <button
	                      type="button"
	                      onClick={() => handleSelectMessageVersion(message, 1)}
	                      disabled={activeMessageVersionIndex >= messageVersions.length - 1}
	                      className="grid h-7 w-7 place-items-center transition hover:bg-blue-50 disabled:opacity-35"
	                      title="下一个版本"
	                      aria-label="下一个版本"
	                    >
	                      <AppIcon name="chevronRight" className="h-3.5 w-3.5" />
	                    </button>
	                  </div>
	                </div>
	              ) : null
              const messageActions = canUseMessageActions ? (
	                <div className={`relative flex gap-1 px-1 ${messageActionsOpen ? 'z-[120]' : 'z-10'} ${isUser ? `ai-chat-user-message-actions justify-end ${userMessageActionVisibilityClass}` : 'justify-start'}`}>
	                  {isUser ? (
	                    <button
	                      type="button"
	                      onClick={() => void handleCopyMessage(message)}
	                      className={getMessageActionButtonClass(messageCopied)}
	                      title={messageCopied ? '已复制' : '复制消息'}
	                      aria-label={messageCopied ? '已复制' : '复制消息'}
	                    >
	                      <span className="relative h-3.5 w-3.5" aria-hidden="true">
	                        <AppIcon name="copy" className={`ai-chat-copy-icon absolute inset-0 h-3.5 w-3.5 ${messageCopied ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`} />
	                        <AppIcon name="check" className={`ai-chat-copy-icon absolute inset-0 h-3.5 w-3.5 ${messageCopied ? 'scale-100 opacity-100' : 'scale-75 opacity-0'}`} />
	                      </span>
	                    </button>
	                  ) : (
	                    <button
	                      type="button"
	                      onClick={() => handleSpeakMessage(message)}
	                      disabled={!canUseBrowserSpeech}
	                      className={canUseBrowserSpeech
	                        ? getMessageActionButtonClass(speakingMessageId === message.id)
	                        : `${messageActionButtonClass} cursor-not-allowed opacity-45`}
	                      title={!canUseBrowserSpeech ? '当前浏览器不支持内置朗读' : speakingMessageId === message.id ? '停止朗读' : '浏览器内置朗读'}
	                      aria-label={!canUseBrowserSpeech ? '当前浏览器不支持内置朗读' : speakingMessageId === message.id ? '停止朗读' : '浏览器内置朗读'}
	                    >
	                      <AppIcon name={speakingMessageId === message.id ? 'volumeOff' : 'audioWave'} className="h-3.5 w-3.5" />
	                    </button>
	                  )}
                  {isUser ? (
                    <button
                      type="button"
                      onClick={() => handleOpenEditMessage(message)}
                      className={messageActionButtonClass}
                      title="编辑并重新发送"
                      aria-label="编辑并重新发送"
                    >
                      <AppIcon name="edit" className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void handleRegenerateAssistantMessage(message)}
                      className={messageActionButtonClass}
                      title="重新生成"
                      aria-label="重新生成"
                    >
                      <AppIcon name="refresh" className="h-3.5 w-3.5" />
                    </button>
                  )}
	                  {!isUser ? <button
	                    type="button"
	                    onClick={() => setMessageMenuId((current) => current === `${message.id}:translate` ? '' : `${message.id}:translate`)}
	                    className={getMessageActionButtonClass(messageMenuId === `${message.id}:translate`)}
	                    title="翻译消息"
	                    aria-label="翻译消息"
	                    disabled={!!translatingMessageId}
                  >
                    <AppIcon name={translatingMessageId === message.id ? 'loader' : 'languages'} className={`h-3.5 w-3.5 ${translatingMessageId === message.id ? 'animate-spin' : ''}`} />
                  </button> : null}
	                  {!isUser ? <button
	                    type="button"
	                    onClick={() => setMessageMenuId((current) => current === `${message.id}:more` ? '' : `${message.id}:more`)}
	                    className={getMessageActionButtonClass(messageMenuId === `${message.id}:more`)}
	                    title="更多"
	                    aria-label="更多"
	                  >
                    <AppIcon name="menu" className="h-3.5 w-3.5" />
	                  </button> : null}
                  {!isUser && messageMenuId.startsWith(`${message.id}:`) ? (
		                    <div className={`absolute bottom-[calc(100%+0.4rem)] z-[130] w-56 overflow-hidden rounded-2xl border border-blue-100 bg-white p-1.5 text-left shadow-[0_18px_42px_rgba(46,58,120,0.2)] ${isUser ? 'right-0' : 'left-0'}`}>
                      {messageMenuId.endsWith(':translate') ? (
                        <>
                          <div className="px-2 py-1 text-[11px] font-black text-[var(--glass-text-tertiary)]">翻译为</div>
                          {AI_CHAT_TRANSLATION_LANGUAGES.map((language) => (
                            <button
                              key={`${message.id}-translate-${language}`}
                              type="button"
                              onClick={() => void handleTranslateMessage(message, language)}
                              className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50"
                            >
                              <AppIcon name="languages" className="h-3.5 w-3.5 text-blue-600" />
                              <span>{language}</span>
                            </button>
                          ))}
                          {message.translation ? (
                            <button
                              type="button"
                              onClick={() => {
	                                setMessages((current) => current.map((item) => {
	                                  if (item.id !== message.id) return item
	                                  const next = { ...item }
	                                  delete next.translation
	                                  if (next.versions?.length) {
	                                    const activeIndex = normalizeAiChatVersionIndex(next.activeVersionIndex, next.versions.length)
	                                    next.versions = next.versions.map((version, index) => {
	                                      if (index !== activeIndex) return version
	                                      const nextVersion = { ...version }
	                                      delete nextVersion.translation
	                                      return nextVersion
	                                    })
	                                  }
	                                  return next
	                                }))
                                setMessageMenuId('')
                              }}
                              className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-red-600 transition hover:bg-red-50"
                            >
                              <AppIcon name="trashAlt" className="h-3.5 w-3.5" />
                              <span>清空翻译</span>
                            </button>
                          ) : null}
                        </>
                      ) : (
                        <>
                          {isUser ? (
                            <button type="button" onClick={() => handleOpenEditMessage(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
                              <AppIcon name="edit" className="h-3.5 w-3.5 text-blue-600" />
                              编辑并重新发送
                            </button>
                          ) : null}
                          <button type="button" onClick={() => handleOpenSelectCopy(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
                            <AppIcon name="copy" className="h-3.5 w-3.5 text-blue-600" />
                            选择复制
                          </button>
                          <button type="button" onClick={() => handleOpenMessagePreview(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
                            <AppIcon name="bookOpen" className="h-3.5 w-3.5 text-blue-600" />
                            网页视图渲染
                          </button>
                          <button type="button" onClick={() => downloadAiChatMessage(message, 'markdown')} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
                            <AppIcon name="download" className="h-3.5 w-3.5 text-blue-600" />
                            导出 Markdown
                          </button>
                          <button type="button" onClick={() => downloadAiChatMessage(message, 'txt')} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
                            <AppIcon name="fileText" className="h-3.5 w-3.5 text-blue-600" />
                            导出 TXT
                          </button>
	                          <button type="button" onClick={() => handleForkConversationAtMessage(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-[var(--glass-text-primary)] transition hover:bg-blue-50">
	                            <AppIcon name="folderOpen" className="h-3.5 w-3.5 text-blue-600" />
	                            创建分支
	                          </button>
	                          {hasMessageVersions ? (
	                            <button type="button" onClick={() => handleDeleteCurrentMessageVersion(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-red-600 transition hover:bg-red-50">
	                              <AppIcon name="trashAlt" className="h-3.5 w-3.5" />
	                              删除本版本
	                            </button>
	                          ) : null}
	                          <button type="button" onClick={() => handleDeleteMessage(message)} className="flex min-h-9 w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-xs font-bold text-red-600 transition hover:bg-red-50">
	                            <AppIcon name="trashAlt" className="h-3.5 w-3.5" />
	                            删除本消息
	                          </button>
                        </>
                      )}
                    </div>
                  ) : null}
                </div>
              ) : null
              const showForkDividerAfterMessage = activeSessionForkSource?.messageId === message.id
              const messageBubble = (
                <div
                  className={`${isUser ? 'max-w-full' : 'max-w-full'} text-sm leading-[1.58] ${
                    isUser
                      ? 'rounded-3xl bg-[linear-gradient(135deg,#2f74ff_0%,#7c4dff_100%)] px-4 py-3 text-white shadow-[0_12px_30px_rgba(32,46,88,0.08)]'
                      : isWeatherMessage
                        ? 'text-[var(--glass-text-primary)]'
	                      : 'rounded-3xl bg-transparent px-1 py-1 text-[var(--glass-text-primary)] shadow-none'
                  }`}
                >
	                  {!isUser && message.steps?.length ? (
	                    <AiChatProcessSteps steps={message.steps} />
	                  ) : null}
	                  {compressionSummary ? (
	                    <div className="w-full max-w-[min(32rem,calc(100vw-4rem))] rounded-2xl border border-blue-100 bg-blue-50/80 p-3 text-[var(--glass-text-primary)] shadow-[0_10px_24px_rgba(38,114,255,0.08)]">
	                      <div className="flex w-full items-start justify-between gap-2">
	                        <button
	                          type="button"
	                          onClick={() => setExpandedCompressionSummaryIds((current) => {
	                            const next = new Set(current)
	                            if (next.has(message.id)) next.delete(message.id)
	                            else next.add(message.id)
	                            return next
	                          })}
	                          className="min-w-0 flex-1 text-left"
	                          aria-expanded={compressionSummaryExpanded}
	                        >
	                          <span className="block text-xs font-black text-blue-700">上下文已压缩</span>
	                          <span className="mt-1 line-clamp-2 block text-[11px] font-semibold leading-4 text-[var(--glass-text-secondary)]">
	                            {compressionSummary.heading || '旧对话明细已折叠，后续回答会继承摘要。'}
	                          </span>
	                        </button>
	                        <div className="flex shrink-0 items-center gap-1">
	                          <button
	                            type="button"
	                            onClick={() => handleOpenCompressionSummaryEdit(message, compressionSummary.summary)}
	                            disabled={isSending || isCompressing}
	                            className="inline-flex h-7 items-center gap-1 rounded-full border border-blue-100 bg-white px-2 text-[11px] font-black text-blue-700 disabled:opacity-45"
	                            aria-label="编辑压缩摘要"
	                          >
	                            <AppIcon name="edit" className="h-3.5 w-3.5" />
	                            编辑
	                          </button>
	                          <button
	                            type="button"
	                            onClick={() => setExpandedCompressionSummaryIds((current) => {
	                              const next = new Set(current)
	                              if (next.has(message.id)) next.delete(message.id)
	                              else next.add(message.id)
	                              return next
	                            })}
	                            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-blue-100 bg-white text-blue-700"
	                            aria-label={compressionSummaryExpanded ? '收起压缩摘要' : '展开压缩摘要'}
	                          >
	                            <AppIcon name={compressionSummaryExpanded ? 'chevronUp' : 'chevronDown'} className="h-3.5 w-3.5" />
	                          </button>
	                        </div>
	                      </div>
	                      {compressionSummaryExpanded && compressionSummary.summary ? (
	                        <div className="mt-3 rounded-xl border border-blue-100 bg-white/78 px-3 py-2 text-xs leading-5 text-[var(--glass-text-secondary)]">
	                          {compressionSummaryEditing ? (
	                            <div className="space-y-2">
	                              <textarea
	                                value={editingCompressionSummaryText}
	                                onChange={(event) => {
	                                  setEditingCompressionSummaryText(event.target.value)
	                                  setEditingCompressionSummaryError('')
	                                }}
	                                className="block min-h-32 w-full resize-y rounded-lg border border-blue-100 bg-white px-2.5 py-2 text-xs font-semibold leading-5 text-[var(--glass-text-primary)] outline-none focus:border-blue-400"
	                                aria-label="压缩摘要内容"
	                              />
	                              {editingCompressionSummaryError ? (
	                                <div className="text-[11px] font-bold text-red-600">{editingCompressionSummaryError}</div>
	                              ) : null}
	                              <div className="flex justify-end gap-2">
	                                <button
	                                  type="button"
	                                  onClick={handleCancelCompressionSummaryEdit}
	                                  className="h-8 rounded-lg border border-blue-100 bg-white px-3 text-xs font-black text-blue-700"
	                                >
	                                  取消
	                                </button>
	                                <button
	                                  type="button"
	                                  onClick={() => handleSaveCompressionSummaryEdit(message)}
	                                  className="h-8 rounded-lg bg-blue-600 px-3 text-xs font-black text-white"
	                                  aria-label="保存压缩摘要"
	                                >
	                                  保存
	                                </button>
	                              </div>
	                            </div>
	                          ) : (
	                            <MarkdownMessage content={compressionSummary.summary} />
	                          )}
	                        </div>
	                      ) : null}
	                    </div>
	                  ) : isWeatherMessage && message.weatherCard ? (
                    <div className={`relative w-full overflow-hidden rounded-[1.45rem] border border-white/14 bg-gradient-to-br p-3.5 text-white backdrop-blur-xl ring-1 ring-white/10 sm:p-4 ${weatherCardThemeClass(message.weatherCard.condition)}`}>
                      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_90%_18%,rgba(125,211,252,0.13),transparent_24%),linear-gradient(180deg,rgba(255,255,255,0.06),transparent_48%)]" />
                      <div className="pointer-events-none absolute -left-20 -top-20 h-48 w-48 rounded-full bg-white/8 blur-3xl" />
                      <div className="pointer-events-none absolute bottom-0 right-0 h-40 w-40 rounded-full bg-black/14 blur-2xl" />
                      <div className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />

                      <div className="relative flex flex-wrap items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2 text-xs font-black leading-none text-white/96 sm:text-sm">
                          <span className="text-sm leading-none drop-shadow">📍</span>
                          <span className="truncate">{message.weatherCard.location}</span>
                        </div>
                        <div className="shrink-0 rounded-full border border-white/10 bg-white/8 px-2.5 py-1 text-[10px] font-bold text-sky-50/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                          {formatWeatherUpdatedAt(message.weatherCard.updatedAt)}
                        </div>
                      </div>

                      <div className="relative mt-3 grid gap-3 sm:grid-cols-[minmax(230px,0.92fr)_minmax(340px,1.34fr)] sm:items-stretch">
                        <div className="flex items-center gap-4 rounded-[1.1rem] border border-white/10 bg-white/[0.07] px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
                          <span className="grid h-16 w-16 shrink-0 place-items-center text-5xl leading-none drop-shadow-[0_8px_18px_rgba(10,20,45,0.26)]">
                            {weatherIconFor(message.weatherCard.condition)}
                          </span>
                          <div className="min-w-0 max-w-full">
                            <div className="flex items-start">
                              <span className="text-[3rem] font-light leading-[0.9] tracking-[-0.08em] text-white drop-shadow-[0_10px_25px_rgba(0,0,0,0.16)]">
                                {weatherTemperatureValue(message.weatherCard.temperature)}
                              </span>
                              <span className="ml-2 pt-1 text-lg font-light text-white/88">°C</span>
                            </div>
                            <div className="mt-1 whitespace-nowrap text-sm font-black text-white">{message.weatherCard.condition}</div>
                            <div className="mt-1 whitespace-nowrap text-[11px] font-semibold text-sky-100/70">体感 {message.weatherCard.feelsLike}</div>
                          </div>
                        </div>

                        <div className="grid gap-2">
                          <div className="grid grid-cols-3 gap-1.5">
                            {[
                              { icon: '💧', label: '湿度', value: message.weatherCard.humidity },
                              { icon: '💨', label: '风速', value: message.weatherCard.wind },
                              { icon: '🌧️', label: '降水', value: message.weatherCard.precipitation },
                            ].map((item) => (
                              <div key={item.label} className="min-w-0 rounded-[1.2rem] border border-white/8 bg-white/[0.045] px-2 py-1.5 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
                                <div className="flex items-center justify-center gap-1 text-[9px] font-semibold text-sky-100/56">
                                  <span className="text-sm leading-none text-sky-100/88">{item.icon}</span>
                                  <span>{item.label}</span>
                                </div>
                                <div className="mt-0.5 whitespace-nowrap text-[10px] font-black leading-tight text-white sm:text-[11px]">{item.value}</div>
                              </div>
                            ))}
                          </div>

                          {message.weatherCard.forecast.length ? (
                            <div className="grid grid-cols-3 gap-2 border-t border-white/10 pt-2">
                              {message.weatherCard.forecast.map((item, index) => (
                                <div key={`${message.id}-${item.day}`} className={`min-w-0 rounded-xl px-1.5 py-1.5 text-center ${index === 0 ? 'bg-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.10)]' : ''}`}>
                                  <div className="truncate text-[9px] font-bold text-sky-100/52">{formatForecastDayLabel(item.day, index)}</div>
                                  <div className="mt-1 text-base leading-none">{weatherIconFor(item.condition)}</div>
                                  <div className="mt-0.5 text-[10px] font-black text-white">{weatherTemperatureValue(item.range.split('-')[1] || item.range)}°</div>
                                </div>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="relative mt-2 flex justify-end text-[10px] font-semibold text-sky-100/48">
                        <span>讯宝天气</span>
                      </div>
                    </div>
                  ) : quickVideoPending ? (
                    <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">
                      <AppIcon name="loader" className="h-3.5 w-3.5 animate-spin" />
                      正在生成视频{typeof quickVideoWorkflow?.progress === 'number' ? ` ${quickVideoWorkflow.progress}%` : ''}
                    </div>
                  ) : litePptWorkflow ? null : isUser ? (
                    <div className="whitespace-pre-wrap break-words">{textContent}</div>
                  ) : textContent === '正在思考...' ? (
                    <span className="xinbao-thinking px-1 py-1 text-sm font-black">
                      正在思考...
                    </span>
	                  ) : (
                    <MarkdownMessage content={textContent} />
                  )}
                  {message.translation ? (
                    <div className={`mt-3 rounded-2xl border px-3 py-2 ${isUser ? 'border-white/20 bg-white/12 text-white' : 'border-blue-100 bg-blue-50/70 text-[var(--glass-text-primary)]'}`}>
                      <div className={`mb-1 text-[11px] font-black ${isUser ? 'text-white/75' : 'text-blue-700'}`}>
                        翻译 · {message.translation.language}
                      </div>
                      <div className={isUser ? 'whitespace-pre-wrap break-words text-sm leading-[1.58]' : ''}>
                        {isUser ? message.translation.content : <MarkdownMessage content={message.translation.content} />}
                      </div>
                    </div>
                  ) : null}
                  {!isUser && message.sources?.length ? (
                    <details className="group mt-3 max-w-2xl rounded-2xl border border-blue-100 bg-blue-50/60 px-3 py-2">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-black text-blue-700 [&::-webkit-details-marker]:hidden">
                        <span>来源 · {message.sources.length} 条</span>
                        <span className="inline-flex items-center gap-1 text-blue-600">
                          <span className="group-open:hidden">展开</span>
                          <span className="hidden group-open:inline">收起</span>
                          <AppIcon name="chevronDown" className="h-3.5 w-3.5 transition group-open:rotate-180" />
                        </span>
                      </summary>
                      <div className="mt-2 grid gap-1.5">
                        {message.sources.map((source, index) => (
                          <a
                            key={`${message.id}-source-${source.url}-${index}`}
                            href={source.url}
                            target="_blank"
                            rel="noreferrer"
                            className="group min-w-0 rounded-xl bg-white/75 px-2.5 py-2 text-left transition hover:bg-white"
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-black text-white">
                                {index + 1}
                              </span>
                              <span className="min-w-0 flex-1 truncate text-xs font-black text-[var(--glass-text-primary)] group-hover:text-blue-700">
                                {source.title}
                              </span>
                            </div>
                            <div className="mt-1 truncate pl-7 text-[11px] font-semibold text-[var(--glass-text-tertiary)]">
                              {aiChatSourceHost(source.url) || source.url}
                            </div>
                            {source.snippet ? (
                              <div className="mt-1 line-clamp-2 pl-7 text-[11px] leading-4 text-[var(--glass-text-secondary)]">
                                {source.snippet}
                              </div>
                            ) : null}
                          </a>
                        ))}
                      </div>
                    </details>
                  ) : null}
	                  {imageTextWorkflow && ((showWorkflowMediaPreviews && imageTextImages.length > 0) || imageTextWorkflow.status === 'running') ? (
                    <div className="mt-3 grid gap-3">
                      {Array.from({ length: imageTextTargetCount }, (_, index) => {
                        const src = imageTextPageImages[index] || ''
                        const status = imageTextWorkflow.pageStatuses?.[index] || (src ? 'completed' : 'pending')
                        if (src && showWorkflowMediaPreviews) {
                          return (
	                            <ImageTextPreviewButton
	                              key={`${message.id}-${src}-${index}`}
	                              messageId={message.id}
	                              src={src}
	                              index={index}
	                              onPreview={(previewSrc) => {
	                                setPreviewImageUrl(previewSrc)
	                                setPreviewImageQuickVideoUrl(previewSrc)
	                              }}
	                              onLoad={() => scheduleScrollMessagesToSafeBottom(true)}
	                            />
                          )
                        }
                        if (src) return null
                        return (
                          <div
                            key={`${message.id}-pending-${index}`}
                            className={`flex min-h-40 items-center justify-center rounded-2xl border px-4 py-8 text-center ${
                              status === 'failed'
                                ? 'border-red-100 bg-red-50 text-red-500'
                                : 'border-blue-100 bg-blue-50 text-blue-700'
                            }`}
                          >
                            <div className="inline-flex items-center gap-2 text-xs font-black">
                              {status === 'failed' ? (
                                '图片生成失败'
                              ) : (
                                <>
                                  <AppIcon name="loader" className="h-3.5 w-3.5 animate-spin" />
                                  正在生成图片 {index + 1}/{imageTextTargetCount}
                                </>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : null}
                  {litePptWorkflow ? (
                    <div className={`mt-3 max-w-md rounded-2xl border p-3 shadow-[0_10px_24px_rgba(32,46,88,0.08)] ${
                      litePptWorkflow.status === 'failed'
                        ? 'border-red-100 bg-red-50/90'
                        : 'border-blue-100 bg-blue-50/80'
                    }`}>
                      <div className={`flex items-center justify-between gap-3 text-sm font-black ${
                        litePptWorkflow.status === 'failed' ? 'text-red-600' : 'text-blue-700'
                      }`}>
                        <span className="inline-flex min-w-0 items-center gap-2">
                          <AppIcon
                            name={litePptWorkflow.status === 'running' ? 'loader' : 'monitor'}
                            className={`h-4 w-4 shrink-0 ${litePptWorkflow.status === 'running' ? 'animate-spin' : ''}`}
                          />
                          <span className="truncate">
                            {litePptWorkflow.status === 'running'
                              ? 'AI PPT 正在生成'
                              : litePptWorkflow.status === 'failed'
                                ? 'AI PPT 生成失败'
                                : 'AI PPT 项目已创建'}
                          </span>
                        </span>
                        {litePptWorkflow.status === 'running' && typeof litePptProgress === 'number' ? (
                          <span className="shrink-0 tabular-nums">{litePptProgress}%</span>
                        ) : null}
                      </div>
                      {litePptWorkflow.status === 'running' ? (
                        <div className="mt-2">
                          <div className="h-1.5 overflow-hidden rounded-full bg-blue-100">
                            <div
                              className="h-full rounded-full bg-blue-500 transition-[width] duration-500"
                              style={{ width: litePptProgressWidth }}
                            />
                          </div>
                          {litePptWorkflowMessage ? (
                            <div className="mt-1.5 truncate text-xs font-semibold text-blue-600/80">
                              {litePptWorkflowMessage}
                            </div>
                          ) : null}
                        </div>
                      ) : litePptWorkflow.status === 'failed' && litePptWorkflowMessage ? (
                        <div className="mt-1.5 text-xs font-semibold text-red-500">
                          {litePptWorkflowMessage}
                        </div>
                      ) : null}
                      {showWorkflowMediaPreviews && litePptWorkflow.status === 'completed' && litePptPageImages.length > 0 ? (
                        <div className={`mt-3 grid gap-2 ${litePptPageImages.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                          {litePptPageImages.slice(0, 4).map((src, index) => {
                            const boxes = litePptPageTextBoxes[index] || []
                            return (
                              <LitePptPreviewCard
                                key={`${message.id}-lite-ppt-preview-${src}-${index}`}
                                messageId={message.id}
                                src={src}
                                index={index}
                                boxes={boxes}
                                onLoad={() => scheduleScrollMessagesToSafeBottom(true)}
                              />
                            )
                          })}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
	                  {showWorkflowMediaPreviews && quickVideoWorkflow?.videoUrl ? (
                    <QuickVideoPreviewCard
                      videoUrl={quickVideoWorkflow.videoUrl}
                      posterUrl={quickVideoPosterUrl}
                      ratio={quickVideoWorkflow.ratio || quickVideoRatio}
                      onLoadedMetadata={() => scheduleScrollMessagesToSafeBottom(true)}
                    />
                  ) : null}
	                  {message.workflow && (!litePptWorkflow || canOpenLitePptProject) ? (
	                    <a
	                      href={message.workflow.href}
                      className={`mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-black ${
                        isUser
                          ? 'bg-white/20 text-white'
                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                      }`}
                    >
                      <AppIcon name="arrowRight" className="h-3.5 w-3.5" />
                      打开项目
                    </a>
                  ) : null}
                </div>
              )
	              return (
	                <Fragment key={message.id}>
		                  <div className={`flex ${messageMenuId.startsWith(`${message.id}:`) ? 'relative z-[110]' : '[content-visibility:auto] [contain-intrinsic-size:780px_180px]'} ${isUser ? 'justify-end' : 'justify-start'}`}>
	                    {isUser ? (
	                      <div className="group/user-message flex max-w-[78%] flex-col items-end gap-1.5">
		                        {messageBubble}
		                        {messageVersionControls}
		                        {messageActions}
		                      </div>
		                    ) : (
		                      <div className={`flex flex-col items-start gap-1.5 ${isWeatherMessage ? 'w-full max-w-full pr-4' : 'max-w-[78%]'}`}>
		                        <div className="flex items-end gap-2 px-1">
		                          <AiChatAvatar meta={messageAvatarMeta} loading={assistantAvatarLoading} />
		                          <span className="flex min-w-0 -translate-y-0.5 flex-col leading-none">
		                            <span className="truncate text-xs font-[1000] text-[var(--glass-text-tertiary)]">
		                              {messageAvatarMeta.title || AI_CHAT_ASSISTANT_TITLE}
		                            </span>
		                            {assistantResponseTime ? (
		                              <time dateTime={message.createdAt} aria-label="讯宝回应时间" className="mt-1 text-[10px] font-semibold text-[var(--glass-text-tertiary)]/75">
		                                {assistantResponseTime}
		                              </time>
		                            ) : null}
		                          </span>
		                        </div>
		                        {messageBubble}
		                        {messageVersionControls}
		                        {messageActions}
		                      </div>
		                    )}
		                  </div>
		                  {showForkDividerAfterMessage ? (
		                    <div className="flex items-center gap-3 px-2 py-0.5" role="note" aria-label="从对话中派生">
		                      <span className="h-px min-w-0 flex-1 bg-gradient-to-r from-transparent via-[#2672ff]/35 to-[#884dff]/35" />
		                      <span className="shrink-0 bg-[linear-gradient(135deg,#2672ff_0%,#884dff_100%)] bg-clip-text text-sm font-black text-transparent">
		                        从对话中派生
		                      </span>
		                      <span className="h-px min-w-0 flex-1 bg-gradient-to-r from-[#2672ff]/35 via-[#884dff]/35 to-transparent" />
		                    </div>
		                  ) : null}
	                </Fragment>
              )
            })}
            {isCompressing ? (
              <div className="flex justify-start">
	                <div className="rounded-3xl bg-transparent px-1 py-1 text-sm font-bold text-blue-700 shadow-none">
	                  正在整理对话...
                </div>
              </div>
            ) : null}
            {isSending && !streamingAssistantId ? (
              <div className="flex justify-start">
                <div className="flex max-w-[78%] flex-col items-start gap-1.5">
                  <div className="flex items-end gap-2 px-1">
                    <AiChatAvatar meta={assistantAvatarMeta} loading />
                    <span className="flex min-w-0 -translate-y-0.5 flex-col leading-none">
                      <span className="truncate text-xs font-[1000] text-[var(--glass-text-tertiary)]">
                        {assistantAvatarMeta.title || AI_CHAT_ASSISTANT_TITLE}
                      </span>
                      <time dateTime={new Date().toISOString()} aria-label="讯宝回应时间" className="mt-1 text-[10px] font-semibold text-[var(--glass-text-tertiary)]/75">
                        {formatAiChatMessageTime(new Date().toISOString())}
                      </time>
                    </span>
                  </div>
                  <span className="xinbao-thinking px-1 py-1 text-sm font-black">
                    正在思考...
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <form onSubmit={handleSubmit} className="relative z-20 bg-transparent px-4 pb-3 pt-0 lg:px-5 lg:pb-4">
            <div className="relative z-10 overflow-visible rounded-2xl border border-[#2672ff] bg-white shadow-[inset_0_0_0_1px_rgba(38,114,255,0.18)] ring-1 ring-[#2672ff]">
              <div className="px-3 py-1">
                <textarea
                  ref={composerTextareaRef}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                      event.preventDefault()
                      event.currentTarget.form?.requestSubmit()
                    }
                  }}
                  placeholder="发消息..."
                  rows={1}
                  style={{ height: CHAT_INPUT_MIN_HEIGHT, maxHeight: CHAT_INPUT_MAX_HEIGHT }}
                  className="ai-chat-composer-scroll block w-full resize-none overflow-hidden bg-transparent px-1 py-0.5 text-sm leading-5 text-[var(--glass-text-primary)] outline-none placeholder:text-[var(--glass-text-tertiary)]"
                />
              </div>
              <div className="flex min-h-11 items-center justify-between gap-2 bg-transparent px-3 py-1.5">
                <div className="hidden min-w-0 flex-1 flex-wrap gap-1.5 lg:flex">
                  {selectedSkillCard ? (
                    <div className="inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-xl border border-blue-600 bg-blue-600 px-3 text-xs font-black text-white shadow-[0_8px_18px_rgba(31,91,220,0.16)]">
                      <AppIcon name={selectedSkillCard.icon} className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{selectedSkillCard.title}</span>
                      <button
                        type="button"
                        onClick={cancelSelectedSkillCard}
                        className="-mr-1 inline-flex h-5 w-5 items-center justify-center rounded-md text-white/86 transition hover:bg-white/18 hover:text-white"
                        aria-label="取消技能卡片"
                        title="取消技能卡片"
                      >
                        <AppIcon name="closeSm" className="h-3 w-3" />
                      </button>
                    </div>
                  ) : SKILL_CARDS.filter((card) => card.id !== 'general').map((card) => {
                    const active = skill === card.id
                    return (
                      <button
                        key={card.id}
                        type="button"
                        onClick={() => setSkill(card.id)}
                        onFocus={() => prefetchAiChatSkillSettings(card.id)}
                        onMouseEnter={() => prefetchAiChatSkillSettings(card.id)}
                        className={`inline-flex h-8 min-w-0 items-center justify-center gap-1.5 rounded-xl border px-3 text-xs font-black transition ${
                          active
                            ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(31,91,220,0.16)]'
                            : 'border-blue-200 bg-white/85 text-blue-700 shadow-none hover:bg-blue-100'
                        }`}
                      >
                        <AppIcon name={card.icon} className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{card.title}</span>
                      </button>
                    )
                  })}
                </div>
                {selectedSkillCard ? null : (
              <div className="hidden">
                  {mobileComposerToolsOpen ? (
                    <div className="absolute bottom-[calc(100%+0.55rem)] right-0 z-40 grid w-[min(20rem,calc(100vw-2rem))] grid-cols-2 gap-1.5 rounded-2xl border border-white/70 bg-white/94 p-2 shadow-[0_18px_42px_rgba(46,58,120,0.18)] backdrop-blur-xl">
                      <button
                        type="button"
                        onClick={() => {
                          setMobileComposerToolsOpen(false)
                          handleOpenManualCompressionOptions()
                        }}
                        disabled={!canManualCompressContext}
                        aria-label="压缩上下文"
                        title="压缩上下文"
	                        className="inline-flex h-8 min-w-0 shrink-0 items-center justify-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 text-[11px] font-black text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
	                      >
	                        <AppIcon name="package" className="h-3.5 w-3.5" />
	                        <span>压缩上下文</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setMobileComposerToolsOpen(false)
                          textFileInputRef.current?.click()
                        }}
                        disabled={!canImportTextFile}
                        aria-label="导入文档文本"
                        title="导入文档文本"
	                        className="inline-flex h-8 min-w-0 shrink-0 items-center justify-center gap-1 rounded-full border border-blue-200 bg-white px-2.5 text-[11px] font-black text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
	                      >
	                        <AppIcon name={isImportingDocument ? 'loader' : 'fileText'} className={`h-3.5 w-3.5 ${isImportingDocument ? 'animate-spin' : ''}`} />
	                        <span>导入文档</span>
	                      </button>
	                      <button
	                        type="button"
	                        onClick={() => {
	                          setMobileComposerToolsOpen(false)
	                          setWebSearchEnabled((enabled) => !enabled)
	                        }}
	                        disabled={isSending}
	                        aria-pressed={webSearchEnabled}
	                        aria-label={webSearchEnabled ? '关闭联网搜索' : '开启联网搜索'}
	                        title={webSearchEnabled ? '联网搜索已开启' : '联网搜索'}
	                        className={`inline-flex h-8 min-w-0 shrink-0 items-center justify-center gap-1 rounded-full border px-2.5 text-[11px] font-black transition disabled:cursor-not-allowed disabled:opacity-45 ${
	                          webSearchEnabled
	                            ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(31,91,220,0.16)]'
	                            : 'border-blue-200 bg-white text-blue-700 hover:bg-blue-50'
	                        }`}
	                      >
	                        <AppIcon name="globe" className="h-3.5 w-3.5" />
	                        <span>联网搜索</span>
	                      </button>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setMobileComposerToolsOpen((open) => !open)
                      setMobileSkillCardMenuOpen(false)
                      setMobileSessionPanelOpen(false)
                      setMobileSkillPanelOpen(false)
                    }}
                    aria-label="展开更多工具"
                    title="展开更多工具"
                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition ${
                      mobileComposerToolsOpen
                        ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(31,91,220,0.16)]'
                        : 'border-blue-200 bg-white/85 text-blue-700 hover:bg-blue-50'
                    }`}
                  >
                    <AppIcon name="plus" className="h-4 w-4" />
                  </button>
                </div>
                )}
		                <div className="ml-auto flex shrink-0 items-center gap-1.5">
	                  {selectedSkillCard ? null : (
	                    <>
	                      <button
	                        type="button"
	                        onClick={handleOpenManualCompressionOptions}
	                        disabled={!canManualCompressContext}
	                        aria-label="压缩上下文"
	                        title="压缩上下文"
	                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-white/85 text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
	                      >
	                        <AppIcon name="package" className="h-4 w-4" />
	                      </button>
	                      <button
	                        type="button"
	                        onClick={() => textFileInputRef.current?.click()}
	                        disabled={!canImportTextFile}
	                        aria-label="导入文档文本"
	                        title="导入文档文本"
	                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-blue-200 bg-white/85 text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40"
	                      >
	                        <AppIcon name={isImportingDocument ? 'loader' : 'fileText'} className={`h-4 w-4 ${isImportingDocument ? 'animate-spin' : ''}`} />
	                      </button>
	                    </>
	                  )}
			                  {selectedSkillCard ? null : (
                        <button
			                    type="button"
			                    onClick={() => setWebSearchEnabled((enabled) => !enabled)}
		                    disabled={isSending}
		                    aria-pressed={webSearchEnabled}
		                    aria-label={webSearchEnabled ? '关闭联网搜索' : '开启联网搜索'}
		                    title={webSearchEnabled ? '联网搜索已开启' : '联网搜索'}
			                    className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm transition disabled:cursor-not-allowed disabled:opacity-45 ${
		                      webSearchEnabled
		                        ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(31,91,220,0.18)]'
		                        : 'border-blue-200 bg-white/85 text-blue-700 hover:bg-blue-50'
		                    }`}
		                  >
		                    <AppIcon name="globe" className="h-4 w-4" />
		                  </button>
                      )}
			                  <button
			                    type={isSending ? 'button' : 'submit'}
		                    onClick={isSending ? handleStopAiChatGeneration : undefined}
	                    disabled={isSending ? false : !canSend}
	                    aria-label={isSending ? '停止生成' : '发送'}
	                    title={isSending ? '停止生成' : '发送'}
	                    className={`inline-flex h-8 min-w-20 shrink-0 items-center justify-center gap-2 rounded-xl px-4 text-sm font-black shadow-[0_8px_18px_rgba(78,91,220,0.2)] transition disabled:cursor-not-allowed disabled:opacity-45 ${
	                      isSending
	                        ? 'border border-red-200 bg-white text-red-600 hover:bg-red-50'
	                        : 'bg-[linear-gradient(135deg,#2672ff_0%,#884dff_100%)] text-white hover:brightness-105'
	                    }`}
	                  >
	                    <AppIcon name={isSending ? 'closeSm' : 'arrowRight'} className="h-4 w-4" />
	                    {isSending ? '停止' : '发送'}
	                  </button>
		                </div>
              </div>
              <input
                ref={textFileInputRef}
                type="file"
                accept={AI_CHAT_TEXT_FILE_ACCEPT}
                onChange={(event) => void handleImportTextFile(event)}
                className="hidden"
              />
            </div>
          </form>
        </div>
      </section>
	      {compressionOptionsOpen ? (
	        <div
	          className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/34 px-3 py-4 backdrop-blur-sm sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label="压缩上下文"
        >
          <div className="w-full max-w-[26rem] rounded-2xl border border-white/70 bg-white/96 p-4 shadow-[0_26px_70px_rgba(30,58,138,0.26)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <AppIcon name="package" className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-black text-[var(--glass-text-primary)]">压缩上下文</div>
                  <div className="text-[11px] font-bold text-[var(--glass-text-tertiary)]">
                    消息 {manualCompressionStats.totalMessages} 条 · {formatCompactNumber(manualCompressionStats.totalChars)} 字
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!isCompressing) setCompressionOptionsOpen(false)
                }}
                disabled={isCompressing}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700 disabled:opacity-40"
                aria-label="关闭压缩设置"
              >
                <AppIcon name="close" className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {([
                ['start', '最开始'],
                ['recent', '最近'],
                ['unlimited', '完整'],
              ] as const).map(([value, label]) => {
                const active = manualCompressionLimitMode === value
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isCompressing}
                    onClick={() => {
                      setManualCompressionLimitMode(value)
                      setCompressionOptionsError('')
                    }}
                    className={`h-9 rounded-xl border text-xs font-black transition disabled:opacity-50 ${
                      active
                        ? 'border-blue-600 bg-blue-600 text-white shadow-[0_8px_18px_rgba(38,114,255,0.18)]'
                        : 'border-blue-100 bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    {label}
                  </button>
                )
              })}
            </div>

            {manualCompressionLimitMode !== 'unlimited' ? (
              <label className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs font-black text-[var(--glass-text-primary)]">
                <span>{manualCompressionLimitMode === 'start' ? '开头字数' : '最近字数'}</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1000}
                  max={MAX_MANUAL_COMPRESS_CHAR_COUNT}
                  value={manualCompressionMaxChars}
                  disabled={isCompressing}
                  onChange={(event) => {
                    setManualCompressionMaxChars(event.target.value)
                    setCompressionOptionsError('')
                  }}
                  className="h-8 w-28 rounded-lg border border-blue-100 bg-white px-2 text-right text-sm font-black text-blue-800 outline-none focus:border-blue-400"
                />
              </label>
            ) : (
              <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/70 px-3 py-2 text-xs font-bold text-[var(--glass-text-secondary)]">
                完整发送 {formatCompactNumber(manualCompressionStats.totalChars)} 字上下文。
              </div>
            )}

            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[11px] font-bold text-[var(--glass-text-secondary)]">
              <span>压缩后：仅保留摘要</span>
            </div>

            {compressionOptionsError ? (
              <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-bold leading-5 text-red-600">
                {compressionOptionsError}
              </div>
            ) : null}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => setCompressionOptionsOpen(false)}
                disabled={isCompressing}
                className="h-10 flex-1 rounded-xl border border-blue-100 bg-white text-sm font-black text-blue-700 disabled:opacity-45"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleSubmitManualCompressionOptions}
                disabled={isCompressing || (manualCompressionLimitMode !== 'unlimited' && (!Number.isFinite(parsedManualCompressionMaxChars) || parsedManualCompressionMaxChars <= 0))}
                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-black text-white shadow-[0_10px_22px_rgba(38,114,255,0.18)] disabled:opacity-45"
              >
                {isCompressing ? <AppIcon name="loader" className="h-4 w-4 animate-spin" /> : <AppIcon name="package" className="h-4 w-4" />}
                {isCompressing ? '压缩中' : '开始压缩'}
              </button>
            </div>
          </div>
	        </div>
	      ) : null}
	      {regenerateConfirmMessage ? (
	        <div
	          className="fixed inset-0 z-[82] flex items-end justify-center bg-slate-950/34 px-3 py-4 backdrop-blur-sm sm:items-center"
	          role="dialog"
	          aria-modal="true"
	          aria-label="确认重新生成"
	        >
	          <div className="w-full max-w-[25rem] rounded-2xl border border-white/70 bg-white/96 p-4 shadow-[0_26px_70px_rgba(30,58,138,0.26)]">
	            <div className="flex items-start gap-3">
	              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
	                <AppIcon name="refresh" className="h-5 w-5" />
	              </span>
	              <div className="min-w-0">
	                <div className="text-sm font-black text-[var(--glass-text-primary)]">重新生成这条回复？</div>
	                <div className="mt-1 text-xs font-bold leading-5 text-[var(--glass-text-secondary)]">
	                  {regenerateDeleteTrailingMessages
	                    ? '会回到上一条用户消息重新生成，并删除这条回复之后的对话内容。'
	                    : '会基于同一段上下文生成新回复，并替换当前显示的回复版本。'}
	                </div>
	              </div>
	            </div>
	            <div className="mt-4 flex gap-2">
	              <button
	                type="button"
	                onClick={() => setRegenerateConfirmMessage(null)}
	                className="h-10 flex-1 rounded-xl border border-blue-100 bg-white text-sm font-black text-blue-700"
	              >
	                取消
	              </button>
	              <button
	                type="button"
	                onClick={() => void confirmRegenerateAssistantMessage()}
	                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-black text-white shadow-[0_10px_22px_rgba(38,114,255,0.18)]"
	                aria-label="确认重新生成"
	              >
	                <AppIcon name="refresh" className="h-4 w-4" />
	                重新生成
	              </button>
	            </div>
	          </div>
	        </div>
	      ) : null}
	      {editingMessageId ? (
	        <div
	          className="fixed inset-0 z-[82] flex items-end justify-center bg-slate-950/34 px-3 py-4 backdrop-blur-sm sm:items-center"
	          role="dialog"
	          aria-modal="true"
	          aria-label="编辑消息"
	        >
	          <div className="w-full max-w-[30rem] rounded-2xl border border-white/70 bg-white/96 p-4 shadow-[0_26px_70px_rgba(30,58,138,0.26)]">
	            <div className="mb-3 flex items-center justify-between gap-3">
	              <div className="flex min-w-0 items-center gap-2">
	                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
	                  <AppIcon name="edit" className="h-4 w-4" />
	                </span>
	                <div className="min-w-0">
	                  <div className="text-sm font-black text-[var(--glass-text-primary)]">编辑并重新发送</div>
	                  <div className="text-[11px] font-bold text-[var(--glass-text-tertiary)]">会删除这条用户消息之后的回复，再按新内容重新回答。</div>
	                </div>
	              </div>
	              <button
	                type="button"
	                onClick={handleCloseEditMessage}
	                disabled={isSending}
	                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700 disabled:opacity-40"
	                aria-label="关闭编辑消息"
	              >
	                <AppIcon name="close" className="h-4 w-4" />
	              </button>
	            </div>
	            <textarea
	              value={editingMessageText}
	              onChange={(event) => {
	                setEditingMessageText(event.target.value)
	                setEditingMessageError('')
	              }}
	              disabled={isSending}
	              rows={6}
	              className="w-full resize-none rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm leading-6 text-[var(--glass-text-primary)] outline-none focus:border-blue-400"
	            />
	            {editingMessageError ? (
	              <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-bold leading-5 text-red-600">
	                {editingMessageError}
	              </div>
	            ) : null}
	            <div className="mt-4 flex gap-2">
	              <button
	                type="button"
	                onClick={handleCloseEditMessage}
	                disabled={isSending}
	                className="h-10 flex-1 rounded-xl border border-blue-100 bg-white text-sm font-black text-blue-700 disabled:opacity-45"
	              >
	                取消
	              </button>
	              <button
	                type="button"
	                onClick={() => void handleSubmitEditedMessage()}
	                disabled={isSending || !editingMessageText.trim()}
	                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-black text-white shadow-[0_10px_22px_rgba(38,114,255,0.18)] disabled:opacity-45"
	              >
	                {isSending ? <AppIcon name="loader" className="h-4 w-4 animate-spin" /> : <AppIcon name="refresh" className="h-4 w-4" />}
	                {isSending ? '发送中' : '重新发送'}
	              </button>
	            </div>
	          </div>
	        </div>
	      ) : null}
	      {selectCopyMessage ? (
	        <div
	          className="fixed inset-0 z-[83] flex items-end justify-center bg-slate-950/34 px-3 py-4 backdrop-blur-sm sm:items-center"
	          role="dialog"
	          aria-modal="true"
	          aria-label="选择复制"
	        >
	          <div className="w-full max-w-[34rem] rounded-2xl border border-white/70 bg-white/96 p-4 shadow-[0_26px_70px_rgba(30,58,138,0.26)]">
	            <div className="mb-3 flex items-center justify-between gap-3">
	              <div className="flex min-w-0 items-center gap-2">
	                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
	                  <AppIcon name="copy" className="h-4 w-4" />
	                </span>
	                <div className="min-w-0">
	                  <div className="text-sm font-black text-[var(--glass-text-primary)]">选择复制</div>
	                  <div className="text-[11px] font-bold text-[var(--glass-text-tertiary)]">保留原文，可手动选中其中一段。</div>
	                </div>
	              </div>
	              <button
	                type="button"
	                onClick={() => setSelectCopyMessage(null)}
	                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700"
	                aria-label="关闭选择复制"
	              >
	                <AppIcon name="close" className="h-4 w-4" />
	              </button>
	            </div>
	            <textarea
	              readOnly
	              value={stripSkillContextFromMessageContent(selectCopyMessage.content)}
	              rows={10}
	              className="w-full resize-none rounded-xl border border-blue-100 bg-white px-3 py-2.5 text-sm leading-6 text-[var(--glass-text-primary)] outline-none focus:border-blue-400"
	              onFocus={(event) => event.currentTarget.select()}
	            />
	            <div className="mt-4 flex gap-2">
	              <button
	                type="button"
	                onClick={() => setSelectCopyMessage(null)}
	                className="h-10 flex-1 rounded-xl border border-blue-100 bg-white text-sm font-black text-blue-700"
	              >
	                关闭
	              </button>
	              <button
	                type="button"
	                onClick={() => void handleCopyMessage(selectCopyMessage)}
	                className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 text-sm font-black text-white shadow-[0_10px_22px_rgba(38,114,255,0.18)]"
	              >
	                <AppIcon name="copy" className="h-4 w-4" />
	                复制全文
	              </button>
	            </div>
	          </div>
	        </div>
	      ) : null}
	      {previewMessage ? (
	        <div
	          className="fixed inset-0 z-[83] flex items-end justify-center bg-slate-950/34 px-3 py-4 backdrop-blur-sm sm:items-center"
	          role="dialog"
	          aria-modal="true"
	          aria-label="网页视图渲染"
	        >
	          <div className="flex max-h-[86vh] w-full max-w-[44rem] flex-col rounded-2xl border border-white/70 bg-white/96 p-4 shadow-[0_26px_70px_rgba(30,58,138,0.26)]">
	            <div className="mb-3 flex items-center justify-between gap-3">
	              <div className="flex min-w-0 items-center gap-2">
	                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
	                  <AppIcon name="bookOpen" className="h-4 w-4" />
	                </span>
	                <div className="min-w-0">
	                  <div className="text-sm font-black text-[var(--glass-text-primary)]">网页视图渲染</div>
	                  <div className="text-[11px] font-bold text-[var(--glass-text-tertiary)]">按当前 Markdown 渲染消息内容。</div>
	                </div>
	              </div>
	              <button
	                type="button"
	                onClick={() => setPreviewMessage(null)}
	                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-blue-700"
	                aria-label="关闭网页视图渲染"
	              >
	                <AppIcon name="close" className="h-4 w-4" />
	              </button>
	            </div>
	            <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto rounded-xl border border-blue-100 bg-white px-4 py-3">
	              <MarkdownMessage content={stripSkillContextFromMessageContent(previewMessage.content)} />
	            </div>
	          </div>
	        </div>
	      ) : null}
		      {previewImageUrl ? (
		        <ImagePreviewModal
		          imageUrl={previewImageUrl}
		          onClose={() => {
		            setPreviewImageUrl(null)
		            setPreviewImageQuickVideoUrl(null)
		          }}
		          variant="compact"
		          action={previewImageQuickVideoUrl ? {
		            label: '生成视频',
		            icon: <AppIcon name="video" className="h-4 w-4" />,
		            onClick: () => handleImageTextResultForQuickVideo(previewImageQuickVideoUrl),
		          } : undefined}
		        />
		      ) : null}
	    </main>
	  )
	}
